import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  addDoc, 
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  where,
  onSnapshot,
  writeBatch, // Added import
  increment
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { buildClarifySkipToken, isClarifySkipToken } from '@/lib/chat/clarify';
import type { ClarifyRequest, ClarificationSubmissionMeta, ClarifyType } from '@/lib/chat/clarify';
import type { ReviewerFeedback } from '@/types/reviewer';
import type { ClientPreferences } from '@/types/preferences';
import { resolveRuntimeRouting } from '@/lib/graph/runtimeRouting';
import type { RuntimeToolRunAgUiState, RuntimeToolRunResultPayload } from '@/lib/runtimeTools/toolRuns';
import { extractPreferenceUpdates, normalizeClientPreferences, saveUserClientExplicitPreferences } from '@/lib/clientPreferences';
import { trackPreferenceMetric } from '@/lib/preferencesAnalytics';
import { toast } from '@/lib/notifications';
import { createPreferenceEvent, syncPreferenceEventToGraph } from '@/lib/learningLedger';
import { canLogLegacyLearning, canMutateLegacyLearning, getLegacyRealtimeLearningMode } from '@/lib/legacyRealtimeLearning';
import { sanitizeArtifactTitle } from '@/lib/utils';
import { runGraphSSE } from '@/lib/graph/streamSSE';
import { useAuth } from '../context/AuthContext';
import { useAI } from './AIContext';
import { useClient } from './ClientContext';
import { Client, InteractionFeedback, SessionLearningContext, LearningSuggestionState, MemoryReference, PolicyIssue, RunMeta } from '../types';
import { realTimeLearningService } from '../lib/aiAgents/realTimeLearning';
import { useClientManagement } from '../hooks/useClientManagement';

type MessageAttachmentArtifactContext = {
  id: string;
  title: string;
  type: string;
  content?: string;
  artifactKey?: string;
  lineageId?: string;
  runId?: string;
  checkpointId?: string;
};

type MessageAttachmentDocumentContext = {
  id: string;
  title: string;
  type?: string;
  url?: string;
};

type SendMessageContextAttachments = {
  artifact?: MessageAttachmentArtifactContext;
  artifactStrict?: boolean;
  sourceDocumentIds?: string[];
  sourceDocuments?: MessageAttachmentDocumentContext[];
};

type ArtifactFeedbackAction = 'approve' | 'request_changes' | 'discuss' | 'open' | 'free_text';

type ArtifactFeedbackMeta = {
  artifactId: string;
  lineageId?: string;
  runId?: string;
  promptMessageId?: string;
  action?: ArtifactFeedbackAction;
};

interface Message {
  id?: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: any;
  isLoading?: boolean;
  isEdited?: boolean;
  originalContent?: string;
  editedAt?: any;
  messageKind?: 'message' | 'clarification' | 'clarification_skip' | 'ai_response' | 'artifact_feedback_prompt' | 'artifact_feedback_response';
  clarification?: ClarificationSubmissionMeta;
  // Artifact reference fields (backward compatible)
  isArtifactReference?: boolean;
  artifactReference?: {
    artifactId: string;
    artifactType: 'press_release' | 'social_media' | 'email' | 'statement' | 'content' | 'media_pitch';
    artifactTitle: string;
    createdAt: Date;
  };
  // Agent handoff animation data
  agentHandoffData?: {
    selectedAgent: {
      id: string;
      name: string;
      type: 'crisis' | 'media' | 'social' | 'executive' | 'content' | 'general';
      description: string;
      expertise: string[];
    } | null;
    phase: 'selecting' | 'handoff' | 'processing' | 'complete';
    processingSteps: string[];
    statusText?: string;
    lastHandoff?: {
      fromAgentId: string;
      fromAgentName: string;
      toAgentId: string;
      toAgentName: string;
      timestamp: number;
    };
  };
  agentMetadata?: {
    agentId: string;
    agentType: string;
    confidence: number;
    requiresApproval: boolean;
    tokensUsed: number;
    responseTime: number;
    suggestedActions?: string[];
    executedActions?: Array<{
      success: boolean;
      message: string;
      data?: any;
      error?: string;
    }>;
    pendingActions?: Array<{
      type: string;
      description: string;
      parameters: Record<string, any>;
    }>;
    realTimeLearning?: {
      adaptedFromFeedback: boolean;
      sessionCorrections: number;
      confidenceBoost: number;
      lastAdaptation: string;
    };
  };
  // PHASE-02 START
  meta?: {
    memories?: MemoryReference[];
    provenance?: RunMeta | null;
    // PHASE-03 START
    policyIssues?: PolicyIssue[];
    // PHASE-03 END
    clarificationAck?: ClarificationSubmissionMeta;
    contextAttachments?: SendMessageContextAttachments;
    runId?: string;
    pendingStatus?: PendingRunStatus | string;
    artifactFeedback?: ArtifactFeedbackMeta;
    runtimeTool?: {
      state: RuntimeToolRunAgUiState | null;
      error?: string | null;
    };
  };
  // PHASE-02 END
}

type StoredClarificationEntry = {
  text: string;
  placeholderCount: number;
  updatedAt: number;
  skipped?: boolean;
};

const CLARIFY_TYPES: ClarifyType[] = ['generic', 'press', 'pitch', 'social', 'research', 'quality_review'];

const DEFAULT_CLARIFY_QUESTIONS: Record<ClarifyType, string[]> = {
  generic: [
    'What should the final output include?',
    'Who is the intended audience?',
    'Are there any required facts, sources, or constraints?',
  ],
  press: [
    'Exact product/feature and launch date?',
    'Spokesperson name, title, and a 1-2 sentence quote?',
    'Dateline city and date (e.g., City, ST — Month DD, YYYY)?',
    '2-3 key stats or benefits to highlight?',
    'Media contact name, email, and phone number?',
  ],
  pitch: [
    'Target outlet(s) or audience?',
    'Key angle or hook to emphasize?',
    'Spokesperson/interview availability and topic?',
    'Any embargo or timing constraints?',
    'Primary CTA (interview/demo/exclusive)?',
  ],
  social: [
    'Which platform(s) should this be written for?',
    'What is the main message or call to action?',
    'Any required hashtags, handles, or links?',
  ],
  research: [
    'What topic or question should I research?',
    'Which sources, markets, or time period should I prioritize?',
    'What format should the findings use?',
  ],
  quality_review: [
    'Which issue should I apply or ignore?',
    'Any specific revision instructions?',
  ],
};

const normalizeClarifyTypeValue = (raw: unknown): ClarifyType | undefined => {
  if (
    raw === 'generic'
    || raw === 'press'
    || raw === 'pitch'
    || raw === 'social'
    || raw === 'research'
    || raw === 'quality_review'
  ) {
    return raw;
  }
  return undefined;
};

type ClarifyStreamPayload = {
  requestId?: string;
  type: ClarifyType;
  questions: string[];
  agentLabel?: string;
  originRunId?: string;
};

const normalizeClarifyQuestions = (raw: unknown): string[] => (
  Array.isArray(raw)
    ? raw
        .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
        .filter(Boolean)
    : []
);

const getDefaultClarifyQuestions = (type: ClarifyType | undefined): string[] => (
  DEFAULT_CLARIFY_QUESTIONS[type || 'generic'] || DEFAULT_CLARIFY_QUESTIONS.generic
);

const extractClarifyFromInterruptEntries = (
  raw: unknown,
  fallbackOriginRunId?: string,
): ClarifyStreamPayload | null => {
  if (!Array.isArray(raw)) return null;
  for (const entry of raw) {
    const envelope = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : null;
    const candidate = envelope && envelope.value && typeof envelope.value === 'object'
      ? (envelope.value as Record<string, unknown>)
      : envelope;
    if (!candidate) continue;
    const node = typeof candidate.node === 'string' ? candidate.node.trim().toLowerCase() : '';
    const questions = normalizeClarifyQuestions(candidate.questions);
    if (node !== 'clarify' || !questions.length) continue;
    return {
      requestId: typeof candidate.requestId === 'string' && candidate.requestId.trim() ? candidate.requestId.trim() : undefined,
      type: normalizeClarifyTypeValue(candidate.type) || 'press',
      questions,
      agentLabel: typeof candidate.agentLabel === 'string' && candidate.agentLabel.trim() ? candidate.agentLabel.trim() : undefined,
      originRunId: typeof candidate.originRunId === 'string' && candidate.originRunId.trim()
        ? candidate.originRunId.trim()
        : fallbackOriginRunId,
    };
  }
  return null;
};

const extractClarifyFromStreamPayload = (
  raw: unknown,
  fallbackOriginRunId?: string,
): ClarifyStreamPayload | null => {
  if (!raw || typeof raw !== 'object') return null;
  const payload = raw as Record<string, unknown>;
  const node = typeof payload.node === 'string' ? payload.node.trim().toLowerCase() : '';
  if (node === 'clarify') {
    const questions = normalizeClarifyQuestions(payload.questions);
    if (!questions.length) return null;
    return {
      requestId: typeof payload.requestId === 'string' && payload.requestId.trim() ? payload.requestId.trim() : undefined,
      type: normalizeClarifyTypeValue(payload.type) || 'press',
      questions,
      agentLabel: typeof payload.agentLabel === 'string' && payload.agentLabel.trim() ? payload.agentLabel.trim() : undefined,
      originRunId: typeof payload.originRunId === 'string' && payload.originRunId.trim()
        ? payload.originRunId.trim()
        : fallbackOriginRunId,
    };
  }
  if (node === '__interrupt__') {
    return extractClarifyFromInterruptEntries(payload.value, fallbackOriginRunId);
  }
  return null;
};

const asRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
);

const extractClarifyFromState = (
  rawState: unknown,
  fallbackOriginRunId?: string,
): ClarifyStreamPayload | null => {
  const state = asRecord(rawState);
  const work = asRecord(state.work);
  const clarify = asRecord(work.clarify);
  if (clarify.needed !== true) return null;
  const type = normalizeClarifyTypeValue(clarify.type) || 'press';
  const pendingQuestions = normalizeClarifyQuestions(clarify.questions);
  const questions = pendingQuestions.length ? pendingQuestions : getDefaultClarifyQuestions(type);
  if (!questions.length) return null;
  return {
    requestId: normalizeString(clarify.requestId) || undefined,
    type,
    questions,
    agentLabel: normalizeString(clarify.agentLabel) || undefined,
    originRunId: normalizeString(clarify.originRunId) || fallbackOriginRunId,
  };
};

const normalizeString = (value: unknown): string => (
  typeof value === 'string' ? value.trim() : ''
);

const normalizeRuntimeToolArtifactType = (value: unknown): RuntimeToolRunAgUiState['artifactType'] => {
  const normalized = normalizeString(value).toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('press')) return 'press_release';
  if (normalized.includes('pitch')) return 'media_pitch';
  if (normalized.includes('social')) return 'social_media';
  return 'content';
};

const sanitizeRuntimeToolResultPayload = (raw: unknown): RuntimeToolRunResultPayload | null => {
  const payload = asRecord(raw);
  const next: RuntimeToolRunResultPayload = {
    ...(normalizeString(payload.content) ? { content: normalizeString(payload.content) } : {}),
    ...(normalizeString(payload.title) ? { title: normalizeString(payload.title) } : {}),
    ...(normalizeString(payload.summary) ? { summary: normalizeString(payload.summary) } : {}),
    ...(normalizeString(payload.route) ? { route: normalizeString(payload.route) } : {}),
    ...(normalizeString(payload.task) ? { task: normalizeString(payload.task) } : {}),
    ...(normalizeString(payload.resultKind) ? { resultKind: normalizeString(payload.resultKind) } : {}),
    ...(normalizeString(payload.sourceArtifactId) ? { sourceArtifactId: normalizeString(payload.sourceArtifactId) } : {}),
    ...(normalizeString(payload.parentArtifactId) ? { parentArtifactId: normalizeString(payload.parentArtifactId) } : {}),
    ...(normalizeString(payload.sourceArtifactType) ? { sourceArtifactType: normalizeString(payload.sourceArtifactType) } : {}),
    ...(normalizeString(payload.outputArtifactType) ? { outputArtifactType: normalizeString(payload.outputArtifactType) } : {}),
    ...(normalizeString(payload.targetChannel) ? { targetChannel: normalizeString(payload.targetChannel) } : {}),
    ...(normalizeString(payload.targetLanguage) ? { targetLanguage: normalizeString(payload.targetLanguage) } : {}),
    ...(normalizeString(payload.checkpointId) ? { checkpointId: normalizeString(payload.checkpointId) } : {}),
    ...(normalizeString(payload.runId) ? { runId: normalizeString(payload.runId) } : {}),
    ...(normalizeString(payload.artifactId) ? { artifactId: normalizeString(payload.artifactId) } : {}),
    ...(normalizeString(payload.artifactKey) ? { artifactKey: normalizeString(payload.artifactKey) } : {}),
    ...(normalizeString(payload.lineageId) ? { lineageId: normalizeString(payload.lineageId) } : {}),
    ...(normalizeString(payload.messageId) ? { messageId: normalizeString(payload.messageId) } : {}),
  };
  return Object.keys(next).length ? next : null;
};

const buildRuntimeToolMessageMeta = (finalState: unknown): { state: RuntimeToolRunAgUiState; error?: string | null } | null => {
  const state = asRecord(finalState);
  const meta = asRecord(state.meta);
  const execution = asRecord(meta.runtimeToolExecution);
  const work = asRecord(state.work);
  const runtimeToolResult = asRecord(work.runtimeToolResult);
  const payload = sanitizeRuntimeToolResultPayload(runtimeToolResult.payload);
  const rawPayload = asRecord(runtimeToolResult.payload);
  const rawState = asRecord(rawPayload.rawState);
  const rawMeta = asRecord(rawState.meta);
  const rawRuntimeTool = asRecord(rawMeta.runtimeTool);
  const toolId = normalizeString(execution.toolId);
  if (!toolId) return null;

  const requestId = normalizeString(execution.requestId) || normalizeString(state.runId) || 'runtime-tool';
  const createdArtifactIds = Array.isArray(execution.createdArtifactIds)
    ? execution.createdArtifactIds.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : Array.isArray(runtimeToolResult.createdArtifactIds)
      ? runtimeToolResult.createdArtifactIds.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      : [];

  const runtimeToolState: RuntimeToolRunAgUiState = {
    requestId,
    runId: requestId,
    toolId: toolId as RuntimeToolRunAgUiState['toolId'],
    surface: 'chat',
    stage: (normalizeString(execution.stage) || normalizeString(execution.status) || 'completed') as RuntimeToolRunAgUiState['stage'],
    status: (normalizeString(execution.status) || 'completed') as RuntimeToolRunAgUiState['status'],
    summary:
      normalizeString(execution.summary)
      || normalizeString(rawPayload.summary)
      || normalizeString(runtimeToolResult.summary),
    label: normalizeString(execution.label) || toolId,
    clientId: normalizeString(state.clientId),
    artifactType:
      normalizeRuntimeToolArtifactType(rawRuntimeTool.outputArtifactType)
      || normalizeRuntimeToolArtifactType(rawRuntimeTool.artifactType)
      || normalizeRuntimeToolArtifactType(rawPayload.outputArtifactType)
      || normalizeRuntimeToolArtifactType(execution.artifactType)
      || null,
    sourceArtifactType:
      normalizeRuntimeToolArtifactType(rawRuntimeTool.sourceArtifactType)
      || normalizeRuntimeToolArtifactType(rawPayload.sourceArtifactType)
      || null,
    outputArtifactType:
      normalizeRuntimeToolArtifactType(rawRuntimeTool.outputArtifactType)
      || normalizeRuntimeToolArtifactType(rawPayload.outputArtifactType)
      || null,
    targetChannel: normalizeString(rawRuntimeTool.targetChannel) || normalizeString(rawPayload.targetChannel) || null,
    targetLanguage: normalizeString(rawRuntimeTool.targetLanguage) || normalizeString(rawPayload.targetLanguage) || null,
    artifactId: normalizeString(execution.artifactId) || normalizeString(rawPayload.artifactId) || null,
    sourceArtifactId:
      normalizeString(rawRuntimeTool.sourceArtifactId)
      || normalizeString(rawRuntimeTool.artifactId)
      || normalizeString(rawPayload.sourceArtifactId)
      || null,
    parentArtifactId:
      normalizeString(rawRuntimeTool.parentArtifactId)
      || normalizeString(rawPayload.parentArtifactId)
      || null,
    artifactKey: normalizeString(rawPayload.artifactKey) || normalizeString(rawRuntimeTool.artifactKey) || null,
    lineageId: normalizeString(rawPayload.lineageId) || normalizeString(rawRuntimeTool.lineageId) || null,
    checkpointId: normalizeString(rawPayload.checkpointId) || requestId || null,
    sourceConversationId: normalizeString(rawRuntimeTool.sourceConversationId) || normalizeString(state.threadId) || null,
    resultPayload: payload,
    createdArtifactIds,
    error: normalizeString(execution.error) || null,
  };

  return {
    state: runtimeToolState,
    ...(runtimeToolState.error ? { error: runtimeToolState.error } : {}),
  };
};

interface Conversation {
  id: string;
  title: string;
  lastMessage?: string;
  clientId?: string;  // Added clientId to track which client this conversation is about
  clientContext?: string;  // Added to store learned context about the client
  // PHASE-03 START
  language?: 'en' | 'ar';
  // PHASE-03 END
  clarifications?: Partial<Record<ClarifyType, StoredClarificationEntry>>;
  updatedAt: any;
  createdAt: any;
}

type SendMessageKind = 'message' | 'clarification' | 'clarification_skip' | 'artifact_feedback_response';

type SendMessageOptions = {
  kind?: SendMessageKind;
  clarification?: ClarificationSubmissionMeta;
  artifactFeedback?: ArtifactFeedbackMeta;
  saveAsPreference?: boolean;
  previousContent?: string;
  contextAttachments?: SendMessageContextAttachments;
  skipAgentRun?: boolean;
};

type PendingRunStatus = 'queued' | 'in_progress' | 'completed' | 'needs_clarification' | 'error' | 'expired';

type PendingRunAgent = {
  id: string;
  name: string;
  type: 'crisis' | 'media' | 'social' | 'executive' | 'content' | 'general';
  description?: string;
};

type PendingRunHandoff = {
  phase?: 'selecting' | 'handoff' | 'processing' | 'complete';
  step?: string;
  statusText?: string;
  status_text?: string;
  message?: string;
  detail?: string;
  node?: string;
  agentId?: string;
  agentName?: string;
  agentType?: string;
  agentDescription?: string;
  agent?: PendingRunAgent;
};

type PendingRunClarify = {
  requestId?: string;
  type?: string;
  questions?: string[];
  agentLabel?: string;
  originRunId?: string;
};

type PendingRunDoc = {
  runId?: string;
  status?: PendingRunStatus | string;
  kind?: string;
  type?: string;
  partial?: string;
  error?: string;
  statusText?: string;
  status_text?: string;
  step?: string;
  node?: string;
  agentId?: string;
  agentName?: string;
  agentType?: string;
  agentDescription?: string;
  handoff?: PendingRunHandoff;
  clarify?: PendingRunClarify;
  timeoutReason?: string;
  lastActivityAt?: any;
  finalMessageId?: string;
  clarificationResolvedByRunId?: string;
  createdAt?: any;
  updatedAt?: any;
};

interface ChatContextProps {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  isCleaningUp: boolean;
  setIsCleaningUp: (cleaning: boolean) => void;
  sendMessage: (content: string, specificConversationId?: string, options?: SendMessageOptions) => Promise<void>;
  editMessage: (messageId: string, newContent: string) => Promise<void>;
  createNewConversation: (title?: string, clientId?: string) => Promise<string>;
  loadConversation: (conversationId: string) => Promise<void>;
  renameConversation: (conversationId: string, newTitle: string) => Promise<void>;
  setClientForConversation: (conversationId: string, clientId: string) => Promise<void>;
  // PHASE-03 START
  setConversationLanguage: (conversationId: string, language: 'en' | 'ar') => Promise<void>;
  // PHASE-03 END
  getCurrentClientId: () => string | undefined;
  clearCurrentChat: () => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  handleMessageFeedback: (messageId: string, feedback: InteractionFeedback) => Promise<void>;
  getSessionLearningStats: () => any;
  // Artifact reference functions
  createArtifactReferenceMessage: (artifactData: any) => Message;
  replaceMessage: (messageId: string, newMessage: Message) => Promise<void>;
  appendAssistantMessage: (payload: {
    content: string;
    messageKind?: Message['messageKind'];
    meta?: Message['meta'];
  }) => Promise<string | null>;
  // In-Chat Learning functions
  learningSuggestion: LearningSuggestionState | null;
  handleConfirmLearning: () => Promise<void>;
  handleDismissLearning: () => void;
  clarifyPending: ClarifyRequest | null;
  clarifyQueue: ClarifyRequest[];
  storedClarifications: Record<string, Partial<Record<ClarifyType, StoredClarificationEntry>>>;
  persistClarificationEntry: (
    conversationId: string,
    type: ClarifyType,
    entry: { text: string; placeholderCount: number; skipped?: boolean; language?: 'en' | 'ar' }
  ) => Promise<void>;
  clearClarifications: (conversationId: string, scope?: 'all' | ClarifyType) => Promise<void>;
  reviewerFeedback: Record<string, ReviewerFeedback>;
  clientPreferences: ClientPreferences | null;
  preferencesLoading: boolean;
  updateClientPreferences: (
    clientId: string,
    updates: Partial<ClientPreferences>,
    options?: { source?: string }
  ) => Promise<ClientPreferences | null>;
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined);

// PHASE-02 START
const sanitizeMemories = (raw: any): MemoryReference[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      if (typeof item === 'string' && item.trim().length > 0) {
        return { id: item.trim() } as MemoryReference;
      }
      if (!item || typeof item !== 'object') return null;
      const idSource = typeof item.id === 'string' && item.id.trim().length > 0
        ? item.id
        : typeof item.path === 'string' && item.path.trim().length > 0
          ? item.path
        : `memory-${index}`;
      const path = typeof item.path === 'string' && item.path.trim().length > 0 ? item.path : undefined;
      const title = typeof item.title === 'string' && item.title.trim().length > 0 ? item.title : undefined;
      const text = typeof item.text === 'string' && item.text.trim().length > 0 ? item.text : undefined;
      const url = typeof item.url === 'string' && item.url.trim().length > 0 ? item.url : undefined;
      if (!path && !title && !text && !url) {
        return { id: idSource } as MemoryReference;
      }
      return {
        id: idSource,
        ...(path ? { path } : {}),
        ...(title ? { title } : {}),
        ...(text ? { text } : {}),
        ...(url ? { url } : {}),
      } as MemoryReference;
    })
    .filter(Boolean) as MemoryReference[];
};

const sanitizeRunMeta = (meta: any): RunMeta | null => {
  if (!meta || typeof meta !== 'object') return null;
  const sanitized: RunMeta = {};
  if (normalizeString(meta.routeLabel)) sanitized.routeLabel = normalizeString(meta.routeLabel);
  if (normalizeString(meta.fineRoute)) sanitized.fineRoute = normalizeString(meta.fineRoute);
  if (normalizeString(meta.contentMode)) sanitized.contentMode = normalizeString(meta.contentMode);
  if (normalizeString(meta.routePlanReason)) sanitized.routePlanReason = normalizeString(meta.routePlanReason);
  if (meta.artifactTransform && typeof meta.artifactTransform === 'object') {
    const transform = meta.artifactTransform as Record<string, unknown>;
    const transformPayload: NonNullable<RunMeta['artifactTransform']> = {};
    if (normalizeString(transform.kind)) transformPayload.kind = normalizeString(transform.kind);
    if (normalizeString(transform.sourceArtifactType)) transformPayload.sourceArtifactType = normalizeString(transform.sourceArtifactType);
    if (normalizeString(transform.outputArtifactType)) transformPayload.outputArtifactType = normalizeString(transform.outputArtifactType);
    if (normalizeString(transform.targetChannel)) transformPayload.targetChannel = normalizeString(transform.targetChannel);
    if (normalizeString(transform.targetLanguage)) transformPayload.targetLanguage = normalizeString(transform.targetLanguage);
    if (Object.keys(transformPayload).length > 0) sanitized.artifactTransform = transformPayload;
  }
  if (meta.routing && typeof meta.routing === 'object') {
    const routing = meta.routing as Record<string, unknown>;
    const routingPayload: NonNullable<RunMeta['routing']> = {};
    const intentKind = normalizeString(routing.intentKind);
    if (
      intentKind === 'feedback_ack' ||
      intentKind === 'revision_request' ||
      intentKind === 'draft_request' ||
      intentKind === 'qa_request' ||
      intentKind === 'chitchat' ||
      intentKind === 'unknown'
    ) {
      routingPayload.intentKind = intentKind;
    }
    const routeDecisionSource = normalizeString(routing.routeDecisionSource);
    if (
      routeDecisionSource === 'deterministic' ||
      routeDecisionSource === 'deepseek' ||
      routeDecisionSource === 'gemini_fallback'
    ) {
      routingPayload.routeDecisionSource = routeDecisionSource;
    }
    if (typeof routing.llmConfidence === 'number') routingPayload.llmConfidence = routing.llmConfidence;
    if (typeof routing.needsFollowUp === 'boolean') routingPayload.needsFollowUp = routing.needsFollowUp;
    if (typeof routing.askedFollowUp === 'boolean') routingPayload.askedFollowUp = routing.askedFollowUp;
    if (normalizeString(routing.followUpQuestion)) routingPayload.followUpQuestion = normalizeString(routing.followUpQuestion);
    if (typeof routing.ambiguous === 'boolean') routingPayload.ambiguous = routing.ambiguous;
    if (normalizeString(routing.reason)) routingPayload.reason = normalizeString(routing.reason);
    if (Object.keys(routingPayload).length > 0) sanitized.routing = routingPayload;
  }
  const memories = sanitizeMemories((meta as any).memoriesUsed);
  if (memories.length > 0) sanitized.memoriesUsed = memories;
  if (meta.models && typeof meta.models === 'object') {
    sanitized.models = { ...meta.models };
  }
  if (meta.timings && typeof meta.timings === 'object') {
    sanitized.timings = { ...meta.timings };
  }
  if (meta.effectivePreferences && typeof meta.effectivePreferences === 'object') {
    sanitized.effectivePreferences = { ...(meta.effectivePreferences as Record<string, unknown>) };
  }
  if (meta.preferenceProvenance && typeof meta.preferenceProvenance === 'object') {
    sanitized.preferenceProvenance = { ...(meta.preferenceProvenance as Record<string, any>) };
  }
  if (meta.rag && typeof meta.rag === 'object') {
    const rag = meta.rag as Record<string, any>;
    const ragPayload: NonNullable<RunMeta['rag']> = {};
    if (rag.sufficiency && typeof rag.sufficiency === 'object') {
      const sufficiency = rag.sufficiency as Record<string, any>;
      ragPayload.sufficiency = {
        sufficient: !!sufficiency.sufficient,
        cautiousMode: typeof sufficiency.cautiousMode === 'boolean' ? sufficiency.cautiousMode : undefined,
        reason: typeof sufficiency.reason === 'string' ? sufficiency.reason : undefined,
        metrics: sufficiency.metrics && typeof sufficiency.metrics === 'object'
          ? {
              uniqueDocs: typeof sufficiency.metrics.uniqueDocs === 'number' ? sufficiency.metrics.uniqueDocs : undefined,
              chunkCount: typeof sufficiency.metrics.chunkCount === 'number' ? sufficiency.metrics.chunkCount : undefined,
              queryTermCoverage: typeof sufficiency.metrics.queryTermCoverage === 'number' ? sufficiency.metrics.queryTermCoverage : undefined,
            }
          : undefined,
      };
    }
    if (rag.retrievalQuality && typeof rag.retrievalQuality === 'object') {
      const retrievalQuality = rag.retrievalQuality as Record<string, any>;
      ragPayload.retrievalQuality = {
        retrievalMode: retrievalQuality.retrievalMode === 'dense' || retrievalQuality.retrievalMode === 'hybrid'
          ? retrievalQuality.retrievalMode
          : undefined,
        denseCandidates: typeof retrievalQuality.denseCandidates === 'number' ? retrievalQuality.denseCandidates : undefined,
        lexicalCandidates: typeof retrievalQuality.lexicalCandidates === 'number' ? retrievalQuality.lexicalCandidates : undefined,
        fusedCandidates: typeof retrievalQuality.fusedCandidates === 'number' ? retrievalQuality.fusedCandidates : undefined,
        enrichment: retrievalQuality.enrichment && typeof retrievalQuality.enrichment === 'object'
          ? {
              enabled: typeof retrievalQuality.enrichment.enabled === 'boolean'
                ? retrievalQuality.enrichment.enabled
                : undefined,
              added: typeof retrievalQuality.enrichment.added === 'number'
                ? retrievalQuality.enrichment.added
                : undefined,
              expansionFactor: typeof retrievalQuality.enrichment.expansionFactor === 'number'
                ? retrievalQuality.enrichment.expansionFactor
                : undefined,
            }
          : undefined,
        reranker: retrievalQuality.reranker && typeof retrievalQuality.reranker === 'object'
          ? {
              applied: typeof retrievalQuality.reranker.applied === 'boolean'
                ? retrievalQuality.reranker.applied
                : undefined,
              method:
                retrievalQuality.reranker.method === 'jina' ||
                retrievalQuality.reranker.method === 'fallback' ||
                retrievalQuality.reranker.method === 'none'
                  ? retrievalQuality.reranker.method
                  : undefined,
              timedOut: typeof retrievalQuality.reranker.timedOut === 'boolean'
                ? retrievalQuality.reranker.timedOut
                : undefined,
              fallbackUsed: typeof retrievalQuality.reranker.fallbackUsed === 'boolean'
                ? retrievalQuality.reranker.fallbackUsed
                : undefined,
              latencyMs: typeof retrievalQuality.reranker.latencyMs === 'number'
                ? retrievalQuality.reranker.latencyMs
                : undefined,
              rerankerWin: typeof retrievalQuality.reranker.rerankerWin === 'boolean'
                ? retrievalQuality.reranker.rerankerWin
                : undefined,
            }
          : undefined,
      };
    }
    if (Object.keys(ragPayload).length > 0) {
      sanitized.rag = ragPayload;
    }
  }
  if (Array.isArray(meta.citationsV2)) {
    sanitized.citationsV2 = (meta.citationsV2 as Array<any>)
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null;
        const url = typeof entry.url === 'string' ? entry.url : undefined;
        const docId = typeof entry.docId === 'string' ? entry.docId : undefined;
        const title = typeof entry.title === 'string' ? entry.title : undefined;
        const claim = typeof entry.claim === 'string' ? entry.claim : undefined;
        const grounded = typeof entry.grounded === 'boolean' ? entry.grounded : undefined;
        const confidence = typeof entry.confidence === 'number' ? entry.confidence : undefined;
        const snippet = typeof entry.snippet === 'string' ? entry.snippet : undefined;
        const evidenceSnippet = typeof entry.evidenceSnippet === 'string' ? entry.evidenceSnippet : undefined;
        return {
          ...(url ? { url } : {}),
          ...(docId ? { docId } : {}),
          ...(title ? { title } : {}),
          ...(claim ? { claim } : {}),
          ...(grounded !== undefined ? { grounded } : {}),
          ...(confidence !== undefined ? { confidence } : {}),
          ...(snippet ? { snippet } : {}),
          ...(evidenceSnippet ? { evidenceSnippet } : {}),
        };
      })
      .filter(Boolean) as RunMeta['citationsV2'];
  }
  if (Array.isArray((meta as any).policyIssues)) {
    const policyIssues = (meta as any).policyIssues
      .map((issue: any) => {
        if (!issue || typeof issue !== 'object') return null;
        const code = typeof issue.code === 'string' ? issue.code : 'POLICY';
        const message = typeof issue.message === 'string' ? issue.message : '';
        const severity = issue.severity === 'block' || issue.severity === 'warn' || issue.severity === 'info'
          ? issue.severity
          : undefined;
        const category = typeof issue.category === 'string' ? issue.category : undefined;
        const phrase = typeof issue.phrase === 'string' ? issue.phrase : undefined;
        const fixHint = typeof issue.fixHint === 'string' ? issue.fixHint : undefined;
        const claim = typeof issue.claim === 'string' ? issue.claim : undefined;
        const confidence = Number.isFinite(Number(issue.confidence)) ? Number(issue.confidence) : undefined;
        const threshold = Number.isFinite(Number(issue.threshold)) ? Number(issue.threshold) : undefined;
        const status = issue.status === 'open' || issue.status === 'resolved' || issue.status === 'dismissed'
          ? issue.status
          : undefined;
        const actions = Array.isArray(issue.actions)
          ? issue.actions.filter((entry: unknown) => (
              entry === 'dismiss' ||
              entry === 'autofix' ||
              entry === 'add_source' ||
              entry === 'strengthen_pillars'
            ))
          : undefined;
        const id = typeof issue.id === 'string' && issue.id.trim().length > 0 ? issue.id.trim() : undefined;
        return message
          ? {
              ...(id ? { id } : {}),
              code,
              ...(category ? { category } : {}),
              message,
              ...(severity ? { severity } : {}),
              ...(phrase ? { phrase } : {}),
              ...(fixHint ? { fixHint } : {}),
              ...(claim ? { claim } : {}),
              ...(confidence !== undefined ? { confidence } : {}),
              ...(threshold !== undefined ? { threshold } : {}),
              ...(status ? { status } : {}),
              ...(actions?.length ? { actions } : {}),
            }
          : null;
      })
      .filter(Boolean) as PolicyIssue[];
    if (policyIssues.length > 0) sanitized.policyIssues = policyIssues;
  }
  if (Array.isArray((meta as any).degradations)) {
    const degradations = ((meta as any).degradations as unknown[])
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null;
        const record = entry as Record<string, unknown>;
        const stage = typeof record.stage === 'string' ? record.stage.trim().slice(0, 100) : '';
        const code = typeof record.code === 'string' ? record.code.trim().slice(0, 100) : '';
        const message = typeof record.message === 'string' ? record.message.trim().slice(0, 500) : '';
        if (!stage || !code || !message) return null;
        const severity = record.severity === 'info' || record.severity === 'warning' || record.severity === 'critical'
          ? record.severity
          : 'warning';
        const at = typeof record.at === 'string' && record.at.trim() ? record.at.trim() : undefined;
        return { stage, code, severity, message, ...(at ? { at } : {}) };
      })
      .filter(Boolean)
      .slice(0, 20) as NonNullable<RunMeta['degradations']>;
    if (degradations.length > 0) sanitized.degradations = degradations;
  }
  if (meta.consistency && typeof meta.consistency === 'object') {
    const consistency = meta.consistency as Record<string, any>;
    const suggestions = Array.isArray(consistency.suggestions)
      ? (consistency.suggestions as Array<any>)
          .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const label = typeof item.label === 'string' ? item.label : null;
            const recommended = typeof item.recommended === 'string' ? item.recommended : null;
            if (!label || !recommended) return null;
            const severity = item.severity === 'high' ? 'high' : 'medium';
            return {
              key: typeof item.key === 'string' ? item.key : label,
              label,
              recommended,
              severity,
              context: typeof item.context === 'string' ? item.context : undefined,
            };
          })
          .filter(Boolean) as Array<{ key: string; label: string; severity: 'medium' | 'high'; recommended: string; context?: string }>
      : undefined;
    sanitized.consistency = {
      contradictions: typeof consistency.contradictions === 'number' ? consistency.contradictions : undefined,
      conflicts: Array.isArray(consistency.conflicts) ? consistency.conflicts.slice(0, 10) : undefined,
      suggestions,
    };
  }
  if (meta.autoFix && typeof meta.autoFix === 'object') {
    const autoFix = meta.autoFix as Record<string, any>;
    sanitized.autoFix = {
      appliedCount: typeof autoFix.appliedCount === 'number' ? autoFix.appliedCount : undefined,
      totalSuggestions: typeof autoFix.totalSuggestions === 'number' ? autoFix.totalSuggestions : undefined,
      suggestions: Array.isArray(autoFix.suggestions) ? autoFix.suggestions : undefined,
      resolved: Array.isArray(autoFix.resolved) ? autoFix.resolved : undefined,
    };
  }
  if (meta.pillars && typeof meta.pillars === 'object') {
    const pillars = meta.pillars as Record<string, any>;
    sanitized.pillars = {
      score: typeof pillars.score === 'number' ? pillars.score : undefined,
      coverage: Array.isArray(pillars.coverage) ? pillars.coverage : undefined,
      threshold: typeof pillars.threshold === 'number' ? pillars.threshold : undefined,
    };
  }
  if (meta.knowledgePack && typeof meta.knowledgePack === 'object') {
    const pack = meta.knowledgePack as Record<string, any>;
    sanitized.knowledgePack = {
      version: typeof pack.version === 'number' ? pack.version : undefined,
      updatedAt: typeof pack.updatedAt === 'string' ? pack.updatedAt : undefined,
      quotes: Array.isArray(pack.quotes) ? pack.quotes : undefined,
      stats: Array.isArray(pack.stats) ? pack.stats : undefined,
      talkingPoints: Array.isArray(pack.talkingPoints) ? pack.talkingPoints : undefined,
    };
  }
  if (meta.supervisor && typeof meta.supervisor === 'object') {
    const supervisor = meta.supervisor as Record<string, any>;
    sanitized.supervisor = {
      runId: typeof supervisor.runId === 'string' ? supervisor.runId : undefined,
      correlationId: typeof supervisor.correlationId === 'string' ? supervisor.correlationId : undefined,
      plannerRoute: Array.isArray(supervisor.plannerRoute) ? supervisor.plannerRoute : undefined,
    };
  }
  return Object.keys(sanitized).length > 0 ? sanitized : null;
};

const mergePreferenceLayersForUi = (input: {
  baseline: ClientPreferences | null;
  explicit: ClientPreferences | null;
  inferred: ClientPreferences | null;
}): ClientPreferences | null => {
  const baseline = input.baseline;
  const explicit = input.explicit;
  const inferred = input.inferred && Number(input.inferred.confidence || 0) >= 0.75 ? input.inferred : null;
  const layered: ClientPreferences = {
    version: explicit?.version || inferred?.version || baseline?.version || 1,
    updatedAt: explicit?.updatedAt || inferred?.updatedAt || baseline?.updatedAt || new Date().toISOString(),
  };
  const applyLayer = (layer: ClientPreferences | null) => {
    if (!layer) return;
    if (layer.locale) layered.locale = layer.locale;
    if (layer.spokesperson) layered.spokesperson = layer.spokesperson;
    if (layer.mediaContact) layered.mediaContact = layer.mediaContact;
    if (layer.tone) layered.tone = layer.tone;
    if (Array.isArray(layer.styleNotes)) layered.styleNotes = layer.styleNotes;
    if (layer.lengthPreference) layered.lengthPreference = layer.lengthPreference;
    if (layer.structurePreference) layered.structurePreference = layer.structurePreference;
    if (Array.isArray(layer.forbiddenTerms)) layered.forbiddenTerms = layer.forbiddenTerms;
    if (Array.isArray(layer.requiredTerms)) layered.requiredTerms = layer.requiredTerms;
    if (layer.updatedBy) layered.updatedBy = layer.updatedBy;
  };
  applyLayer(baseline);
  applyLayer(inferred);
  applyLayer(explicit);
  const hasPayload = !!(
    layered.tone ||
    layered.lengthPreference ||
    layered.structurePreference ||
    layered.styleNotes?.length ||
    layered.requiredTerms?.length ||
    layered.forbiddenTerms?.length ||
    layered.spokesperson ||
    layered.mediaContact ||
    layered.locale
  );
  if (!hasPayload) return null;
  if (explicit) {
    return {
      ...layered,
      source: 'user_client_explicit',
      confidence: 1,
      updatedAt: explicit.updatedAt || layered.updatedAt,
    };
  }
  if (inferred) {
    return {
      ...layered,
      source: 'user_client_inferred',
      confidence: inferred.confidence,
      updatedAt: inferred.updatedAt || layered.updatedAt,
    };
  }
  if (baseline) {
    return {
      ...layered,
      source: baseline.source || 'client_baseline',
      confidence: baseline.confidence ?? 1,
      updatedAt: baseline.updatedAt || layered.updatedAt,
    };
  }
  return layered;
};
// PHASE-02 END

const toMillis = (value: any): number => {
  if (!value) return Date.now();
  if (typeof value === 'number') return value;
  try {
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') {
      const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
      return value.seconds * 1000 + Math.floor(nanos / 1e6);
    }
  } catch {}
  return Date.now();
};

const ARABIC_SCRIPT_REGEX = /[\u0600-\u06FF]/;
const ENGLISH_SCRIPT_REGEX = /[A-Za-z]/;

const isSmallTalkMessage = (text: string): boolean => {
  const normalized = text.trim().replace(/\s+/g, ' ').toLowerCase();
  if (!normalized) return false;

  const containsFollowUpAction = /\b(can you|could you|please|rewrite|redraft|edit|revise|change|update|improve|shorten|lengthen|translate|add|remove|make)\b/.test(normalized);
  if (containsFollowUpAction) return false;

  if (normalized.length > 120) return false;

  const shortPatterns = [
    /^(hi|hello|hey|hey there|yo|sup|howdy)[!.?]*$/,
    /^(good morning|good afternoon|good evening|morning|afternoon|evening)[!.?]*$/,
    /^(thanks|thank you|thx|ok|okay|cool|nice|great|awesome)[!.?]*$/,
    /^(how are you|what's up|whats up)[?.!]*$/,
    /^(مرحبا|أهلا|اهلا|السلام عليكم)[!.?]*$/,
  ];
  if (shortPatterns.some((pattern) => pattern.test(normalized))) return true;

  const feedbackAckPatterns = [
    /^(thanks|thank you|thx)[,!.\s].*\b(draft|response|output|version|this|it)\b.*\b(ok|okay|good|great|fine|solid|works|looks good|looks great|approved)\b[!.?]*$/,
    /^(thanks|thank you|thx)[,!.\s].*\b(much better|better|improved|looks better)\b[!.?]*$/,
    /^(this|it|the draft|the response)\b.*\b(is|looks|seems)\b.*\b(ok|okay|good|great|fine|solid)\b[!.?]*$/,
    /^(this|it|the draft|the response)\b.*\b(is|looks|seems)\b.*\b(much better|better|improved|clearer)\b[!.?]*$/,
    /^(looks good|looks great|all good|all set|approved|ship it)[!.?]*$/,
  ];

  return feedbackAckPatterns.some((pattern) => pattern.test(normalized));
};

const isLikelyEditContinuation = (text: string): boolean => {
  const normalized = text.trim().replace(/\s+/g, ' ').toLowerCase();
  if (!normalized) return false;

  const keywordHits = [
    'rewrite',
    'redraft',
    'edit',
    'revise',
    'shorter',
    'longer',
    'expand',
    'improve',
    'fix',
    'change tone',
    'make it',
    'rephrase',
    'polish',
    'translate',
    'adjust',
    'refine',
  ].some((keyword) => normalized.includes(keyword));

  if (keywordHits) return true;
  return normalized.length < 80 && /^(can you|please|now|also|next)\b/.test(normalized);
};

const resolveRunLanguage = (conversationLanguage: 'en' | 'ar', input: string): 'en' | 'ar' => {
  const hasArabic = ARABIC_SCRIPT_REGEX.test(input);
  const hasEnglish = ENGLISH_SCRIPT_REGEX.test(input);

  if (hasArabic && !hasEnglish) return 'ar';
  if (hasEnglish && !hasArabic) return 'en';
  return conversationLanguage;
};

const isPermissionDeniedError = (error: unknown): boolean => {
  const code = typeof (error as any)?.code === 'string'
    ? String((error as any).code).toLowerCase()
    : '';
  const message = typeof (error as any)?.message === 'string'
    ? String((error as any).message).toLowerCase()
    : '';
  return code === 'permission-denied'
    || code === 'firestore/permission-denied'
    || message.includes('missing or insufficient permissions')
    || message.includes('permission-denied');
};

const normalizeClarificationsMap = (raw: any): Partial<Record<ClarifyType, StoredClarificationEntry>> => {
  if (!raw || typeof raw !== 'object') return {};
  const normalized: Partial<Record<ClarifyType, StoredClarificationEntry>> = {};
  for (const type of CLARIFY_TYPES) {
    const entry = raw[type];
    if (!entry || typeof entry !== 'object') continue;
    const text = typeof entry.text === 'string' ? entry.text : '';
    if (!text) continue;
    normalized[type] = {
      text,
      placeholderCount: typeof entry.placeholderCount === 'number' ? entry.placeholderCount : 0,
      skipped: entry.skipped === true,
      updatedAt: toMillis(entry.updatedAt),
    };
  }
  return normalized;
};


const CLARIFY_QUEUE_STORAGE_PREFIX = 'chat.clarify.queue:';

const generateClarifyRequestId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {}
  return `clarify-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
};

const parseStoredClarifyQueue = (raw: string | null): ClarifyRequest[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item: any) => {
        if (!item || typeof item !== 'object') return null;
        const type: ClarifyType =
          item.type === 'generic' || item.type === 'pitch' || item.type === 'social' || item.type === 'research'
            ? item.type
            : 'press';
        const questions = Array.isArray(item.questions) ? item.questions.map((q: any) => String(q)) : [];
        if (!questions.length) return null;
        return {
          id: typeof item.id === 'string' && item.id ? item.id : generateClarifyRequestId(),
          type,
          questions,
          agentLabel: typeof item.agentLabel === 'string' ? item.agentLabel : undefined,
          originRunId: typeof item.originRunId === 'string' && item.originRunId.trim() ? item.originRunId.trim() : undefined,
          createdAt: typeof item.createdAt === 'number' ? item.createdAt : Date.now(),
          locale: item.locale === 'ar' ? 'ar' : 'en'
        } as ClarifyRequest;
      })
      .filter(Boolean) as ClarifyRequest[];
  } catch {
    return [];
  }
};

const persistClarifyQueue = (conversationId: string | undefined, queue: ClarifyRequest[]): void => {
  if (!conversationId || typeof window === 'undefined') return;
  const key = `${CLARIFY_QUEUE_STORAGE_PREFIX}${conversationId}`;
  try {
    if (!queue.length) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(queue));
  } catch {}
};

export function ChatProvider({ children }: { children: ReactNode }) {
  const { currentUser, refreshToken } = useAuth();
  const { generateResponse } = useAI();
  const { activeClient, setActiveClient } = useClient();
  const { updateClient } = useClientManagement();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingRunMessages, setPendingRunMessages] = useState<Record<string, Message>>({});
  const pendingRunWatchersRef = useRef<Map<string, () => void>>(new Map());
  const pendingRunHydrationRef = useRef<Set<string>>(new Set());
  const clarifyRequestByRunIdRef = useRef<Map<string, { requestId?: string; type?: ClarifyType; originRunId?: string }>>(new Map());
  const pendingRunsAccessRef = useRef<'unknown' | 'ok' | 'denied'>('unknown');
  const pendingRunHealthResolversRef = useRef<Map<string, (status: PendingRunStatus) => void>>(new Map());
  const pendingRunHealthRejectorsRef = useRef<Map<string, (error: Error) => void>>(new Map());
  const pendingConversationClientRef = useRef<Record<string, string | null>>({});
  const desiredConversationClientRef = useRef<Record<string, string | null>>({});
  const conversationCreateInFlightRef = useRef<Map<string, Promise<string>>>(new Map());
  const [loading, setLoading] = useState<boolean>(false);
  const [isCleaningUp, setIsCleaningUp] = useState<boolean>(false);
  const { flags } = useFeatureFlags();
  
  // In-Chat Learning state
  const [learningSuggestion, setLearningSuggestion] = useState<LearningSuggestionState | null>(null);
  // Interactive clarification queue (supports multiple requests + persistence)
  const [clarifyRequests, setClarifyRequests] = useState<ClarifyRequest[]>([]);
  const clarifyPending = clarifyRequests.length ? clarifyRequests[0] : null;
  const clarificationAckRef = useRef<ClarificationSubmissionMeta | null>(null);
  const [storedClarifications, setStoredClarifications] = useState<Record<string, Partial<Record<ClarifyType, StoredClarificationEntry>>>>({});
  const [reviewerFeedback, setReviewerFeedback] = useState<Record<string, ReviewerFeedback>>({});
  const [clientPreferencesMap, setClientPreferencesMap] = useState<Record<string, ClientPreferences | null>>({});
  const [preferencesLoading, setPreferencesLoading] = useState<boolean>(false);

  const preferenceClientId = currentConversation?.clientId || activeClient?.id;
  const clientPreferences = preferenceClientId ? clientPreferencesMap[preferenceClientId] || null : null;

  const getConversationClarifications = useCallback((conversationId?: string): Partial<Record<ClarifyType, StoredClarificationEntry>> => {
    if (!conversationId) return {};
    const stored = storedClarifications[conversationId] || {};
    const legacy = currentConversation?.id === conversationId
      ? currentConversation?.clarifications || {}
      : (conversations.find((c) => c.id === conversationId)?.clarifications || {});
    return { ...legacy, ...stored };
  }, [conversations, currentConversation, storedClarifications]);

  const updateConversationClarifications = useCallback((conversationId: string, type: ClarifyType, entry: StoredClarificationEntry) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId
          ? { ...c, clarifications: { ...(c.clarifications || {}), [type]: entry } }
          : c
      )
    );
    setCurrentConversation((prev) => {
      if (!prev || prev.id !== conversationId) return prev;
      return { ...prev, clarifications: { ...(prev.clarifications || {}), [type]: entry } };
    });
    setStoredClarifications((prev) => ({
      ...prev,
      [conversationId]: {
        ...(prev[conversationId] || {}),
        [type]: entry,
      },
    }));
  }, [setConversations, setCurrentConversation, setStoredClarifications]);

  const persistClarificationEntry = useCallback(
    async (
      conversationId: string,
      type: ClarifyType,
      entry: { text: string; placeholderCount: number; skipped?: boolean; language?: 'en' | 'ar' }
    ) => {
      if (!currentUser) return;
      const clarificationRef = doc(
        db,
        'users',
        currentUser.uid,
        'conversations',
        conversationId,
        'clarifications',
        type
      );
      const localEntry: StoredClarificationEntry = {
        text: entry.text,
        placeholderCount: entry.placeholderCount,
        skipped: entry.skipped ?? false,
        updatedAt: Date.now(),
      };
      try {
        await setDoc(clarificationRef, {
          type,
          text: entry.text,
          placeholderCount: entry.placeholderCount,
          skipped: entry.skipped ?? false,
          updatedAt: serverTimestamp(),
          language: entry.language || 'en',
          source: 'conversation',
          v: 1
        });
      } catch (e) {
        console.error('[ChatContext] clarifications_save_failed', (e as any)?.message);
      }
      updateConversationClarifications(conversationId, type, localEntry);
    },
    [currentUser, updateConversationClarifications]
  );

  const clearClarifications = useCallback(
    async (conversationId: string, scope: 'all' | ClarifyType = 'all') => {
      if (!currentUser) return;
      const conversationRef = doc(db, 'users', currentUser.uid, 'conversations', conversationId);
      try {
        if (scope === 'all') {
          const batch = writeBatch(db);
          CLARIFY_TYPES.forEach((type) => {
            const clarificationRef = doc(
              db,
              'users',
              currentUser.uid,
              'conversations',
              conversationId,
              'clarifications',
              type
            );
            batch.delete(clarificationRef);
          });
          batch.update(conversationRef, {
            clarifications: deleteField(),
            updatedAt: serverTimestamp(),
          });
          await batch.commit();
          setStoredClarifications((prev) => ({ ...prev, [conversationId]: {} }));
          setConversations((prev) =>
            prev.map((c) => (c.id === conversationId ? { ...c, clarifications: {} } : c))
          );
          setCurrentConversation((prev) => {
            if (!prev || prev.id !== conversationId) return prev;
            return { ...prev, clarifications: {} };
          });
          return;
        }

        const clarificationRef = doc(
          db,
          'users',
          currentUser.uid,
          'conversations',
          conversationId,
          'clarifications',
          scope
        );
        await deleteDoc(clarificationRef);
        try {
          await updateDoc(conversationRef, {
            [`clarifications.${scope}`]: deleteField(),
            updatedAt: serverTimestamp(),
          });
        } catch {}
        setStoredClarifications((prev) => {
          const nextClarifications = { ...(prev[conversationId] || {}) };
          delete nextClarifications[scope];
          return { ...prev, [conversationId]: nextClarifications };
        });
        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== conversationId) return c;
            const nextClarifications = { ...(c.clarifications || {}) };
            delete nextClarifications[scope];
            return { ...c, clarifications: nextClarifications };
          })
        );
        setCurrentConversation((prev) => {
          if (!prev || prev.id !== conversationId) return prev;
          const nextClarifications = { ...(prev.clarifications || {}) };
          delete nextClarifications[scope];
          return { ...prev, clarifications: nextClarifications };
        });
      } catch (e) {
        console.error('[ChatContext] clarifications_clear_failed', (e as any)?.message);
        try {
          (toast as any)?.error?.('Failed to clear clarifications.');
        } catch {}
      }
    },
    [currentUser]
  );

  const composeClarificationsPayload = useCallback(
    (conversationId: string, baseText?: string, excludeType?: ClarifyType) => {
      const clarifications = getConversationClarifications(conversationId);
      const extraSegments: string[] = [];
      const reusedTypes: ClarifyType[] = [];
      for (const type of CLARIFY_TYPES) {
        if (excludeType && type === excludeType) continue;
        const entry = clarifications?.[type];
        const entryText = typeof entry?.text === 'string' ? entry.text.trim() : '';
        const shouldSkipEntry = !entryText || !!entry?.skipped || isClarifySkipToken(entryText);
        if (shouldSkipEntry) continue;
        extraSegments.push(entryText);
        reusedTypes.push(type);
      }
      let text = baseText;
      if (text) {
        if (extraSegments.length) {
          text = [text, ...extraSegments].join('\n\n');
        }
      } else if (extraSegments.length) {
        text = extraSegments.join('\n\n');
      }
      return { text, reusedTypes };
    },
    [getConversationClarifications]
  );

  const updateClientPreferences = useCallback(
    async (
      clientId: string,
      updates: Partial<ClientPreferences>,
      options?: { source?: string }
    ): Promise<ClientPreferences | null> => {
      if (!clientId) return null;
      if (!updates || !Object.keys(updates).length) return null;
      if (!currentUser?.uid) return null;
      const source = options?.source || 'preferences_drawer';
      try {
        const saved = await saveUserClientExplicitPreferences(currentUser.uid, clientId, updates, {
          current: clientPreferencesMap[clientId] || null,
          locale: (currentConversation?.language as 'en' | 'ar') || flags.localeDefault,
        });
        if (saved) {
          setClientPreferencesMap((prev) => ({ ...prev, [clientId]: saved }));
          void createPreferenceEvent({
            userId: currentUser.uid,
            clientId,
            signalType: 'explicit',
            updates,
            confidence: 1,
            source,
          });
          try {
            const token = await currentUser.getIdToken();
            void syncPreferenceEventToGraph(
              {
                userId: currentUser.uid,
                clientId,
                signalType: 'explicit',
                updates,
                confidence: 1,
                source,
              },
              token,
            );
          } catch {}

          if (currentConversation?.id) {
            const runtimeRef = doc(
              db,
              'users',
              currentUser.uid,
              'conversations',
              currentConversation.id,
              'runtimePreferences',
              'current'
            );
            const nowIso = new Date().toISOString();
            await setDoc(
              runtimeRef,
              {
                ...updates,
                source: 'session_override',
                confidence: 1,
                updatedAt: nowIso,
                updatedBy: currentUser.uid,
              },
              { merge: true }
            );
          }
        }
        return saved;
      } catch (error) {
        console.warn('[preferences] update_failed', { clientId, error: (error as any)?.message || error });
        return null;
      }
    },
    [clientPreferencesMap, currentConversation?.id, currentConversation?.language, currentUser, flags.localeDefault, setClientPreferencesMap]
  );
  
  // Helper function to verify authentication state
  const verifyAuthState = async (): Promise<boolean> => {
    if (!currentUser) {
      return false;
    }
    
    // Check if currentUser has a valid UID
    if (!currentUser.uid) {
      console.error("User object exists but has no UID");
      return false;
    }
    
    // Try to refresh token to ensure it's valid
    try {
      const token = await refreshToken();
      if (!token) {
        console.error("Token refresh returned null");
        
        // Try one more time with a short delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        const retryToken = await refreshToken();
        if (!retryToken) {
          console.error("Token refresh retry failed");
          return false;
        }
        return true;
      }
      return true;
    } catch (err) {
      console.error("Token refresh failed:", err);
      return false;
    }
  };

  // Hydrate clarification queue from localStorage when switching conversations
  useEffect(() => {
    if (!currentConversation?.id) {
      setClarifyRequests([]);
      return;
    }
    if (typeof window === 'undefined') return;
    try {
      const key = `${CLARIFY_QUEUE_STORAGE_PREFIX}${currentConversation.id}`;
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        setClarifyRequests([]);
        return;
      }
      const parsed = parseStoredClarifyQueue(raw);
      setClarifyRequests(parsed);
    } catch {
      setClarifyRequests([]);
    }
  }, [currentConversation?.id]);

  // Persist clarification queue whenever it changes for the active conversation
  useEffect(() => {
    if (!currentConversation?.id) return;
    persistClarifyQueue(currentConversation.id, clarifyRequests);
  }, [clarifyRequests, currentConversation?.id]);

  const updateClarifyQueueForConversation = useCallback(
    (targetConversationId: string | undefined, updater: (prev: ClarifyRequest[]) => ClarifyRequest[]) => {
      if (!targetConversationId) return;
      if (targetConversationId === currentConversation?.id) {
        setClarifyRequests((prev) => updater(prev));
        return;
      }
      if (typeof window === 'undefined') return;
      const key = `${CLARIFY_QUEUE_STORAGE_PREFIX}${targetConversationId}`;
      const base = parseStoredClarifyQueue(window.localStorage.getItem(key));
      const next = updater(base);
      persistClarifyQueue(targetConversationId, next);
      // A new conversation can be created and selected while the async run
      // closure still sees the previous conversation id. Keep the visible
      // queue unblocked; hydration will correct it on the next conversation switch.
      setClarifyRequests(next);
    },
    [currentConversation?.id]
  );

  useEffect(() => {
    if (!preferenceClientId) {
      setPreferencesLoading(false);
      return;
    }
    let didTrack = false;
    setPreferencesLoading(true);
    let baseline: ClientPreferences | null = null;
    let explicit: ClientPreferences | null = null;
    let inferred: ClientPreferences | null = null;
    const unsubscribers: Array<() => void> = [];

    const applyMerged = () => {
      const merged = mergePreferenceLayersForUi({ baseline, explicit, inferred });
      setClientPreferencesMap((prev) => ({ ...prev, [preferenceClientId]: merged }));
      if (!didTrack) {
        trackPreferenceMetric('client_preferences_loaded_count', {
          clientId: preferenceClientId,
          hasPreferences: !!merged,
        });
        didTrack = true;
      }
      setPreferencesLoading(false);
    };

    const baselineRef = doc(db, 'clients', preferenceClientId, 'preferences', 'meta');
    unsubscribers.push(
      onSnapshot(
        baselineRef,
        (snapshot) => {
          baseline = snapshot.exists()
            ? normalizeClientPreferences(snapshot.data() as Record<string, unknown>)
            : null;
          applyMerged();
        },
        (error) => {
          console.warn('[preferences] baseline_listen_failed', (error as any)?.message || error);
          setPreferencesLoading(false);
        }
      )
    );

    if (currentUser?.uid) {
      const userPrefRef = doc(db, 'users', currentUser.uid, 'clientPreferences', preferenceClientId);
      unsubscribers.push(
        onSnapshot(
          userPrefRef,
          (snapshot) => {
            if (!snapshot.exists()) {
              explicit = null;
              inferred = null;
              applyMerged();
              return;
            }
            const data = snapshot.data() as Record<string, unknown>;
            explicit = normalizeClientPreferences((data.explicit as Record<string, unknown>) || null);
            inferred = normalizeClientPreferences((data.inferred as Record<string, unknown>) || null);
            applyMerged();
          },
          (error) => {
            console.warn('[preferences] user_client_listen_failed', (error as any)?.message || error);
          }
        )
      );
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [currentUser?.uid, preferenceClientId]);

  useEffect(() => {
    if (!currentUser || !currentConversation?.id) return;
    const clarificationsRef = collection(
      db,
      'users',
      currentUser.uid,
      'conversations',
      currentConversation.id,
      'clarifications'
    );
    const unsubscribe = onSnapshot(
      clarificationsRef,
      (snapshot) => {
        const next: Partial<Record<ClarifyType, StoredClarificationEntry>> = {};
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          const rawType = typeof data.type === 'string' ? data.type : docSnap.id;
          const type = CLARIFY_TYPES.includes(rawType as ClarifyType) ? (rawType as ClarifyType) : null;
          if (!type) return;
          const text = typeof data.text === 'string' ? data.text : '';
          if (!text && data.skipped !== true) return;
          next[type] = {
            text,
            placeholderCount: typeof data.placeholderCount === 'number' ? data.placeholderCount : 0,
            skipped: data.skipped === true,
            updatedAt: toMillis((data as any).updatedAt),
          };
        });
        const legacy = normalizeClarificationsMap(currentConversation?.clarifications);
        setStoredClarifications((prev) => ({
          ...prev,
          [currentConversation.id]: { ...legacy, ...next },
        }));
      },
      (error) => {
        console.warn('[ChatContext] clarifications_listener_failed', (error as any)?.message || error);
      }
    );
    return () => unsubscribe();
  }, [currentConversation?.clarifications, currentConversation?.id, currentUser]);

  useEffect(() => {
    if (!currentConversation?.id) return;
    const legacy = normalizeClarificationsMap(currentConversation?.clarifications);
    if (!Object.keys(legacy).length) return;
    setStoredClarifications((prev) => ({
      ...prev,
      [currentConversation.id]: {
        ...legacy,
        ...(prev[currentConversation.id] || {}),
      },
    }));
  }, [currentConversation?.clarifications, currentConversation?.id]);

  useEffect(() => {
    if (!currentUser || !currentConversation?.id) {
      setReviewerFeedback({});
      return;
    }
    const feedbackRef = collection(
      db,
      'users',
      currentUser.uid,
      'conversations',
      currentConversation.id,
      'reviewHistory'
    );
    const feedbackQuery = query(feedbackRef, orderBy('createdAt', 'desc'), limit(50));
    const unsubscribe = onSnapshot(
      feedbackQuery,
      (snapshot) => {
        const next: Record<string, ReviewerFeedback> = {};
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data() as ReviewerFeedback;
          const messageId = typeof data.appliedToMessageId === 'string' ? data.appliedToMessageId : '';
          if (!messageId) return;
          if (!next[messageId]) {
            next[messageId] = {
              ...data,
              id: data.id || docSnap.id,
              appliedToMessageId: messageId,
            };
          }
        });
        setReviewerFeedback(next);
      },
      (error) => {
        console.warn('[reviewer] load_failed', (error as any)?.message || error);
      }
    );
    return () => unsubscribe();
  }, [currentConversation?.id, currentUser]);
  
  // Load user conversations
  useEffect(() => {
    if (!currentUser) {
      setConversations([]);
      setCurrentConversation(null);
      setMessages([]);
      setStoredClarifications({});
      setReviewerFeedback({});
      setClientPreferencesMap({});
      setPreferencesLoading(false);
      return;
    }

    const conversationsRef = collection(db, 'users', currentUser.uid, 'conversations');
    const q = query(
      conversationsRef, 
      orderBy('updatedAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const conversationsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        const clarifications = normalizeClarificationsMap((data as any).clarifications);
        return {
          id: doc.id,
          ...data,
          clarifications,
        } as Conversation;
      });

      setConversations(conversationsData);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Load messages for current conversation
  useEffect(() => {
    if (!currentUser || !currentConversation) {
      setMessages([]);
      return;
    }

    const messagesRef = collection(
      db, 
      'users', 
      currentUser.uid, 
      'conversations', 
      currentConversation.id, 
      'messages'
    );
    
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Message));
      
      setMessages(messagesData);
    });

    return () => unsubscribe();
  }, [currentUser, currentConversation]);

  const getAgentDescription = useCallback((agentType: string): string => {
    const descriptions = {
      crisis: 'Expert in crisis communication and damage control',
      media: 'Specialist in media outreach and relationship building',
      social: 'Expert in social media campaigns and engagement',
      executive: 'Specialist in C-suite and executive messaging',
      content: 'Expert in creating compelling PR content',
      general: 'General purpose AI assistant'
    };
    return descriptions[agentType as keyof typeof descriptions] || 'AI specialist';
  }, []);

  const getAgentExpertise = useCallback((agentType: string): string[] => {
    const expertise = {
      crisis: ['Crisis Management', 'Reputation Recovery', 'Emergency Response'],
      media: ['Media Pitching', 'Press Relations', 'Journalist Outreach'],
      social: ['Social Strategy', 'Community Management', 'Viral Content'],
      executive: ['Executive Messaging', 'Board Communications', 'Leadership Voice'],
      content: ['Content Writing', 'Press Releases', 'Brand Storytelling'],
      general: ['General Assistance', 'Content Creation', 'Communication Support']
    };
    return expertise[agentType as keyof typeof expertise] || ['AI Assistance'];
  }, []);

  const normalizeHandoffAgentName = useCallback((agentId: string, fallbackName?: string): string => {
    if (agentId === 'writer') return 'Drafter';
    if (agentId === 'writer_refine') return 'Drafter';
    if (agentId === 'editor') return 'Reviewer';
    if (agentId === 'reviewer') return 'Reviewer';
    if (fallbackName && fallbackName.trim()) return fallbackName.trim();
    return `${agentId.charAt(0).toUpperCase()}${agentId.slice(1)}`;
  }, []);

  const normalizeHandoffAgentType = useCallback((raw: unknown): PendingRunAgent['type'] | null => {
    if (raw === 'crisis' || raw === 'media' || raw === 'social' || raw === 'executive' || raw === 'content' || raw === 'general') {
      return raw;
    }
    return null;
  }, []);

  const shouldRenderHandoffAgent = useCallback((agentId?: string | null): boolean => {
    if (!agentId || typeof agentId !== 'string') return false;
    const normalized = agentId.trim().toLowerCase();
    return !['router', 'routing', 'orchestrator', 'coordinator', 'planner', 'system'].includes(normalized);
  }, []);

  const inferHandoffAgentType = useCallback((...parts: Array<string | undefined>): PendingRunAgent['type'] => {
    const value = parts.filter(Boolean).join(' ').toLowerCase();
    if (value.includes('crisis')) return 'crisis';
    if (value.includes('media') || value.includes('pitch') || value.includes('journal')) return 'media';
    if (value.includes('social')) return 'social';
    if (value.includes('executive') || value.includes('leadership')) return 'executive';
    if (value.includes('writer') || value.includes('draft') || value.includes('editor') || value.includes('review') || value.includes('content')) return 'content';
    return 'general';
  }, []);

  const formatAgentNodeName = useCallback((nodeId: string): string => {
    return nodeId
      .replace(/[_-]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
      .join(' ');
  }, []);

  const resolveStepDrivenAgent = useCallback((step?: string): PendingRunAgent | null => {
    const value = typeof step === 'string' ? step.trim().toLowerCase() : '';
    if (!value) return null;

    if (value.includes('review') || value.includes('editor')) {
      return {
        id: 'editor',
        name: 'Reviewer',
        type: 'content',
        description: 'Reviewing and improving draft',
      };
    }
    if (value.includes('draft') || value.includes('write') || value.includes('redraft') || value.includes('revise') || value.includes('clarif')) {
      return {
        id: 'writer',
        name: 'Drafter',
        type: 'content',
        description: 'Drafting content',
      };
    }
    if (value.includes('social')) {
      return {
        id: 'social',
        name: 'Social Agent',
        type: 'social',
        description: 'Drafting social content',
      };
    }
    if (value.includes('pitch') || value.includes('media')) {
      return {
        id: 'media',
        name: 'Media Agent',
        type: 'media',
        description: 'Generating media pitch',
      };
    }
    if (value.includes('retriev') || value.includes('source') || value.includes('document')) {
      return {
        id: 'retrieval',
        name: 'Retriever',
        type: 'general',
        description: 'Gathering client documents',
      };
    }
    if (value.includes('research')) {
      return {
        id: 'researcher',
        name: 'Researcher',
        type: 'general',
        description: 'Gathering current sources and angles',
      };
    }
    if (value.includes('answer') || value.includes('qa')) {
      return {
        id: 'qa',
        name: 'Assistant',
        type: 'general',
        description: 'Answering your question',
      };
    }
    if (value.includes('insight') || value.includes('coverage') || value.includes('analysis')) {
      return {
        id: 'insights',
        name: 'Insights Agent',
        type: 'general',
        description: 'Generating coverage insights',
      };
    }
    return null;
  }, []);

  const resolveHandoffAgent = useCallback((params: {
    nodeId?: string;
    fallbackName?: string;
    fallbackType?: unknown;
    fallbackDescription?: string;
    step?: string;
  }): PendingRunAgent | null => {
    const rawNode = typeof params.nodeId === 'string' ? params.nodeId.trim() : '';
    if (rawNode && shouldRenderHandoffAgent(rawNode)) {
      const nodeId = rawNode.toLowerCase();
      const normalizedType = normalizeHandoffAgentType(params.fallbackType);

      const presetMap: Record<string, PendingRunAgent> = {
        writer: {
          id: 'writer',
          name: 'Drafter',
          type: 'content',
          description: 'Drafting content',
        },
        writer_refine: {
          id: 'writer',
          name: 'Drafter',
          type: 'content',
          description: 'Applying reviewer notes',
        },
        drafter: {
          id: 'writer',
          name: 'Drafter',
          type: 'content',
          description: 'Drafting content',
        },
        editor: {
          id: 'editor',
          name: 'Reviewer',
          type: 'content',
          description: 'Reviewing and improving draft',
        },
        reviewer: {
          id: 'editor',
          name: 'Reviewer',
          type: 'content',
          description: 'Reviewing and improving draft',
        },
        retrieval: {
          id: 'retrieval',
          name: 'Retriever',
          type: 'general',
          description: 'Gathering client documents',
        },
        retriever: {
          id: 'retrieval',
          name: 'Retriever',
          type: 'general',
          description: 'Gathering client documents',
        },
        researcher: {
          id: 'researcher',
          name: 'Researcher',
          type: 'general',
          description: 'Gathering current sources and angles',
        },
        pitch: {
          id: 'media',
          name: 'Media Agent',
          type: 'media',
          description: 'Generating media pitch',
        },
        social: {
          id: 'social',
          name: 'Social Agent',
          type: 'social',
          description: 'Drafting social content',
        },
        insights: {
          id: 'insights',
          name: 'Insights Agent',
          type: 'general',
          description: 'Generating coverage insights',
        },
        qa: {
          id: 'qa',
          name: 'Assistant',
          type: 'general',
          description: 'Answering your question',
        },
      };

      const preset = presetMap[nodeId];
      if (preset) return preset;

      const inferredType = normalizedType || inferHandoffAgentType(nodeId, params.fallbackName, params.step);
      const inferredName = normalizeHandoffAgentName(
        nodeId,
        params.fallbackName || formatAgentNodeName(nodeId)
      );

      return {
        id: nodeId,
        name: inferredName,
        type: inferredType,
        description: params.fallbackDescription || getAgentDescription(inferredType),
      };
    }

    const stepDriven = resolveStepDrivenAgent(params.step);
    if (stepDriven) return stepDriven;
    return null;
  }, [
    formatAgentNodeName,
    getAgentDescription,
    inferHandoffAgentType,
    normalizeHandoffAgentName,
    normalizeHandoffAgentType,
    resolveStepDrivenAgent,
    shouldRenderHandoffAgent,
  ]);

  const normalizeNodeId = useCallback((value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    const clean = value.trim().toLowerCase();
    return clean || undefined;
  }, []);

  const deriveNodeStatusText = useCallback((nodeId?: string): string | undefined => {
    if (!nodeId) return undefined;
    switch (nodeId) {
      case 'router':
      case 'routing':
      case 'orchestrator':
      case 'coordinator':
      case 'planner':
      case 'clarify_gate':
        return 'Routing request';
      case 'writer':
      case 'drafter':
        return 'Drafting';
      case 'editor':
      case 'reviewer':
        return 'Reviewing';
      case 'editor_final':
        return 'Final review';
      case 'writer_refine':
        return 'Redrafting';
      case 'pitch':
        return 'Pitching';
      case 'social':
        return 'Social drafting';
      case 'insights':
        return 'Analyzing coverage';
      case 'research':
      case 'researcher':
        return 'Researching';
      case 'retrieval':
      case 'retriever':
      case 'rag_retrieval':
        return 'Retrieving client sources';
      case 'qa':
        return 'Answering';
      default:
        return `Running ${formatAgentNodeName(nodeId)}`;
    }
  }, [formatAgentNodeName]);

  const shouldReplaceGenericStatusWithNode = useCallback((statusText: string | undefined, nodeId?: string): boolean => {
    if (!nodeId) return false;
    if (!statusText) return true;
    const normalized = statusText.trim().toLowerCase();
    if (!normalized) return true;
    if (normalized === 'run complete' || normalized === 'completed') return false;
    const isRouterNode = ['router', 'routing', 'orchestrator', 'coordinator', 'planner', 'clarify_gate'].includes(nodeId);
    const genericStatus = normalized === 'routing request'
      || normalized === 'router active'
      || normalized === 'working on your request…'
      || normalized === 'working on your request...'
      || normalized === 'queued for processing…'
      || normalized === 'queued for processing...'
      || normalized === 'queued'
      || normalized === 'processing'
      || normalized === 'in progress';
    return genericStatus && !isRouterNode;
  }, []);

  const sanitizeLiveStatusText = useCallback((value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    const clean = value.replace(/\s+/g, ' ').trim();
    if (!clean) return undefined;
    if (clean.length > 96) {
      return `${clean.slice(0, 95).trim()}…`;
    }
    return clean;
  }, []);

  const formatHandoffCapabilityLabel = useCallback((value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    const normalized = value.trim().toLowerCase();
    if (!normalized) return undefined;

    const labelMap: Record<string, string> = {
      content: 'Content',
      crisis: 'Crisis',
      outreach: 'Outreach',
      integration: 'Integration',
      localization: 'Localization',
      'localization:ar': 'Arabic Localization',
      thought_leadership: 'Thought Leadership',
    };

    if (labelMap[normalized]) return labelMap[normalized];

    return normalized
      .replace(/[:/_-]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
      .join(' ');
  }, []);

  const summarizeHandoffPayloadKeys = useCallback((payload: unknown): string | undefined => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;
    const record = payload as Record<string, unknown>;
    const keys = Object.keys(record);
    if (!keys.length) return undefined;

    const priority = ['artifactType', 'deliveryMode', 'sequenceObjective', 'locale', 'outreachPlanId', 'incidentId'];
    const ordered = [
      ...priority.filter((key) => keys.includes(key)),
      ...keys.filter((key) => !priority.includes(key)),
    ];
    const picked = ordered.slice(0, 2);
    if (!picked.length) return undefined;

    return picked
      .map((key) => key.replace(/[_-]+/g, ' ').trim())
      .filter(Boolean)
      .join(', ');
  }, []);

  const extractHandoffNarrative = useCallback((payload: any): string | undefined => {
    if (!payload || typeof payload !== 'object') return undefined;

    const dispatchSecondary = payload?.meta?.dispatchSecondary || payload?.state?.meta?.dispatchSecondary;
    if (dispatchSecondary && typeof dispatchSecondary === 'object' && !Array.isArray(dispatchSecondary)) {
      const status = typeof (dispatchSecondary as any).status === 'string'
        ? String((dispatchSecondary as any).status).trim().toLowerCase()
        : '';
      const from = formatHandoffCapabilityLabel((dispatchSecondary as any).handoffFrom);
      const to = formatHandoffCapabilityLabel((dispatchSecondary as any).handoffTo);

      if (status === 'completed' && from && to) {
        return sanitizeLiveStatusText(`Handoff complete: ${from} -> ${to}`);
      }
      if ((status === 'error' || status === 'failed') && to) {
        return sanitizeLiveStatusText(`Handoff to ${to} failed`);
      }
      if (status && status !== 'no_secondary_route' && to) {
        return sanitizeLiveStatusText(`Preparing handoff to ${to}`);
      }
    }

    const envelope =
      payload?.work?.handoffEnvelope
      || payload?.work?.outreachHandoffEnvelope
      || payload?.state?.work?.handoffEnvelope
      || payload?.state?.work?.outreachHandoffEnvelope;
    if (envelope && typeof envelope === 'object' && !Array.isArray(envelope)) {
      const from = formatHandoffCapabilityLabel((envelope as any).from);
      const to = formatHandoffCapabilityLabel((envelope as any).to);
      if (from && to) {
        const payloadSummary = summarizeHandoffPayloadKeys((envelope as any).payload);
        const narrative = payloadSummary
          ? `Handing off ${from} -> ${to} (${payloadSummary})`
          : `Handing off ${from} -> ${to}`;
        return sanitizeLiveStatusText(narrative);
      }
    }

    const nodeId = typeof payload?.node === 'string' ? payload.node.trim().toLowerCase() : '';
    const likelyHandoffNode = nodeId.includes('handoff') || nodeId.includes('dispatch_secondary');
    if (likelyHandoffNode) {
      const to =
        formatHandoffCapabilityLabel(payload?.handoffTo)
        || formatHandoffCapabilityLabel(payload?.meta?.dispatchSecondary?.handoffTo)
        || formatHandoffCapabilityLabel(payload?.state?.meta?.dispatchSecondary?.handoffTo);
      if (to) {
        return sanitizeLiveStatusText(`Preparing handoff to ${to}`);
      }
    }

    return undefined;
  }, [formatHandoffCapabilityLabel, sanitizeLiveStatusText, summarizeHandoffPayloadKeys]);

  const extractLiveStatusText = useCallback((payload: any): string | undefined => {
    if (!payload || typeof payload !== 'object') return undefined;

    const candidateFields = [
      payload.statusText,
      payload.status_text,
      payload.step,
      payload.label,
      payload.message,
      payload.detail,
      payload.summary,
      payload.state?.statusText,
      payload.state?.status_text,
      payload.state?.step,
      payload.state?.label,
      payload.state?.message,
      payload.state?.detail,
      payload.work?.statusText,
      payload.work?.status_text,
      payload.work?.step,
      payload.work?.label,
      payload.work?.message,
      payload.work?.detail,
    ];

    for (const candidate of candidateFields) {
      const normalized = sanitizeLiveStatusText(candidate);
      if (normalized) return normalized;
    }

    const handoffNarrative = extractHandoffNarrative(payload);
    if (handoffNarrative) return handoffNarrative;

    const statusValue = sanitizeLiveStatusText(payload.status || payload.state?.status);
    if (!statusValue) return undefined;

    const normalizedStatus = statusValue.toLowerCase();
    if (normalizedStatus === 'started') return 'Routing request';
    if (normalizedStatus === 'completed' || normalizedStatus === 'done' || normalizedStatus === 'finished') {
      return 'Run complete';
    }
    return statusValue;
  }, [extractHandoffNarrative, sanitizeLiveStatusText]);

  const normalizePendingRunStatus = (raw: unknown): PendingRunStatus | null => {
    if (typeof raw !== 'string') return null;
    const value = raw.trim().toLowerCase();
    if (!value) return null;
    if (value === 'queued' || value === 'queue' || value === 'pending') return 'queued';
    if (value === 'in_progress' || value === 'running' || value === 'processing' || value === 'active' || value === 'working' || value === 'started') {
      return 'in_progress';
    }
    if (value === 'completed' || value === 'complete' || value === 'done' || value === 'finished' || value === 'success' || value === 'succeeded') {
      return 'completed';
    }
    if (value === 'needs_clarification' || value === 'needs_clarify' || value === 'clarification_needed' || value === 'needs_input') {
      return 'needs_clarification';
    }
    if (value === 'error' || value === 'failed' || value === 'failure') return 'error';
    if (value === 'expired' || value === 'timeout' || value === 'timed_out') return 'expired';
    return null;
  };

  const clearPendingRunHealthWaiter = useCallback((runId: string) => {
    pendingRunHealthResolversRef.current.delete(runId);
    pendingRunHealthRejectorsRef.current.delete(runId);
  }, []);

  const resolvePendingRunHealthWaiter = useCallback((runId: string, status: PendingRunStatus) => {
    const resolve = pendingRunHealthResolversRef.current.get(runId);
    if (!resolve) return;
    clearPendingRunHealthWaiter(runId);
    resolve(status);
  }, [clearPendingRunHealthWaiter]);

  const rejectPendingRunHealthWaiter = useCallback((runId: string, error: Error) => {
    const reject = pendingRunHealthRejectorsRef.current.get(runId);
    if (!reject) return;
    clearPendingRunHealthWaiter(runId);
    reject(error);
  }, [clearPendingRunHealthWaiter]);

  const waitForPendingRunHealth = useCallback((runId: string, timeoutMs = 15000): Promise<PendingRunStatus> => (
    new Promise<PendingRunStatus>((resolve, reject) => {
      clearPendingRunHealthWaiter(runId);
      const timeoutId = window.setTimeout(() => {
        clearPendingRunHealthWaiter(runId);
        reject(new Error('Clarification resume timed out. Please retry.'));
      }, timeoutMs);

      pendingRunHealthResolversRef.current.set(runId, (status) => {
        window.clearTimeout(timeoutId);
        resolve(status);
      });
      pendingRunHealthRejectorsRef.current.set(runId, (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
    })
  ), [clearPendingRunHealthWaiter]);

  const removePendingRunWatcher = useCallback((runId: string) => {
    const unsubscribe = pendingRunWatchersRef.current.get(runId);
    if (unsubscribe) {
      try {
        unsubscribe();
      } catch {}
      pendingRunWatchersRef.current.delete(runId);
    }
    clearPendingRunHealthWaiter(runId);
  }, [clearPendingRunHealthWaiter]);

  const clearPendingRunWatchers = useCallback(() => {
    pendingRunWatchersRef.current.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch {}
    });
    pendingRunHealthRejectorsRef.current.forEach((reject) => {
      try {
        reject(new Error('Pending run watcher cleared.'));
      } catch {}
    });
    pendingRunWatchersRef.current.clear();
    pendingRunHydrationRef.current.clear();
    clarifyRequestByRunIdRef.current.clear();
    pendingRunHealthResolversRef.current.clear();
    pendingRunHealthRejectorsRef.current.clear();
    setPendingRunMessages({});
  }, []);

  const clearPendingRunMessage = useCallback((runId: string) => {
    setPendingRunMessages((prev) => {
      if (!prev[runId]) return prev;
      const next = { ...prev };
      delete next[runId];
      return next;
    });
    clarifyRequestByRunIdRef.current.delete(runId);
    removePendingRunWatcher(runId);
  }, [removePendingRunWatcher]);

  const hydrateCompletedRunMessage = useCallback(
    async (conversationId: string, runId: string, finalMessageId: string) => {
      if (!currentUser || !conversationId || !finalMessageId) return;

      const hydrationKey = `${conversationId}:${runId}`;
      if (pendingRunHydrationRef.current.has(hydrationKey)) return;
      pendingRunHydrationRef.current.add(hydrationKey);

      try {
        const messageRef = doc(
          db,
          'users',
          currentUser.uid,
          'conversations',
          conversationId,
          'messages',
          finalMessageId
        );

        for (let attempt = 0; attempt < 6; attempt += 1) {
          const snapshot = await getDoc(messageRef);
          if (snapshot.exists()) {
            if (currentConversation?.id !== conversationId) {
              return;
            }
            const nextMessage = {
              id: snapshot.id,
              ...snapshot.data(),
            } as Message;

            setMessages((prev) => {
              const merged = [...prev.filter((msg) => msg.id !== nextMessage.id), nextMessage];
              merged.sort((a, b) => toMillis((a as any)?.timestamp) - toMillis((b as any)?.timestamp));
              return merged;
            });
            return;
          }

          await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 150 : 500));
        }

        console.warn('[jobs] completed run message was not readable after retries', {
          conversationId,
          runId,
          finalMessageId,
        });
      } catch (error) {
        console.warn('[jobs] completed run message hydration failed', {
          conversationId,
          runId,
          finalMessageId,
          error: (error as any)?.message || error,
        });
      } finally {
        pendingRunHydrationRef.current.delete(hydrationKey);
      }
    },
    [currentConversation?.id, currentUser]
  );

  const disablePendingRunsTracking = useCallback((reason: string, error?: unknown) => {
    if (pendingRunsAccessRef.current === 'denied') return;
    pendingRunsAccessRef.current = 'denied';
    console.warn('[jobs] pending run tracking unavailable; falling back to SSE', {
      reason,
      error: (error as any)?.message || error,
    });
  }, []);

  const ensurePendingRunsReadable = useCallback(async (conversationId: string): Promise<boolean> => {
    if (!currentUser || !conversationId) return false;
    if (pendingRunsAccessRef.current === 'denied') return false;
    if (pendingRunsAccessRef.current === 'ok') return true;

    try {
      const pendingRunsRef = collection(
        db,
        'users',
        currentUser.uid,
        'conversations',
        conversationId,
        'pendingRuns'
      );
      const accessQuery = query(pendingRunsRef, orderBy('updatedAt', 'desc'), limit(1));
      await getDocs(accessQuery);
      pendingRunsAccessRef.current = 'ok';
      return true;
    } catch (error) {
      if (isPermissionDeniedError(error)) {
        disablePendingRunsTracking('access_check_permission_denied', error);
        return false;
      }
      console.warn('[jobs] pending run access check failed; continuing in jobs mode', (error as any)?.message || error);
      return true;
    }
  }, [currentUser, disablePendingRunsTracking]);

  useEffect(() => {
    pendingRunsAccessRef.current = 'unknown';
  }, [currentUser?.uid]);

  const upsertPendingRunMessage = useCallback(
    (runId: string, conversationId: string, pending: PendingRunDoc) => {
      const status = normalizePendingRunStatus(pending.status);
      if (!status) return;
      const pendingKind = typeof pending.kind === 'string' ? pending.kind : '';
      const pendingType = normalizeClarifyTypeValue(pending.type);
      const clarifyFromPending = pending.clarify && typeof pending.clarify === 'object'
        ? pending.clarify
        : undefined;
      const trackedClarify = clarifyRequestByRunIdRef.current.get(runId);
      const resolvedClarifyRequestId =
        (typeof clarifyFromPending?.requestId === 'string' && clarifyFromPending.requestId.trim())
          ? clarifyFromPending.requestId.trim()
          : trackedClarify?.requestId;
      const resolvedClarifyType =
        normalizeClarifyTypeValue(clarifyFromPending?.type)
        || pendingType
        || trackedClarify?.type;
      const resolvedOriginRunId =
        (typeof clarifyFromPending?.originRunId === 'string' && clarifyFromPending.originRunId.trim())
          ? clarifyFromPending.originRunId.trim()
          : trackedClarify?.originRunId;
      const supersededClarifyRun = typeof pending.clarificationResolvedByRunId === 'string' && pending.clarificationResolvedByRunId.trim()
        ? runId
        : undefined;

      const hasFinalMessageId = typeof pending.finalMessageId === 'string' && pending.finalMessageId.trim().length > 0;
      if (status === 'completed' || (hasFinalMessageId && status !== 'error' && status !== 'expired')) {
        if (hasFinalMessageId) {
          void hydrateCompletedRunMessage(conversationId, runId, pending.finalMessageId!.trim());
        }
        const shouldResolveClarificationQueue = status === 'completed'
          || pendingKind === 'clarification'
          || pendingKind === 'clarification_skip'
          || !!resolvedClarifyRequestId
          || !!resolvedClarifyType
          || !!resolvedOriginRunId
          || !!supersededClarifyRun;
        if (shouldResolveClarificationQueue) {
          updateClarifyQueueForConversation(conversationId, (existingQueue) => {
            if (!existingQueue.length) return existingQueue;

            let nextQueue = existingQueue;
            if (resolvedClarifyRequestId) {
              nextQueue = nextQueue.filter((item) => item.id !== resolvedClarifyRequestId);
            }

            if (resolvedOriginRunId) {
              nextQueue = nextQueue.filter((item) => item.originRunId !== resolvedOriginRunId);
            }

            if (supersededClarifyRun) {
              nextQueue = nextQueue.filter((item) => item.originRunId !== supersededClarifyRun);
            }

            if (resolvedClarifyType && nextQueue.length === existingQueue.length) {
              const matchIndex = nextQueue.findIndex((item) => item.type === resolvedClarifyType);
              if (matchIndex >= 0) {
                nextQueue = nextQueue.filter((_, idx) => idx !== matchIndex);
              }
            }

            if (
              nextQueue.length === existingQueue.length
              && (pendingKind === 'clarification' || pendingKind === 'clarification_skip')
            ) {
              nextQueue = nextQueue.slice(1);
            }

            return nextQueue;
          });
        }
        clarifyRequestByRunIdRef.current.delete(runId);
        clearPendingRunMessage(runId);
        return;
      }

      const timestamp = toMillis(pending.updatedAt || pending.createdAt || Date.now());
      const partialText = typeof pending.partial === 'string' ? pending.partial : '';
      const errorText = typeof pending.error === 'string' ? pending.error : 'Run failed';
      const timeoutReason = typeof pending.timeoutReason === 'string' ? pending.timeoutReason.trim() : '';
      const surfacedError = timeoutReason && !errorText.includes(timeoutReason)
        ? `${errorText} (${timeoutReason})`
        : errorText;
      const defaultContent =
        status === 'queued'
          ? 'Queued for processing…'
          : status === 'needs_clarification'
            ? 'ℹ️ Clarifications requested — please use the form below.'
            : status === 'in_progress'
              ? 'Agent run in progress…'
              : status === 'expired'
                ? 'This run expired before finishing. Please retry.'
                : `Sorry — this run failed (${surfacedError}). Please retry.`;
      const handoff = pending.handoff && typeof pending.handoff === 'object' ? (pending.handoff as PendingRunHandoff) : undefined;
      const liveHandoffStatus = extractLiveStatusText(handoff) || extractLiveStatusText(pending);

      setPendingRunMessages((prev) => {
        const existing = prev[runId];
        const existingSteps = existing?.agentHandoffData?.processingSteps || [];
        const nodeId = normalizeNodeId(handoff?.node)
          || normalizeNodeId(pending?.node)
          || normalizeNodeId(handoff?.agentId)
          || normalizeNodeId(pending?.agentId);
        const fallbackNodeStatus = deriveNodeStatusText(nodeId);
        const rawStepText = liveHandoffStatus || handoff?.step || pending.step;
        const stepText = shouldReplaceGenericStatusWithNode(rawStepText, nodeId)
          ? (fallbackNodeStatus || rawStepText)
          : (rawStepText || fallbackNodeStatus);
        const nextSteps = stepText && !existingSteps.includes(stepText)
          ? [...existingSteps, stepText]
          : existingSteps;
        const explicitAgent = handoff?.agent && typeof handoff.agent === 'object'
          ? resolveHandoffAgent({
              nodeId: handoff.agent.id,
              fallbackName: handoff.agent.name,
              fallbackType: handoff.agent.type,
              fallbackDescription: handoff.agent.description,
              step: stepText,
            })
          : null;
        const inferredAgent = resolveHandoffAgent({
          nodeId:
            (typeof handoff?.agentId === 'string' ? handoff.agentId : undefined)
            || (typeof pending?.agentId === 'string' ? pending.agentId : undefined)
            || (typeof handoff?.node === 'string' ? handoff.node : undefined)
            || (typeof pending?.node === 'string' ? pending.node : undefined),
          fallbackName:
            (typeof handoff?.agentName === 'string' ? handoff.agentName : undefined)
            || (typeof handoff?.agent?.name === 'string' ? handoff.agent.name : undefined)
            || (typeof pending?.agentName === 'string' ? pending.agentName : undefined),
          fallbackType:
            (typeof handoff?.agentType === 'string' ? handoff.agentType : undefined)
            || handoff?.agent?.type
            || pending?.agentType,
          fallbackDescription:
            (typeof handoff?.agentDescription === 'string' ? handoff.agentDescription : undefined)
            || handoff?.agent?.description
            || pending?.agentDescription,
          step: stepText,
        });
        const nextAgent = explicitAgent || inferredAgent;
        const agent = nextAgent
          ? {
              id: nextAgent.id,
              name: nextAgent.name,
              type: nextAgent.type,
              description: nextAgent.description || getAgentDescription(nextAgent.type),
              expertise: getAgentExpertise(nextAgent.type),
            }
          : existing?.agentHandoffData?.selectedAgent || null;
        const phase = (status === 'error' || status === 'expired')
          ? 'complete'
          : handoff?.phase
            || (status === 'queued'
              ? 'handoff'
              : status === 'in_progress' || status === 'needs_clarification'
                ? 'processing'
                : existing?.agentHandoffData?.phase
                  || 'selecting');
        const previousAgent = existing?.agentHandoffData?.selectedAgent;
        const lastHandoff =
          previousAgent && agent && previousAgent.id !== agent.id
            ? {
                fromAgentId: previousAgent.id,
                fromAgentName: previousAgent.name,
                toAgentId: agent.id,
                toAgentName: agent.name,
                timestamp: Date.now(),
              }
            : existing?.agentHandoffData?.lastHandoff;
        const message: Message = {
          id: `pending-${runId}`,
          content: partialText || defaultContent,
          sender: 'ai',
          timestamp,
          isLoading: status === 'queued' || status === 'in_progress',
          messageKind: 'ai_response',
          agentHandoffData: {
            selectedAgent: agent,
            phase,
            processingSteps: nextSteps,
            statusText: stepText || existing?.agentHandoffData?.statusText || defaultContent,
            lastHandoff,
          },
          meta: { ...(existing?.meta || {}), runId, pendingStatus: status },
        };
        return { ...prev, [runId]: message };
      });

      if (status === 'needs_clarification') {
        const pendingQuestions = normalizeClarifyQuestions(clarifyFromPending?.questions);
        const type =
          normalizeClarifyTypeValue(clarifyFromPending?.type)
          || pendingType
          || trackedClarify?.type
          || 'press';
        const questions = pendingQuestions.length ? pendingQuestions : getDefaultClarifyQuestions(type);
        if (questions.length) {
          const requestId = typeof clarifyFromPending?.requestId === 'string' && clarifyFromPending.requestId.trim()
            ? clarifyFromPending.requestId.trim()
            : `clarify-${runId}`;
          const nextRequest: ClarifyRequest = {
            id: requestId,
            type,
            questions,
            agentLabel: typeof clarifyFromPending?.agentLabel === 'string' ? clarifyFromPending.agentLabel : undefined,
            originRunId:
              typeof clarifyFromPending?.originRunId === 'string' && clarifyFromPending.originRunId.trim()
                ? clarifyFromPending.originRunId.trim()
                : runId,
            createdAt: Date.now(),
            locale: (currentConversation?.language as 'en' | 'ar') || flags.localeDefault || 'en',
          };
          clarifyRequestByRunIdRef.current.set(runId, { requestId, type, originRunId: nextRequest.originRunId });
          updateClarifyQueueForConversation(conversationId, (existingQueue) => {
            const withoutCurrent = existingQueue.filter((item) => item.id !== nextRequest.id);
            // Prioritize the newest clarification request so stale hidden entries cannot block the form.
            return [nextRequest, ...withoutCurrent];
          });
        }
      }

      if (status === 'needs_clarification' || status === 'error' || status === 'expired') {
        if (status === 'error' || status === 'expired') {
          clarifyRequestByRunIdRef.current.delete(runId);
        }
        removePendingRunWatcher(runId);
      }
    },
    [
      clearPendingRunMessage,
      currentConversation?.language,
      deriveNodeStatusText,
      flags.localeDefault,
      getAgentDescription,
      getAgentExpertise,
      extractLiveStatusText,
      normalizeNodeId,
      resolveHandoffAgent,
      removePendingRunWatcher,
      shouldReplaceGenericStatusWithNode,
      hydrateCompletedRunMessage,
      updateClarifyQueueForConversation,
    ]
  );

  const watchPendingRun = useCallback(
    (conversationId: string, runId: string) => {
      if (!currentUser || !conversationId || !runId) return;
      if (pendingRunWatchersRef.current.has(runId)) return;

      const pendingRef = doc(
        db,
        'users',
        currentUser.uid,
        'conversations',
        conversationId,
        'pendingRuns',
        runId
      );

      const unsubscribe = onSnapshot(
        pendingRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            return;
          }
          const data = snapshot.data() as PendingRunDoc;
          const snapshotStatus = normalizePendingRunStatus(data?.status) || 'queued';
          if (snapshotStatus === 'error' || snapshotStatus === 'expired') {
            rejectPendingRunHealthWaiter(
              runId,
              new Error(snapshotStatus === 'expired'
                ? 'Clarification resume expired. Please retry.'
                : 'Clarification resume failed. Please retry.'),
            );
          } else if (
            snapshotStatus === 'in_progress'
            || snapshotStatus === 'completed'
            || snapshotStatus === 'needs_clarification'
          ) {
            resolvePendingRunHealthWaiter(runId, snapshotStatus);
          }
          upsertPendingRunMessage(runId, conversationId, { runId, ...data });
        },
        (error) => {
          console.warn('[jobs] pending run listener failed', {
            runId,
            conversationId,
            error: (error as any)?.message || error,
          });
          rejectPendingRunHealthWaiter(runId, new Error('Clarification resume listener failed. Please retry.'));
          if (isPermissionDeniedError(error)) {
            disablePendingRunsTracking('listener_permission_denied', error);
            clearPendingRunMessage(runId);
            return;
          }
          upsertPendingRunMessage(runId, conversationId, {
            runId,
            status: 'error',
            error: 'Listener failed',
            updatedAt: Date.now(),
          });
        }
      );

      pendingRunWatchersRef.current.set(runId, unsubscribe);
    },
    [
      clearPendingRunMessage,
      currentUser,
      disablePendingRunsTracking,
      rejectPendingRunHealthWaiter,
      resolvePendingRunHealthWaiter,
      upsertPendingRunMessage,
    ]
  );

  useEffect(() => {
    if (!flags?.useGraphJobs || !currentUser || !currentConversation?.id) {
      clearPendingRunWatchers();
      return;
    }

    clearPendingRunWatchers();
    let cancelled = false;

    const hydratePendingRuns = async () => {
      if (pendingRunsAccessRef.current === 'denied') return;
      try {
        const pendingRunsRef = collection(
          db,
          'users',
          currentUser.uid,
          'conversations',
          currentConversation.id,
          'pendingRuns'
        );
        const pendingQuery = query(pendingRunsRef, orderBy('updatedAt', 'desc'), limit(25));
        const snapshot = await getDocs(pendingQuery);
        if (cancelled) return;
        pendingRunsAccessRef.current = 'ok';
        snapshot.docs.forEach((pendingDoc) => {
          const data = pendingDoc.data() as PendingRunDoc;
          const status = normalizePendingRunStatus(data?.status);
          if (!status || status === 'completed') return;
          watchPendingRun(currentConversation.id, pendingDoc.id);
          upsertPendingRunMessage(pendingDoc.id, currentConversation.id, {
            runId: pendingDoc.id,
            ...data,
          });
        });
      } catch (error) {
        if (isPermissionDeniedError(error)) {
          disablePendingRunsTracking('hydrate_permission_denied', error);
          return;
        }
        console.warn('[jobs] pending runs hydration failed', (error as any)?.message || error);
      }
    };

    void hydratePendingRuns();

    return () => {
      cancelled = true;
      clearPendingRunWatchers();
    };
  }, [clearPendingRunWatchers, currentConversation?.id, currentUser, disablePendingRunsTracking, flags?.useGraphJobs, upsertPendingRunMessage, watchPendingRun]);

  useEffect(() => {
    const completedRunIds = new Set<string>();
    messages.forEach((msg) => {
      const runIdFromMeta = typeof msg.meta?.runId === 'string' ? msg.meta.runId.trim() : '';
      if (runIdFromMeta) completedRunIds.add(runIdFromMeta);
    });
    if (!completedRunIds.size) return;
    Object.keys(pendingRunMessages).forEach((runId) => {
      if (completedRunIds.has(runId)) {
        clearPendingRunMessage(runId);
      }
    });
  }, [clearPendingRunMessage, messages, pendingRunMessages]);

  // Auto-generate conversation title based on first few messages (AI summary with fallback)
  async function maybeGenerateConversationTitle(conversationId: string, userMessage: string, aiResponse: string): Promise<void> {
    if (!currentUser) return;
    
    try {
      // Check if conversation already has a custom title (not a default placeholder)
      const conversationRef = doc(db, 'users', currentUser.uid, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) return;

      const conversationData = conversationSnap.data();
      const currentTitle = conversationData.title;

      // Only generate title if it's still a default placeholder:
      // - "New Conversation" (no client)
      // - "ClientName – Chat" (client-based default)
      const isDefaultTitle = currentTitle === 'New Conversation' || /^.+\s–\sChat$/.test(currentTitle);
      if (!isDefaultTitle) return;
      
      // Count messages to ensure this is early in the conversation
      const messagesRef = collection(db, 'users', currentUser.uid, 'conversations', conversationId, 'messages');
      const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'), limit(10));
      const messagesSnap = await getDocs(messagesQuery);
      
      // Only generate title if this is one of the first few exchanges (2-6 messages total)
      const messageCount = messagesSnap.docs.length;
      if (messageCount < 2 || messageCount > 6) return;

      // Build a compact context from the first few messages
      const earlyMessages = messagesSnap.docs.map(d => d.data() as any);
      const condensed = earlyMessages
        .slice(0, Math.min(6, earlyMessages.length))
        .map((m: any) => `${m.sender === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
        .join('\n');

      // Try AI-based title summarization first
      let newTitle = '';
      try {
        const clientHint = activeClient?.name ? ` for ${activeClient.name}` : '';
        const prompt = `Create a concise${clientHint} conversation title (3–6 words) summarizing the topic from these early messages. 
Return ONLY the title text without quotes, punctuation at the end, or emojis. Prioritize specificity over generic words.
\n\n${condensed}`;

        const aiTitle = await generateResponse(prompt, {
          messageContext: [],
          conversationId,
          additionalContext: 'Task: Title summarization only. Keep it under 50 characters.'
        });

        newTitle = cleanAndClampTitle(aiTitle);
        // Validate quality; fallback to heuristic if it still looks like a tagline/assistant intro
        const tooManyWords = newTitle.split(/\s+/).length > 8;
        const containsAssistantSpeak = /(i\s*['’]?m|i am|your ai|assistant)/i.test(newTitle);
        if (tooManyWords || containsAssistantSpeak) {
          newTitle = cleanAndClampTitle(
            generateTitleFromContent(userMessage, aiResponse, activeClient)
          );
        }
      } catch (aiTitleErr) {
        console.warn('AI title summarization failed, falling back to heuristic:', aiTitleErr);
        newTitle = cleanAndClampTitle(
          generateTitleFromContent(userMessage, aiResponse, activeClient)
        );
      }
      
      // Update the conversation title
      await updateDoc(conversationRef, {
        title: newTitle,
        updatedAt: serverTimestamp()
      });
      
      // Update local state if this is the current conversation
      if (currentConversation && currentConversation.id === conversationId) {
        setCurrentConversation({
          ...currentConversation,
          title: newTitle,
          updatedAt: serverTimestamp()
        });
      }
      
      console.log(`Auto-generated title for conversation ${conversationId}: "${newTitle}"`);
      
    } catch (error) {
      console.error('Error generating conversation title:', error);
    }
  }
  
  // Clean and clamp AI/heuristic title to format standards
  function cleanAndClampTitle(title: string): string {
    if (!title) return 'PR Discussion';
    let t = String(title).trim();
    
    // Use only the first line to avoid extra explanations
    t = t.split('\n')[0] || t;
    
    // Remove Markdown formatting (bold/italic/code/headers/links)
    t = t.replace(/\*\*(.*?)\*\*/g, '$1')
         .replace(/\*(.*?)\*/g, '$1')
         .replace(/_(.*?)_/g, '$1')
         .replace(/`+/g, '')
         .replace(/^\s*#+\s*/g, '')
         .replace(/\[(.*?)\]\([^)]*\)/g, '$1')
         .replace(/^>\s*/g, '');
    
    // Remove common leading emojis and pictographs
    try {
      t = t.replace(/[\p{Extended_Pictographic}\uFE0F\u200D]+/gu, '');
    } catch {
      // Fallback: strip a set of common emoji ranges if Unicode properties aren't supported
      t = t.replace(/[\u2700-\u27BF\uE000-\uF8FF\uD83C-\uDBFF\uDC00-\uDFFF]+/g, '');
    }
    
    // Remove wrapping quotes and trailing punctuation
    t = t.replace(/^\s*['"`]+|['"`]+\s*$/g, '');
    t = t.replace(/[.!?\s]+$/g, '');
    
    // Remove leading symbols like bullets/dashes
    t = t.replace(/^[\-•–—\s]+/, '');
    
    // Collapse whitespace
    t = t.replace(/\s+/g, ' ');
    
    // If the string looks like a sentence about the assistant, discard
    const lower = t.toLowerCase();
    if (/(i\s*['’]?m|i am|your ai|i can help|i will)/.test(lower)) {
      return 'PR Discussion';
    }
    
    // If after cleaning it's empty or too generic, fallback
    if (!t || t.length < 3) return 'PR Discussion';
    
    // Clamp length to ~50 chars
    if (t.length > 50) t = t.slice(0, 50).trim();
    
    return t || 'PR Discussion';
  }

  // Heuristic fallback: generate a contextual title from the first prompt
  function generateTitleFromContent(userMessage: string, aiResponse: string, client?: Client | null): string {
    const message = userMessage.toLowerCase().trim();
    
    // Client-specific patterns
    if (client) {
      if (message.includes('press release') || message.includes('announcement')) {
        return `${client.name} Press Release`;
      }
      if (message.includes('crisis') || message.includes('urgent') || message.includes('issue')) {
        return `${client.name} Crisis Communication`;
      }
      if (message.includes('media') || message.includes('journalist') || message.includes('interview')) {
        return `${client.name} Media Relations`;
      }
      if (message.includes('social media') || message.includes('social post') || message.includes('twitter') || message.includes('linkedin')) {
        return `${client.name} Social Media`;
      }
      if (message.includes('email') || message.includes('template') || message.includes('pitch')) {
        return `${client.name} Email Template`;
      }
      if (message.includes('content') || message.includes('blog') || message.includes('article')) {
        return `${client.name} Content Strategy`;
      }
      if (message.includes('internal') || message.includes('employee') || message.includes('team')) {
        return `${client.name} Internal Comms`;
      }
    }
    
    // General patterns without client
    if (message.includes('press release') || message.includes('announcement')) {
      return 'Press Release Draft';
    }
    if (message.includes('crisis') || message.includes('urgent') || message.includes('issue')) {
      return 'Crisis Communication';
    }
    if (message.includes('media') || message.includes('journalist') || message.includes('interview')) {
      return 'Media Relations';
    }
    if (message.includes('social media') || message.includes('social post')) {
      return 'Social Media Strategy';
    }
    if (message.includes('email') || message.includes('template') || message.includes('pitch')) {
      return 'Email Template';
    }
    if (message.includes('content') || message.includes('blog') || message.includes('article')) {
      return 'Content Creation';
    }
    if (message.includes('strategy') || message.includes('plan') || message.includes('approach')) {
      return 'PR Strategy Discussion';
    }
    if (message.includes('help') || message.includes('advice') || message.includes('guidance')) {
      return 'PR Consultation';
    }
    
    // Extract topic from both user and assistant signals
    const combined = `${userMessage} ${aiResponse || ''}`.trim();
    const words = combined.split(/\s+/).slice(0, 6);
    let topic = words.join(' ');
    topic = topic.replace(/^(can you|could you|please|i need|help me|how do i|what|how|why)\b/i, '').trim();
    if (topic) return topic.charAt(0).toUpperCase() + topic.slice(1);
    
    return 'PR Discussion';
  }

  // In-Chat Learning functions
  const checkForLearningOpportunity = async (userMessage: string) => {
    if (!activeClient) return; // Ensure a client is active

    try {
      const analyzeInteraction = httpsCallable(functions, 'analyzeInteractionForLearning');
      const result = await analyzeInteraction({
        message: userMessage,
        clientId: activeClient.id,
        clientName: activeClient.name,
      });

      if (result.data) {
        setLearningSuggestion(result.data as LearningSuggestionState);
      }
    } catch (error) {
      console.error("Could not check for learning opportunity via callable:", error);
      // Fallback for local dev: call Next API to avoid CORS
      try {
        if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
          const res = await fetch('/api/learning/analyze', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message: userMessage,
              clientId: activeClient.id,
              clientName: activeClient.name,
            })
          });
          if (res.ok) {
            const json = await res.json();
            if (json?.suggestion) {
              setLearningSuggestion(json.suggestion as LearningSuggestionState);
            }
          }
        }
      } catch (e) {
        console.warn('Learning fallback failed:', e);
      }
    }
  };

  const updateClientProfileField = async (clientId: string, updateType: string, suggestedValue: string) => {
    if (!activeClient) return;

    // Create the update object based on the updateType
    const updates: any = {};

    switch (updateType) {
      case 'brandTone':
        updates.context = {
          ...activeClient.context,
          brandTone: suggestedValue
        };
        break;
      case 'keyMessage':
        const newKeyMessages = [...(activeClient.context.keyMessages || []), suggestedValue];
        updates.context = {
          ...activeClient.context,
          keyMessages: newKeyMessages
        };
        break;
      case 'forbiddenLanguage':
        const newForbiddenLanguage = [...(activeClient.context.forbiddenLanguage || []), suggestedValue];
        updates.context = {
          ...activeClient.context,
          forbiddenLanguage: newForbiddenLanguage
        };
        break;
      case 'approvedLanguage':
        const newApprovedLanguage = [...(activeClient.context.approvedLanguage || []), suggestedValue];
        updates.context = {
          ...activeClient.context,
          approvedLanguage: newApprovedLanguage
        };
        break;
      case 'factCorrection':
        // For fact corrections, we could store them in notes or a special field
        const currentNotes = activeClient.context.notes || '';
        const factCorrection = `${currentNotes}\n\nFact Correction: ${suggestedValue}`.trim();
        updates.context = {
          ...activeClient.context,
          notes: factCorrection
        };
        break;
      default:
        console.warn('Unknown update type:', updateType);
        return;
    }

    // Update the client using the useClientManagement hook
    await updateClient(clientId, updates);
  };

  const handleConfirmLearning = async () => {
    if (!learningSuggestion || !activeClient) return;

    // Update the client profile
    await updateClientProfileField(
      activeClient.id,
      learningSuggestion.updateType,
      learningSuggestion.suggestedValue
    );

    // Also persist a vector memory for LangGraph retrieval
    try {
      const makeMemoryText = () => {
        switch (learningSuggestion.updateType) {
          case 'brandTone':
            return `Brand tone preference for ${activeClient.name}: ${learningSuggestion.suggestedValue}`;
          case 'keyMessage':
            return `Key message for ${activeClient.name}: ${learningSuggestion.suggestedValue}`;
          case 'forbiddenLanguage':
            return `Avoided language for ${activeClient.name}: ${learningSuggestion.suggestedValue}`;
          case 'approvedLanguage':
            return `Preferred language for ${activeClient.name}: ${learningSuggestion.suggestedValue}`;
          case 'factCorrection':
            return `Fact for ${activeClient.name}: ${learningSuggestion.suggestedValue}`;
          default:
            return `${activeClient.name}: ${learningSuggestion.suggestedValue}`;
        }
      };
      const text = makeMemoryText();
      const embed = httpsCallable(functions as any, 'embedMemory');
      // Detect script to assign memory language (ar/en) for better retrieval by translation agent
      const containsArabic = /[\u0600-\u06FF]/.test(learningSuggestion.suggestedValue || '') || /[\u0600-\u06FF]/.test(text);
      const memoryLanguage = containsArabic ? 'ar' : 'en';

      await embed({
        userId: currentUser!.uid,
        clientId: activeClient.id,
        text,
        type: 'client_preference',
        tags: [activeClient.id],
        language: memoryLanguage,
      });
    } catch (e) {
      console.warn('Embedding memory failed (non-blocking):', e);
    }

    // Add a confirmation message to the chat
    const confirmationMessage = {
      id: `learn_${Date.now()}`,
      content: `✅ Profile updated. I'll remember that for ${activeClient.name}.`,
      sender: 'ai' as const,
      timestamp: serverTimestamp()
    };

    if (currentConversation) {
      const messageRef = collection(
        db, 
        'users', 
        currentUser!.uid, 
        'conversations', 
        currentConversation.id, 
        'messages'
      );
      
      await addDoc(messageRef, confirmationMessage);
    }

    setLearningSuggestion(null); // Clear the suggestion
  };

  const handleDismissLearning = () => {
    setLearningSuggestion(null); // Just clear the suggestion
  };

  async function sendMessage(content: string, specificConversationId?: string, options?: SendMessageOptions): Promise<void> {
    // Verify auth state before proceeding
    const isAuthenticated = await verifyAuthState();
    
    if (!isAuthenticated || !currentUser) {
      throw new Error("No authenticated user");
    }
    
    // Use the specified conversationId if provided, otherwise use currentConversation
    let conversationId = specificConversationId || (currentConversation?.id);
    
    // Lazy-create a conversation if none exists yet (fresh page load)
    if (!conversationId) {
      try {
        const title = activeClient?.name ? `${activeClient.name} – Chat` : 'New Conversation';
        const newConversationId = await createNewConversation(title, activeClient?.id);
        await loadConversation(newConversationId);
        conversationId = newConversationId;
      } catch (e) {
        console.error('Failed to create a new conversation before sending message', e);
        throw new Error("No active conversation");
      }
    }

    const hasPendingClientOverride = () => (
      !!conversationId
      && Object.prototype.hasOwnProperty.call(pendingConversationClientRef.current, conversationId)
    );

    let authoritativeClientId: string | undefined = (() => {
      if (!conversationId) return normalizeString(activeClient?.id) || undefined;
      if (hasPendingClientOverride()) {
        return pendingConversationClientRef.current[conversationId] || undefined;
      }
      if (currentConversation?.id === conversationId) {
        return normalizeString(currentConversation.clientId) || undefined;
      }
      return normalizeString(activeClient?.id) || undefined;
    })();

    const refreshAuthoritativeClientId = async (): Promise<string | undefined> => {
      if (!conversationId) return authoritativeClientId;

      if (hasPendingClientOverride()) {
        authoritativeClientId = pendingConversationClientRef.current[conversationId] || undefined;
        return authoritativeClientId;
      }

      try {
        const conversationDoc = await getDoc(doc(db, 'users', currentUser.uid, 'conversations', conversationId));
        if (!conversationDoc.exists()) return authoritativeClientId;

        const conversationData = conversationDoc.data();
        const docClientId = normalizeString(conversationData.clientId);
        authoritativeClientId = docClientId || undefined;

        if (docClientId && (!activeClient || activeClient.id !== docClientId)) {
          try {
            const clientDocRef = doc(db, 'clients', docClientId);
            const clientSnap = await getDoc(clientDocRef);

            if (clientSnap.exists()) {
              const clientData = { id: clientSnap.id, ...clientSnap.data() } as Client;
              setActiveClient(clientData);
            }
          } catch (error) {
            console.error("Error fetching client data:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching conversation details:", error);
      }

      return authoritativeClientId;
    };
    
    setLoading(true);
    const conversationLocale = (currentConversation?.language as 'en' | 'ar') || flags.localeDefault || 'en';
    await refreshAuthoritativeClientId();
    const saveAsPreference = options?.saveAsPreference === true;
    if (saveAsPreference) {
      const targetClientId = authoritativeClientId;
      if (targetClientId) {
        try {
          const updates = extractPreferenceUpdates(content, conversationLocale);
          if (Object.keys(updates).length) {
            const saved = await updateClientPreferences(targetClientId, updates);
            if (!saved) {
              try { (toast as any)?.error?.('Unable to save client preferences.'); } catch {}
            }
          }
        } catch (error) {
          console.warn('[preferences] extract_failed', (error as any)?.message || error);
        }
      }
    }

    const heuristicsClarifyMatch = /^\s*Clarifications\s+for\b/i.test(content) || /placeholders/i.test(content);
    const heuristicsClarify = heuristicsClarifyMatch && !!clarifyPending;
    const fallbackClarificationMeta: ClarificationSubmissionMeta | undefined = heuristicsClarify && clarifyPending
      ? {
          requestId: clarifyPending.id,
          type: clarifyPending.type,
          questions: clarifyPending.questions,
          answers: [],
          placeholderCount: 0,
          compiledText: content,
          originRunId: clarifyPending.originRunId,
        }
      : undefined;
    const baseMessageKind: SendMessageKind = options?.kind || (heuristicsClarify ? 'clarification' : 'message');
    const isClarifySkip = baseMessageKind === 'clarification_skip';
    const clarificationMeta = options?.clarification || fallbackClarificationMeta;
    const artifactFeedbackMeta = (() => {
      const raw = options?.artifactFeedback;
      if (!raw || typeof raw !== 'object') return undefined;
      const artifactId = typeof raw.artifactId === 'string' ? raw.artifactId.trim() : '';
      if (!artifactId) return undefined;
      return {
        artifactId,
        ...(typeof raw.lineageId === 'string' && raw.lineageId.trim() ? { lineageId: raw.lineageId.trim() } : {}),
        ...(typeof raw.runId === 'string' && raw.runId.trim() ? { runId: raw.runId.trim() } : {}),
        ...(typeof raw.promptMessageId === 'string' && raw.promptMessageId.trim() ? { promptMessageId: raw.promptMessageId.trim() } : {}),
        ...(raw.action ? { action: raw.action } : {}),
      } satisfies ArtifactFeedbackMeta;
    })();
    if (baseMessageKind === 'clarification' && clarificationMeta) {
      clarificationAckRef.current = clarificationMeta;
    }

    const attachedArtifactContext = (() => {
      const artifact = options?.contextAttachments?.artifact;
      if (!artifact || typeof artifact !== 'object') return undefined;
      const id = typeof artifact.id === 'string' ? artifact.id.trim() : '';
      if (!id) return undefined;
      const title = typeof artifact.title === 'string' && artifact.title.trim()
        ? artifact.title.trim()
        : 'Artifact';
      const type = typeof artifact.type === 'string' && artifact.type.trim()
        ? artifact.type.trim()
        : 'content';
      return {
        id,
        title,
        type,
        ...(typeof artifact.content === 'string' && artifact.content.trim()
          ? { content: artifact.content.slice(0, 10000) }
          : {}),
        ...(typeof artifact.artifactKey === 'string' && artifact.artifactKey.trim()
          ? { artifactKey: artifact.artifactKey.trim() }
          : {}),
        ...(typeof artifact.lineageId === 'string' && artifact.lineageId.trim()
          ? { lineageId: artifact.lineageId.trim() }
          : {}),
        ...(typeof artifact.runId === 'string' && artifact.runId.trim()
          ? { runId: artifact.runId.trim() }
          : {}),
        ...(typeof artifact.checkpointId === 'string' && artifact.checkpointId.trim()
          ? { checkpointId: artifact.checkpointId.trim() }
          : {}),
      } as MessageAttachmentArtifactContext;
    })();

    const attachedSourceDocumentIds = Array.from(new Set(
      (Array.isArray(options?.contextAttachments?.sourceDocumentIds)
        ? options?.contextAttachments?.sourceDocumentIds
        : [])
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean),
    ));

    const attachedSourceDocuments = (Array.isArray(options?.contextAttachments?.sourceDocuments)
      ? options?.contextAttachments?.sourceDocuments
      : []
    )
      .map((doc) => {
        if (!doc || typeof doc !== 'object') return null;
        const id = typeof doc.id === 'string' ? doc.id.trim() : '';
        if (!id || !attachedSourceDocumentIds.includes(id)) return null;
        const title = typeof doc.title === 'string' && doc.title.trim()
          ? doc.title.trim()
          : 'Document';
        const type = typeof doc.type === 'string' && doc.type.trim() ? doc.type.trim() : undefined;
        const url = typeof doc.url === 'string' && doc.url.trim() ? doc.url.trim() : undefined;
        return {
          id,
          title,
          ...(type ? { type } : {}),
          ...(url ? { url } : {}),
        } as MessageAttachmentDocumentContext;
      })
      .filter((doc): doc is MessageAttachmentDocumentContext => !!doc);

    const attachedArtifactStrict = attachedArtifactContext
      ? options?.contextAttachments?.artifactStrict === true
      : false;

    const requestContextAttachments: SendMessageContextAttachments | undefined =
      attachedArtifactContext || attachedSourceDocumentIds.length
        ? {
            ...(attachedArtifactContext ? { artifact: attachedArtifactContext } : {}),
            ...(attachedArtifactContext && attachedArtifactStrict ? { artifactStrict: true } : {}),
            ...(attachedSourceDocumentIds.length ? { sourceDocumentIds: attachedSourceDocumentIds } : {}),
            ...(attachedSourceDocuments.length ? { sourceDocuments: attachedSourceDocuments } : {}),
          }
        : undefined;
    
    const messageRef = collection(
      db, 
      'users', 
      currentUser.uid, 
      'conversations', 
      conversationId, 
      'messages'
    );

    const userMessageMeta: Message['meta'] = {
      ...(requestContextAttachments ? { contextAttachments: requestContextAttachments } : {}),
      ...(artifactFeedbackMeta ? { artifactFeedback: artifactFeedbackMeta } : {}),
    };
    
    const userMessage: Message = {
      content,
      sender: 'user',
      timestamp: serverTimestamp(),
      ...(baseMessageKind !== 'message' ? { messageKind: baseMessageKind } : {}),
      ...(clarificationMeta ? { clarification: clarificationMeta } : {}),
      ...(Object.keys(userMessageMeta).length ? { meta: userMessageMeta } : {}),
    };
    
    await addDoc(messageRef, userMessage as any);
    if ((baseMessageKind === 'clarification' || baseMessageKind === 'clarification_skip') && clarificationMeta?.type && conversationId) {
      const storedText = clarificationMeta.skipped
        ? buildClarifySkipToken(clarificationMeta.type)
        : (clarificationMeta.compiledText || content);
      await persistClarificationEntry(conversationId, clarificationMeta.type, {
        text: storedText,
        placeholderCount: clarificationMeta.placeholderCount ?? 0,
        skipped: !!clarificationMeta.skipped,
        language: conversationLocale,
      });
    }
    
    // Update conversation with last message
    const conversationRef = doc(
      db, 
      'users', 
      currentUser.uid, 
      'conversations', 
      conversationId
    );
    
    await updateDoc(conversationRef, {
      lastMessage: content.substring(0, 100) + (content.length > 100 ? '...' : ''),
      updatedAt: serverTimestamp(),
      hasUserMessage: true,
      messageCount: increment(1)
    });

    if (options?.skipAgentRun === true) {
      return;
    }
    
    // Phase 1: Adding a temporary AI message with agent selection animation
    const resumeOriginRunId = (baseMessageKind === 'clarification' || baseMessageKind === 'clarification_skip')
      && typeof clarificationMeta?.originRunId === 'string'
      ? clarificationMeta.originRunId.trim()
      : '';
    const resumeTargetMessage = resumeOriginRunId
      ? (() => {
          for (let index = messages.length - 1; index >= 0; index -= 1) {
            const message = messages[index];
            const runId = typeof (message as any)?.meta?.runId === 'string'
              ? String((message as any).meta.runId).trim()
              : '';
            const messageId = typeof (message as any)?.id === 'string' ? String((message as any).id) : '';
            const isMatch = (
              (message as any)?.sender === 'ai'
              && !!(message as any)?.agentHandoffData
              && (
                runId === resumeOriginRunId
                || messageId === resumeOriginRunId
                || messageId === `pending-${resumeOriginRunId}`
              )
            );
            if (isMatch) return message;
          }
          return undefined;
        })()
      : undefined;
    const tempMessageId = resumeTargetMessage?.id || `temp-${Date.now()}`;
    const tempMessage: Message = {
      id: tempMessageId,
      content: '',
      sender: 'ai',
      timestamp: Date.now(),
      isLoading: true,
      agentHandoffData: {
        selectedAgent: null,
        phase: 'selecting',
        processingSteps: []
      },
      ...(resumeOriginRunId ? { meta: { runId: resumeOriginRunId } } : {})
    };
    
    if (resumeTargetMessage?.id) {
      setMessages(prev => prev.map((msg) => {
        if (msg.id !== tempMessageId) return msg;
        const current = msg.agentHandoffData || {
          selectedAgent: null,
          phase: 'selecting',
          processingSteps: [] as string[],
        };
        const resumeStep = baseMessageKind === 'clarification_skip'
          ? 'Continuing with placeholders'
          : 'Clarifications received';
        return {
          ...msg,
          content: '',
          isLoading: true,
          agentHandoffData: {
            ...current,
            phase: 'handoff',
            processingSteps: current.processingSteps.includes(resumeStep)
              ? current.processingSteps
              : [...current.processingSteps, resumeStep],
            statusText: resumeStep,
          },
          meta: {
            ...(msg.meta || {}),
            ...(resumeOriginRunId ? { runId: resumeOriginRunId } : {}),
          },
        } as Message;
      }));
    } else {
      setMessages(prev => [...prev, tempMessage]);
    }
    
    try {
      // If this conversation has a client associated with it, ensure we have the client loaded
      // Get the conversation details to check for the authoritative client ID
      await refreshAuthoritativeClientId();

      // Phase 4+5: Optional LangGraph SSE path via Hosting rewrite + feature flags
      try {
        const useLangGraph = !!flags?.useLangGraph;
        const trafficPct = Number(flags?.langGraphTrafficPercent ?? 100);
        const forceGraph = (process.env.NEXT_PUBLIC_FORCE_LANGGRAPH || 'false') === 'true';
        // PHASE-10 START
        // LangGraph-only cutover: keep telemetry and route only to graph runtime.
        const routingDecision = resolveRuntimeRouting({
          forceGraph,
          useLangGraph,
          trafficPct,
          emergencyLegacyFallback: false,
        });
        // Log adoption decision
        try {
          await addDoc(collection(db as any, 'agentRuns'), {
            task: 'chat_gate',
            method: routingDecision.method,
            useLangGraph,
            pct: trafficPct,
            disableLegacyChat: routingDecision.disableLegacyChat,
            userId: currentUser?.uid || null,
            conversationId: conversationId || null,
            createdAt: serverTimestamp(),
          } as any);
        } catch {}
        // PHASE-10 END

        if (routingDecision.routeToGraph) {
          let lastDraft: string | undefined;
          let streamingBuffer = '';

          let hadRefine = false;
          let hadFinalReview = false;
          // Provide previous content (last generated text for this conversation) if available
          const prevContent = ((): string | undefined => {
            try {
              const reversed = [...messages].reverse();
              const lastArtifactRef = reversed.find((m) => (m as any).isArtifactReference && !!(m as any).artifactContent);
              if (lastArtifactRef) return (lastArtifactRef as any).artifactContent;
              const MIN_PREV_CHARS = 200;
              const lastAiDraft = reversed.find((m) => {
                if ((m as any).sender !== 'ai' || (m as any).isArtifactReference) return false;
                const kind = (m as any).messageKind;
                if (kind === 'clarification' || kind === 'clarification_skip') return false;
                const hasDraftMarker =
                  kind === 'ai_response' ||
                  !!(m as any).artifact ||
                  !!((m as any).meta && ((m as any).meta.provenance || (m as any).meta.memories));
                if (hasDraftMarker) return !!((m as any).content || (m as any).text);
                const text = ((m as any).content || (m as any).text || '') as string;
                return text.trim().length >= MIN_PREV_CHARS;
              });
              if (lastAiDraft) {
                return (lastAiDraft as any).content || (lastAiDraft as any).text;
              }
            } catch {}
            return undefined;
          })();

          // Combine the original user ask with clarifications so the agent keeps task context
          const lastUserAsk = (() => {
            try {
              const reversed = [...messages].reverse();
              const u = reversed.find(m => (m as any).sender === 'user' && !!((m as any).content || (m as any).text));
              return u ? ((u as any).content || (u as any).text) : undefined;
            } catch { return undefined; }
          })();

          const isClarifySubmission = baseMessageKind === 'clarification';
          const clarifyContext = clarificationMeta;
          const taskOverride = clarifyContext?.type
            ? clarifyContext.type
            : undefined;
          // PHASE-02 START
          let runMemories: MemoryReference[] = [];
          let runMeta: RunMeta | null = null;
          // PHASE-02 END
          const conversationLanguage = (currentConversation?.language as 'en' | 'ar') || flags.localeDefault || 'en';
          const requestKind = isClarifySkip
            ? 'clarification_skip'
            : isClarifySubmission
              ? 'clarification'
              : 'message';
          const requestResume = isClarifySkip || isClarifySubmission;
          const clarificationsTextPayload = isClarifySkip
            ? buildClarifySkipToken(clarifyContext?.type || 'unknown')
            : isClarifySubmission
              ? (clarifyContext?.compiledText || content)
              : undefined;
          const shouldReuseStoredClarifications = requestResume;
          const { text: clarificationsTextCombined, reusedTypes } = conversationId
            ? (shouldReuseStoredClarifications
                ? composeClarificationsPayload(conversationId, clarificationsTextPayload, clarifyContext?.type)
                : { text: clarificationsTextPayload, reusedTypes: [] as ClarifyType[] })
            : { text: clarificationsTextPayload, reusedTypes: [] as ClarifyType[] };
          if (!clarificationsTextPayload && reusedTypes.length) {
            try {
              const label = reusedTypes
                .map((type) =>
                  type === 'generic'
                    ? 'general details'
                    : type === 'press'
                      ? 'press release details'
                      : type === 'pitch'
                        ? 'pitch details'
                        : type === 'social'
                          ? 'social details'
                          : 'research preferences'
                )
                .join(', ');
              (toast as any)?.info?.(`Reusing saved ${label} from earlier in this chat.`);
            } catch {}
          }
          const runInput = isClarifySubmission
            ? `${(lastUserAsk || '').trim()}\n\n${clarifyContext?.compiledText || content}`.trim()
            : (isClarifySkip && lastUserAsk)
              ? lastUserAsk
              : content;
          const runLanguage = resolveRunLanguage(conversationLanguage, runInput);
          const explicitPreviousContent = typeof options?.previousContent === 'string' && options.previousContent.trim()
            ? options.previousContent
            : undefined;
          const hasArtifactRevisionContext =
            !!requestContextAttachments?.artifact || !!artifactFeedbackMeta?.artifactId;
          const includePreviousContent = hasArtifactRevisionContext || isLikelyEditContinuation(runInput);
          const shouldPreferAttachedPointer =
            requestContextAttachments?.artifactStrict === true
            && !!requestContextAttachments?.artifact;
          const previousContentForRun = shouldPreferAttachedPointer
            ? explicitPreviousContent
            : (explicitPreviousContent || (includePreviousContent ? prevContent : undefined));
          const requestMetaForRun = (() => {
            const requestMeta: Record<string, unknown> = {};
            if (requestContextAttachments?.sourceDocumentIds?.length) {
              requestMeta.requestedSourceDocumentIds = requestContextAttachments.sourceDocumentIds;
            }
            if (requestContextAttachments?.sourceDocuments?.length) {
              requestMeta.requestedSourceDocuments = requestContextAttachments.sourceDocuments;
            }
            if (requestContextAttachments?.artifact) {
              requestMeta.attachedArtifact = requestContextAttachments.artifact;
              if (requestContextAttachments.artifactStrict === true) {
                requestMeta.attachedArtifactStrict = true;
              }
            }
            if (artifactFeedbackMeta) {
              requestMeta.messageKind = 'artifact_feedback_response';
              requestMeta.artifactFeedback = artifactFeedbackMeta;
            }
            return Object.keys(requestMeta).length ? requestMeta : undefined;
          })();
          const smallTalkOnly = requestKind === 'message' && isSmallTalkMessage(runInput);

          if (smallTalkOnly) {
            const normalizedInput = runInput.trim().replace(/\s+/g, ' ').toLowerCase();
            const isArtifactBoundFeedback = !!artifactFeedbackMeta?.artifactId;
            const hasDraftReference = /\b(draft|response|output|version|content|article|release|this|it|that)\b/.test(normalizedInput);
            const hasPositiveMarker = /\b(ok|okay|good|great|fine|solid|works|approved|perfect|looks good|looks great|better|much better|improved|looks better)\b/.test(normalizedInput);
            const hasNegativeMarker =
              /\bnot\s+(good|ok|okay|great|clear|right|ready)\b/.test(normalizedInput)
              || /\b(bad|poor|weak|off|wrong|confusing|unclear)\b/.test(normalizedInput)
              || /\b(needs revision|needs revisions|needs changes|not ready)\b/.test(normalizedInput);
            const feedbackSignal: 'positive' | 'negative' | null = hasDraftReference
              ? (hasNegativeMarker ? 'negative' : hasPositiveMarker ? 'positive' : null)
              : null;
            const hasThanks = /\b(thanks|thank you|thx)\b/.test(normalizedInput);
            const looksLikeFeedbackAck = feedbackSignal !== null || hasThanks || hasPositiveMarker;
            const quickReply = feedbackSignal === 'negative'
              ? (runLanguage === 'ar'
                ? 'شكرًا على الملاحظة. فهمت أن هذه النسخة تحتاج تعديل. حدّد لي التغييرات المطلوبة وسأراجعها فورًا.'
                : "Thanks for the feedback. Noted that this needs changes. Tell me what to adjust and I’ll revise it.")
              : looksLikeFeedbackAck
                ? (runLanguage === 'ar'
                  ? 'شكرًا على الملاحظة، تم تسجيلها. إذا أردت، أقدر أحسّن النسخة أكثر.'
                  : "Thanks for the feedback, noted. If you'd like, I can still refine this draft further.")
                : (runLanguage === 'ar'
                  ? 'مرحبًا! أنا جاهز للمساعدة. أخبرني بما تريد كتابته أو مراجعته.'
                  : "Hi! I'm ready to help. Tell me what you'd like to draft, review, or improve.");

            if ((feedbackSignal || looksLikeFeedbackAck) && isArtifactBoundFeedback) {
              const latestAiMessage = [...messages]
                .reverse()
                .find((msg) =>
                  msg.sender === 'ai'
                  && !!msg.id
                  && !String(msg.id).startsWith('temp-')
                  && !String(msg.id).startsWith('pending-')
                );
              if (latestAiMessage?.id) {
                if (feedbackSignal) {
                  void handleMessageFeedback(latestAiMessage.id, {
                    rating: feedbackSignal,
                    satisfaction: feedbackSignal === 'positive' ? 5 : 2,
                    details: runInput,
                  });
                }
                void (async () => {
                  try {
                    const token = await currentUser.getIdToken();
                    const runId = typeof (latestAiMessage as any)?.meta?.runId === 'string'
                      ? String((latestAiMessage as any).meta.runId)
                      : undefined;
                    const memoryRefs = Array.isArray((latestAiMessage as any)?.meta?.memories)
                      ? ((latestAiMessage as any).meta.memories as any[])
                      : [];
                    const memoryIds = Array.from(
                      new Set(
                        memoryRefs
                          .map((memory: any) => {
                            if (typeof memory === 'string' && memory.trim()) return memory.trim();
                            if (memory && typeof memory === 'object' && typeof memory.id === 'string' && memory.id.trim()) {
                              return memory.id.trim();
                            }
                            return '';
                          })
                          .filter(Boolean),
                      ),
                    );
                    if (feedbackSignal) {
                      const response = await fetch('/api/graph/feedback/thumbs', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({
                          messageId: latestAiMessage.id,
                          runId,
                          thumbs: feedbackSignal === 'positive' ? 'up' : 'down',
                          memoryIds,
                        }),
                      });
                      if (!response.ok) {
                        const errorText = await response.text().catch(() => '');
                        console.warn('[feedback] auto_feedback_graph_sync_failed', {
                          status: response.status,
                          error: errorText,
                          messageId: latestAiMessage.id,
                          runId,
                        });
                      }
                    }

                    const textResponse = await fetch('/api/graph/feedback/text', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                      },
                      body: JSON.stringify({
                        messageId: latestAiMessage.id,
                        runId,
                        feedbackText: runInput,
                      }),
                    });
                    if (!textResponse.ok) {
                      const errorText = await textResponse.text().catch(() => '');
                      console.warn('[feedback] auto_text_feedback_graph_sync_failed', {
                        status: textResponse.status,
                        error: errorText,
                        messageId: latestAiMessage.id,
                        runId,
                      });
                    }
                  } catch (error) {
                    console.warn('[feedback] auto_feedback_graph_sync_error', (error as any)?.message || error);
                  }
                })();
              }
            }

            setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId));
            await addDoc(messageRef, {
              content: quickReply,
              sender: 'ai',
              timestamp: serverTimestamp(),
              messageKind: 'ai_response',
            } as any);
            return;
          }

          const canUseGraphJobs = flags?.useGraphJobs
            ? await ensurePendingRunsReadable(conversationId)
            : false;
          if (canUseGraphJobs) {
            try {
              if (!conversationId) throw new Error('missing_conversation_id');
              const { runGraphJob } = await import('@/lib/graph/runGraphJob');
              const job = await runGraphJob({
                input: runInput,
                userId: currentUser.uid,
                clientId: authoritativeClientId,
                conversationId,
                threadId: conversationId,
                language: runLanguage,
                task: taskOverride,
                previousContent: previousContentForRun,
                clarificationsText: clarificationsTextCombined,
                meta: requestMetaForRun,
                kind: requestKind,
                type: clarifyContext?.type,
                resume: requestResume,
                resumeFromRunId: clarifyContext?.originRunId,
              });

              if (!job.ok || !job.runId) {
                throw new Error(job.error || 'jobs_run_failed');
              }

              setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId));
              const seededStatus = normalizePendingRunStatus(job.status) || 'queued';
              upsertPendingRunMessage(job.runId, conversationId, {
                runId: job.runId,
                status: seededStatus,
                handoff: { phase: 'handoff', step: 'Routing request' },
                updatedAt: Date.now(),
              });
              const pendingRunHealthPromise = requestResume && clarifyContext?.requestId
                ? waitForPendingRunHealth(job.runId)
                : null;
              watchPendingRun(conversationId, job.runId);
              if (pendingRunHealthPromise && clarifyContext?.requestId) {
                try {
                  await pendingRunHealthPromise;
                } catch (resumeError) {
                  clearPendingRunMessage(job.runId);
                  const nextError = new Error((resumeError as Error)?.message || 'Clarification resume timed out. Please retry.');
                  (nextError as Error & { preventSseFallback?: boolean }).preventSseFallback = true;
                  throw nextError;
                }
                updateClarifyQueueForConversation(conversationId, (prev) =>
                  prev.filter((req) => req.id !== clarifyContext.requestId)
                );
              }
              return;
            } catch (jobsError) {
              if ((jobsError as Error & { preventSseFallback?: boolean })?.preventSseFallback) {
                throw jobsError;
              }
              console.warn('[jobs] submission failed; falling back to SSE', (jobsError as any)?.message || jobsError);
            }
          }

          const final = await runGraphSSE({
            input: runInput,
            userId: currentUser.uid,
            clientId: authoritativeClientId,
            threadId: conversationId,
            language: runLanguage,
            task: taskOverride,
            previousContent: previousContentForRun,
            clarificationsText: clarificationsTextCombined,
            meta: requestMetaForRun,
            kind: requestKind,
            type: clarifyContext?.type,
            resume: requestResume,
            resumeFromRunId: clarifyContext?.originRunId,
            onEvent: ({ event, data }) => {
              // Helper to update handoff UI live
              const applyHandoff = (update: {
                phase?: 'selecting'|'handoff'|'processing'|'complete';
                step?: string;
                agent?: { id: string; name: string; type: 'crisis'|'media'|'social'|'executive'|'content'|'general'; description: string };
              }) => {
                setMessages(prev => prev.map(msg => {
                  if (msg.id !== tempMessageId) return msg;
                  const current = msg.agentHandoffData || { selectedAgent: null, phase: 'selecting', processingSteps: [] as string[] };
                  const steps = [...current.processingSteps];
                  if (update.step && !steps.includes(update.step)) steps.push(update.step);
                  const selectedAgent = update.agent && shouldRenderHandoffAgent(update.agent.id)
                    ? {
                        id: update.agent.id,
                        name: update.agent.name,
                        type: update.agent.type,
                        description: update.agent.description,
                        expertise: getAgentExpertise(update.agent.type)
                      }
                    : current.selectedAgent;
                  const lastHandoff =
                    current.selectedAgent &&
                    selectedAgent &&
                    current.selectedAgent.id !== selectedAgent.id
                      ? {
                          fromAgentId: current.selectedAgent.id,
                          fromAgentName: current.selectedAgent.name,
                          toAgentId: selectedAgent.id,
                          toAgentName: selectedAgent.name,
                          timestamp: Date.now(),
                        }
                      : current.lastHandoff;
                  return {
                    ...msg,
                    agentHandoffData: {
                      selectedAgent,
                      phase: update.phase || current.phase,
                      processingSteps: steps,
                      statusText: update.step || current.statusText,
                      lastHandoff,
                    }
                  } as Message;
                }));
              };

              // Helpers to format structured work into readable text
              const formatPitch = (p: any): string => {
                if (!p) return '';
                const subjects = Array.isArray(p.subjects) ? p.subjects : [];
                const subjBlock = subjects.length
                  ? `Subject options:\n${subjects.map((s: string, i: number) => `- ${s}`).join('\n')}`
                  : '';
                const angleBlock = Array.isArray(p.angleReasons) && p.angleReasons.length
                  ? `\nReasons:\n${p.angleReasons.map((r: string) => `- ${r}`).join('\n')}`
                  : '';
                const follow = p.followUp ? `\n\nFollow-up:\n${p.followUp}` : '';
                return [subjBlock, p.pitch ? `\nPitch:\n${p.pitch}` : '', angleBlock, follow].filter(Boolean).join('\n');
              };
              const formatSocial = (s: any): string => {
                if (!s) return '';
                const xBlock = Array.isArray(s.xThread) && s.xThread.length
                  ? `X Thread:\n${s.xThread.map((t: string, i: number) => `${i + 1}. ${t}`).join('\n')}`
                  : '';
                const liBlock = s.linkedin ? `\n\nLinkedIn:\n${s.linkedin}` : '';
                const igBlock = s.instagram ? `\n\nInstagram:\n- Caption: ${s.instagram.caption || ''}\n- Alt Text: ${s.instagram.altText || ''}` : '';
                return [xBlock, liBlock, igBlock].filter(Boolean).join('');
              };
              // PHASE-04 START
              const formatRagSources = (sources: any): string => {
                if (!Array.isArray(sources) || !sources.length) return '';
                return sources
                  .map((s: any, idx: number) => {
                    const title = s.title || s.docId || `Source ${idx + 1}`;
                    const page = s.page ? ` p.${s.page}` : '';
                    const url = s.url ? ` — ${s.url}` : '';
                    const snippet = typeof s.text === 'string' && s.text.trim()
                      ? s.text.trim().slice(0, 320) + (s.text.trim().length > 320 ? '…' : '')
                      : '';
                    return `[${idx + 1}] ${title}${page}${url}${snippet ? `\n   ${snippet}` : ''}`;
                  })
                  .join('\n');
              };
              const formatResearch = (pack: any): string => {
                if (!pack) return '';
                const sources = Array.isArray(pack.sources) ? pack.sources : [];
                const angles = Array.isArray(pack.angles) ? pack.angles : [];
                const facts = Array.isArray(pack.facts) ? pack.facts : [];
                const risks = Array.isArray(pack.risks) ? pack.risks : [];
                const sections: string[] = [];
                if (sources.length) {
                  const lines = sources.map((s: any, idx: number) => {
                    const title = s.title || s.url || `Source ${idx + 1}`;
                    const snippet = s.snippet ? ` — ${s.snippet}` : '';
                    const date = s.date ? ` (${s.date})` : '';
                    const urlLine = s.url ? `\n   ${s.url}` : '';
                    return `${idx + 1}. ${title}${snippet}${date}${urlLine}`;
                  });
                  sections.push(`Sources:\n${lines.join('\n')}`);
                }
                if (angles.length) {
                  sections.push(`Angles:\n${angles.map((a: string) => `- ${a}`).join('\n')}`);
                }
                if (facts.length) {
                  sections.push(`Facts:\n${facts.map((f: any) => `- ${f.claim}${f.evidenceUrl ? ` (${f.evidenceUrl})` : ''}`).join('\n')}`);
                }
                if (risks.length) {
                  sections.push(`Risks:\n${risks.map((r: string) => `- ${r}`).join('\n')}`);
                }
                return sections.join('\n\n');
              };
              // PHASE-04 END

              // AG-UI TEXT_MESSAGE streaming — token-by-token content from writer subgraph
              if (event === 'TEXT_MESSAGE_START') {
                streamingBuffer = '';
                applyHandoff({
                  phase: 'processing',
                  step: 'Drafting',
                  agent: { id: 'writer', name: 'Drafter', type: 'content', description: 'Drafting initial content' }
                });
                return;
              }
              if (event === 'TEXT_MESSAGE_CONTENT') {
                const delta = data && typeof data === 'object' && typeof (data as any).delta === 'string'
                  ? (data as any).delta
                  : '';
                if (delta) {
                  streamingBuffer += delta;
                  setMessages(prev => prev.map(msg => (
                    msg.id === tempMessageId ? { ...msg, content: streamingBuffer } : msg
                  )));
                }
                return;
              }
              if (event === 'TEXT_MESSAGE_END') {
                lastDraft = streamingBuffer;
                return;
              }

              if (event === 'error') {
                const msg = (data && (data.message || data.error || 'Processing error')) || 'Processing error';
                // Show a readable error in the temp message instead of a blank bubble
                setMessages(prev => prev.map(m => (
                  m.id === tempMessageId ? { ...m, content: `Error: ${msg}`, isLoading: false } : m
                )));
                return;
              }
              if (event === 'update') {
                const liveEventStatus = extractLiveStatusText(data);
                const nodeId = typeof data?.node === 'string' ? data.node : '';
                const clarifyEvent = extractClarifyFromStreamPayload(data, undefined);
                let handledNode = false;

                // Initial signal
                if (data?.status === 'started') {
                  applyHandoff({ phase: 'handoff', step: liveEventStatus || 'Routing request' });
                }

                // Node-specific updates
                if (data?.node === 'writer') {
                  handledNode = true;
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || 'Drafting',
                    agent: { id: 'writer', name: 'Drafter', type: 'content', description: 'Drafting initial content' }
                  });
                } else if (data?.node === 'editor') {
                  handledNode = true;
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || 'Reviewing',
                    agent: { id: 'editor', name: 'Reviewer', type: 'content', description: 'Reviewing and improving draft' }
                  });
                } else if (data?.node === 'pitch') {
                  handledNode = true;
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || 'Pitching',
                    agent: { id: 'media', name: 'Media Agent', type: 'media', description: 'Generating media pitch' }
                  });
                } else if (data?.node === 'social') {
                  handledNode = true;
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || 'Social drafting',
                    agent: { id: 'social', name: 'Social Agent', type: 'social', description: 'Drafting social content' }
                  });
                } else if (data?.node === 'insights') {
                  handledNode = true;
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || 'Analyzing coverage',
                    agent: { id: 'insights', name: 'Insights Agent', type: 'general', description: 'Generating coverage insights' }
                  });
                } else if (data?.node === 'researcher') {
                  // PHASE-04 START
                  handledNode = true;
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || 'Researching',
                    agent: { id: 'researcher', name: 'Researcher', type: 'general', description: 'Gathering current sources and angles' }
                  });
                  // Backend sends the research pack as `work` directly on this event
                  const formatted = formatResearch(((data as any)?.work) || (data as any).researcher);
                  if (formatted) {
                    setMessages(prev => prev.map(msg => (
                      msg.id === tempMessageId ? { ...msg, content: formatted } : msg
                    )));
                    lastDraft = formatted;
                  }
                  // PHASE-04 END
                } else if (data?.node === 'retrieval') {
                  handledNode = true;
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || 'Retrieving client sources',
                    agent: { id: 'retrieval', name: 'Retriever', type: 'general', description: 'Gathering client documents' }
                  });
                  const formatted = formatRagSources((data as any)?.sources || []);
                  if (formatted) {
                    setMessages(prev => prev.map(msg => (
                      msg.id === tempMessageId ? { ...msg, content: formatted } : msg
                    )));
                    lastDraft = formatted;
                  }
                } else if (data?.node === 'writer_refine') {
                  handledNode = true;
                  hadRefine = true;
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || 'Redrafting',
                    agent: { id: 'writer', name: 'Drafter', type: 'content', description: 'Applying reviewer notes' }
                  });
                } else if (nodeId === 'router' || nodeId === 'orchestrator') {
                  handledNode = true;
                  applyHandoff({
                    phase: 'handoff',
                    step: liveEventStatus || 'Routing request',
                  });
                } else if (clarifyEvent) {
                  handledNode = true;
                  const requestId = clarifyEvent.requestId || generateClarifyRequestId();
                  const nextRequest: ClarifyRequest = {
                    id: requestId,
                    type: clarifyEvent.type,
                    questions: clarifyEvent.questions,
                    agentLabel: clarifyEvent.agentLabel,
                    originRunId: clarifyEvent.originRunId,
                    createdAt: Date.now(),
                    locale: conversationLanguage
                  };
                  updateClarifyQueueForConversation(conversationId, (prev) => {
                    const withoutCurrent = prev.filter((req) => (
                      req.id !== nextRequest.id
                      && req.id !== clarifyContext?.requestId
                    ));
                    // Keep newest request first so the clarification form opens on fresh prompts.
                    return [nextRequest, ...withoutCurrent];
                  });
                  applyHandoff({ phase: 'processing', step: liveEventStatus || 'Clarifying', agent: { id: 'writer', name: 'Drafter', type: 'content', description: 'Collecting details' } });
                  setMessages(prev => prev.map(msg => (
                    msg.id === tempMessageId
                      ? {
                          ...msg,
                          content: 'ℹ️ Clarifications requested — please use the form below.',
                          isLoading: false,
                          meta: {
                            ...(msg.meta || {}),
                            ...(nextRequest.originRunId ? { runId: nextRequest.originRunId } : {}),
                          },
                        }
                      : msg
                  )));
                }

                const inferredNodeAgent = !handledNode
                  ? resolveHandoffAgent({
                      nodeId:
                        (typeof data?.agentId === 'string' ? data.agentId : undefined)
                        || (typeof data?.agent?.id === 'string' ? data.agent.id : undefined)
                        || nodeId,
                      fallbackName:
                        (typeof data?.agentName === 'string' ? data.agentName : undefined)
                        || (typeof data?.agent?.name === 'string' ? data.agent.name : undefined),
                      fallbackType:
                        (typeof data?.agentType === 'string' ? data.agentType : undefined)
                        || data?.agent?.type,
                      fallbackDescription:
                        (typeof data?.agentDescription === 'string' ? data.agentDescription : undefined)
                        || data?.agent?.description,
                      step: liveEventStatus,
                    })
                  : null;
                if (!handledNode && inferredNodeAgent) {
                  handledNode = true;
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || `Running ${inferredNodeAgent.name}`,
                    agent: {
                      id: inferredNodeAgent.id,
                      name: inferredNodeAgent.name,
                      type: inferredNodeAgent.type,
                      description: inferredNodeAgent.description || getAgentDescription(inferredNodeAgent.type),
                    },
                  });
                }

                if (!handledNode && liveEventStatus && data?.status !== 'started') {
                  applyHandoff({
                    phase: nodeId ? 'processing' : undefined,
                    step: liveEventStatus,
                  });
                }

                // Partial text streaming
                if (data?.draft || data?.review) {
                  const partial = data?.review?.revised || data?.draft || '';
                  setMessages(prev => prev.map(msg => (
                    msg.id === tempMessageId ? { ...msg, content: partial } : msg
                  )));
                  if (data?.draft) lastDraft = data.draft;
                }

                // QA updates
                if (data?.node === 'qa' && (data?.answer || data?.summary)) {
                  applyHandoff({
                    phase: 'processing',
                    step: liveEventStatus || 'Answering',
                    agent: { id: 'qa', name: 'Assistant', type: 'general', description: 'Answering your question' }
                  });
                  const qaText = data?.answer || data?.summary || '';
                  setMessages(prev => prev.map(msg => (
                    msg.id === tempMessageId ? { ...msg, content: qaText } : msg
                  )));
                  lastDraft = qaText;
                }

                // If we had a refine pass and editor runs again, push a distinct step
                if (hadRefine && data?.node === 'editor') {
                  hadFinalReview = true;
                  applyHandoff({
                    phase: 'processing',
                    step: 'Final review',
                    agent: { id: 'editor', name: 'Reviewer', type: 'content', description: 'Final review after redraft' }
                  });
                }

                // Live preview for structured work (pitch/social)
                if (data?.work?.pitch) {
                  const formatted = formatPitch(data.work.pitch);
                  setMessages(prev => prev.map(msg => (
                    msg.id === tempMessageId ? { ...msg, content: formatted } : msg
                  )));
                  lastDraft = formatted;
                }
                if (data?.work?.social) {
                  const formatted = formatSocial(data.work.social);
                  setMessages(prev => prev.map(msg => (
                    msg.id === tempMessageId ? { ...msg, content: formatted } : msg
                  )));
                  lastDraft = formatted;
                }
              }

              if (event === 'done') {
                const doneStatus = extractLiveStatusText(data) || 'Run complete';
                // Mark as complete just before finalizing
                setMessages(prev => prev.map(msg => (
                  msg.id === tempMessageId && msg.agentHandoffData
                    ? {
                        ...msg,
                        agentHandoffData: {
                          ...msg.agentHandoffData,
                          phase: 'complete',
                          processingSteps: msg.agentHandoffData.processingSteps.includes(doneStatus)
                            ? msg.agentHandoffData.processingSteps
                            : [...msg.agentHandoffData.processingSteps, doneStatus],
                          statusText: doneStatus,
                        },
                      }
                    : msg
                )));
                // PHASE-02 START
                const metaFromDone = sanitizeRunMeta(data?.state?.meta);
                if (metaFromDone) {
                  runMeta = metaFromDone;
                  runMemories = metaFromDone.memoriesUsed || [];
                }
                // PHASE-02 END
              }
            },
            // PHASE-02 START
            onDone: (payload) => {
              const meta = sanitizeRunMeta(payload?.state?.meta);
              if (meta) {
                runMeta = meta;
                runMemories = meta.memoriesUsed || [];
              }
            },
            // PHASE-02 END
          });

          const clarifyFromFinalInterrupt = extractClarifyFromInterruptEntries(
            (final as any)?.state?.__interrupt__,
            typeof final?.state?.runId === 'string' ? final.state.runId : undefined,
          );
          if (clarifyFromFinalInterrupt) {
            const requestId = clarifyFromFinalInterrupt.requestId || generateClarifyRequestId();
            const nextRequest: ClarifyRequest = {
              id: requestId,
              type: clarifyFromFinalInterrupt.type,
              questions: clarifyFromFinalInterrupt.questions,
              agentLabel: clarifyFromFinalInterrupt.agentLabel,
              originRunId: clarifyFromFinalInterrupt.originRunId,
              createdAt: Date.now(),
              locale: conversationLanguage,
            };
            updateClarifyQueueForConversation(conversationId, (prev) => {
              const withoutCurrent = prev.filter((req) => (
                req.id !== nextRequest.id
                && req.id !== clarifyContext?.requestId
              ));
              return [nextRequest, ...withoutCurrent];
            });
            setMessages(prev => prev.map(msg => (
              msg.id === tempMessageId
                ? {
                    ...msg,
                    content: 'ℹ️ Clarifications requested — please use the form below.',
                    isLoading: false,
                    agentHandoffData: msg.agentHandoffData
                      ? {
                          ...msg.agentHandoffData,
                          phase: 'processing',
                          statusText: 'Clarifying',
                          processingSteps: msg.agentHandoffData.processingSteps.includes('Clarifying')
                            ? msg.agentHandoffData.processingSteps
                            : [...msg.agentHandoffData.processingSteps, 'Clarifying'],
                        }
                      : msg.agentHandoffData,
                    meta: {
                      ...(msg.meta || {}),
                      ...(nextRequest.originRunId ? { runId: nextRequest.originRunId } : {}),
                    },
                  }
                : msg
            )));
            return;
          }

          const clarifyFromFinalState = extractClarifyFromState(
            final?.state,
            typeof final?.state?.runId === 'string' ? final.state.runId : undefined,
          );
          if (clarifyFromFinalState) {
            const requestId = clarifyFromFinalState.requestId || generateClarifyRequestId();
            const nextRequest: ClarifyRequest = {
              id: requestId,
              type: clarifyFromFinalState.type,
              questions: clarifyFromFinalState.questions,
              agentLabel: clarifyFromFinalState.agentLabel,
              originRunId: clarifyFromFinalState.originRunId,
              createdAt: Date.now(),
              locale: conversationLanguage,
            };
            updateClarifyQueueForConversation(conversationId, (prev) => {
              const withoutCurrent = prev.filter((req) => (
                req.id !== nextRequest.id
                && req.id !== clarifyContext?.requestId
              ));
              return [nextRequest, ...withoutCurrent];
            });
            setMessages(prev => prev.map(msg => (
              msg.id === tempMessageId
                ? {
                    ...msg,
                    content: 'ℹ️ Clarifications requested — please use the form below.',
                    isLoading: false,
                    agentHandoffData: msg.agentHandoffData
                      ? {
                          ...msg.agentHandoffData,
                          phase: 'processing',
                          statusText: 'Clarifying',
                          processingSteps: msg.agentHandoffData.processingSteps.includes('Clarifying')
                            ? msg.agentHandoffData.processingSteps
                            : [...msg.agentHandoffData.processingSteps, 'Clarifying'],
                        }
                      : msg.agentHandoffData,
                    meta: {
                      ...(msg.meta || {}),
                      ...(nextRequest.originRunId ? { runId: nextRequest.originRunId } : {}),
                    },
                  }
                : msg
            )));
            return;
          }

          // If clarify was requested, keep questions visible and do not finalize
          if (final?.state?.work?.clarify?.needed) {
            return;
          }

          if (requestResume && clarifyContext?.requestId) {
            updateClarifyQueueForConversation(conversationId, (prev) =>
              prev.filter((req) => req.id !== clarifyContext.requestId)
            );
          }

          // PHASE-02 START
          if (!runMeta) {
            const fallbackMeta = sanitizeRunMeta(final?.state?.meta);
            if (fallbackMeta) {
              runMeta = fallbackMeta;
              runMemories = fallbackMeta.memoriesUsed || [];
            }
          }
          // PHASE-02 END

          // Prefer structured work outputs when present (pitch/social/qa), else review/draft
          const getFormattedFromWork = () => {
            const work = (final?.state?.work || {}) as any;
            const pitch = work.pitch ? (work.pitch) : null;
            const social = work.social ? (work.social) : null;
            const qa = work.qa ? work.qa : null;
            if (pitch) {
              const subjects = Array.isArray(pitch.subjects) ? pitch.subjects : [];
              const subjBlock = subjects.length
                ? `Subject options:\n${subjects.map((s: string, i: number) => `- ${s}`).join('\n')}`
                : '';
              const angleBlock = Array.isArray(pitch.angleReasons) && pitch.angleReasons.length
                ? `\nReasons:\n${pitch.angleReasons.map((r: string) => `- ${r}`).join('\n')}`
                : '';
              const follow = pitch.followUp ? `\n\nFollow-up:\n${pitch.followUp}` : '';
              return [subjBlock, pitch.pitch ? `\nPitch:\n${pitch.pitch}` : '', angleBlock, follow].filter(Boolean).join('\n');
            }
            if (social) {
              const xBlock = Array.isArray(social.xThread) && social.xThread.length
                ? `X Thread:\n${social.xThread.map((t: string, i: number) => `${i + 1}. ${t}`).join('\n')}`
                : '';
              const liBlock = social.linkedin ? `\n\nLinkedIn:\n${social.linkedin}` : '';
              const igBlock = social.instagram ? `\n\nInstagram:\n- Caption: ${social.instagram.caption || ''}\n- Alt Text: ${social.instagram.altText || ''}` : '';
              return [xBlock, liBlock, igBlock].filter(Boolean).join('');
            }
            if (qa) {
              const answer = typeof qa.answer === 'string' ? qa.answer.trim() : '';
              if (answer) return answer;
              const summary = typeof qa.summary === 'string' ? qa.summary.trim() : '';
              if (summary) {
                if (Array.isArray(qa.items) && qa.items.length) {
                  const lines = qa.items.slice(0, 10).map((item: any, idx: number) => {
                    const title = (
                      (typeof item?.title === 'string' && item.title.trim()) ||
                      (typeof item?.name === 'string' && item.name.trim()) ||
                      (typeof item?.id === 'string' && item.id.trim()) ||
                      `Item ${idx + 1}`
                    );
                    return `${idx + 1}. ${title}`;
                  });
                  return `${summary}\n\nItems:\n${lines.join('\n')}`;
                }
                return summary;
              }
            }
            return '';
          };

          const workText = getFormattedFromWork();
          const finalBaseText = workText || final?.state?.review?.revised || final?.state?.draft || lastDraft || '';
          // PHASE-04 START
          // Local helper to avoid scope issues when formatting research in the final message
          const formatResearchFinal = (pack: any): string => {
            if (!pack) return '';
            const sources = Array.isArray(pack.sources) ? pack.sources : [];
            const angles = Array.isArray(pack.angles) ? pack.angles : [];
            const facts = Array.isArray(pack.facts) ? pack.facts : [];
            const risks = Array.isArray(pack.risks) ? pack.risks : [];
            const sections: string[] = [];
            if (sources.length) {
              const lines = sources.map((s: any, idx: number) => {
                const title = s.title || s.url || `Source ${idx + 1}`;
                const snippet = s.snippet ? ` — ${s.snippet}` : '';
                const date = s.date ? ` (${s.date})` : '';
                const urlLine = s.url ? `\n   ${s.url}` : '';
                return `${idx + 1}. ${title}${snippet}${date}${urlLine}`;
              });
              sections.push(`Sources:\n${lines.join('\n')}`);
            }
            if (angles.length) sections.push(`Angles:\n${angles.map((a: string) => `- ${a}`).join('\n')}`);
            if (facts.length) sections.push(`Facts:\n${facts.map((f: any) => `- ${f.claim}${f.evidenceUrl ? ` (${f.evidenceUrl})` : ''}`).join('\n')}`);
            if (risks.length) sections.push(`Risks:\n${risks.map((r: string) => `- ${r}`).join('\n')}`);
            return sections.join('\n\n');
          };
          const formatRagFinal = (sources: any): string => {
            if (!Array.isArray(sources) || !sources.length) return '';
            return sources
              .map((s: any, idx: number) => {
                const title = s.title || s.docId || `Document ${idx + 1}`;
                const page = s.page ? ` p.${s.page}` : '';
                const url = s.url ? ` — ${s.url}` : '';
                const snippet = typeof s.text === 'string' && s.text.trim()
                  ? s.text.trim().slice(0, 320) + (s.text.trim().length > 320 ? '…' : '')
                  : '';
                return `[${idx + 1}] ${title}${page}${url}${snippet ? `\n   ${snippet}` : ''}`;
              })
              .join('\n');
          };
          const researchFinalText = formatResearchFinal(((final?.state?.work || {}) as any)?.researcher);
          const ragFinalText = formatRagFinal(((final?.state?.work || {}) as any)?.rag?.sources);
          const runtimeToolMeta = buildRuntimeToolMessageMeta(final?.state);
          const runtimeToolFallbackText = runtimeToolMeta
            ? (
                normalizeString(runtimeToolMeta.state.error)
                || normalizeString(runtimeToolMeta.state.summary)
                || 'The requested runtime tool did not return a draft.'
              )
            : '';
          let finalText = finalBaseText;
          if (researchFinalText) {
            finalText = [finalText, `Research Pack:\n${researchFinalText}`].filter(Boolean).join('\n\n');
          }
          if (ragFinalText) {
            finalText = [finalText, `Client Sources:\n${ragFinalText}`].filter(Boolean).join('\n\n');
          }
          // As a safety net, ensure we never send an empty assistant message
          if (!finalText || !finalText.trim()) {
            finalText = runtimeToolFallbackText || researchFinalText || ragFinalText || 'I could not assemble a draft from this request. Please try rephrasing or provide more details.';
          }
          // PHASE-04 END

          // Remove temp and add the final AI message
          setMessages(prev => prev.filter(msg => msg.id !== tempMessageId));
          const aiMessage: any = {
            content: finalText,
            sender: 'ai',
            timestamp: serverTimestamp(),
            messageKind: 'ai_response'
          };
          // Thread agent extras for downstream UI and artifact creation.
          // QA answers stay as chat messages; qaIssues are the artifact-sidecar field.
          try {
            const work: any = final?.state?.work || {};
            const extras: any = {};
            if (work.pitch) {
              const p = work.pitch || {};
              if (Array.isArray(p.targets)) extras.targets = p.targets;
              if (Array.isArray(p.sourcesEnriched)) extras.sourcesEnriched = p.sourcesEnriched;
              if (Array.isArray(p.qaIssues)) extras.qaIssues = p.qaIssues;
              if (Array.isArray(p.validationIssues)) extras.validationIssues = p.validationIssues;
            }
            if (work.social) {
              const s = work.social || {};
              if (Array.isArray(s.qaIssues)) extras.qaIssues = s.qaIssues;
              if (Array.isArray(s.validationIssues)) extras.validationIssues = s.validationIssues;
            }
            if (work.format) {
              extras.format = work.format;
            }
            // PHASE-04 START
            if (work.researcher) {
              extras.researcher = work.researcher;
            }
            if (work.rag) {
              extras.rag = work.rag;
            }
            // PHASE-04 END
            if (Object.keys(extras).length > 0) {
              aiMessage.artifact = extras;
            }
          } catch {}
          // Tag with agent type for nicer UI + artifact detection
          try {
            const routeNode = (final?.state?.work && (final?.state as any)?.lastNode) || null;
            // We didn't track lastNode; infer from which work is present
            const workAny = (final?.state?.work as any) || {};
            const agentType = workAny.pitch
              ? 'media'
              : workAny.social
                ? 'social'
                : workAny.researcher || workAny.qa
                  ? 'general'
                  : 'content';
            aiMessage.agentMetadata = { agentType, multiPass: { refined: hadRefine, finalReview: hadFinalReview } };
          } catch {}
          // PHASE-02 START
          const metaPayload: Record<string, any> = {};
          if (clarificationAckRef.current) {
            metaPayload.clarificationAck = clarificationAckRef.current;
          }
          if (runMemories.length > 0) {
            metaPayload.memories = runMemories;
          }
          if (runMeta) {
            metaPayload.provenance = runMeta;
            if (Array.isArray(runMeta.policyIssues) && runMeta.policyIssues.length > 0) {
              metaPayload.policyIssues = runMeta.policyIssues;
            }
          }
          if (runtimeToolMeta) {
            metaPayload.runtimeTool = runtimeToolMeta;
          }
          if (Object.keys(metaPayload).length > 0) {
            aiMessage.meta = metaPayload;
          }
          // PHASE-02 END
          const runId = typeof final?.state?.runId === 'string' ? final.state.runId : undefined;
          const persistedMessageId = typeof final?.messageId === 'string' ? final.messageId : undefined;
          const wasPersisted = final?.persisted === true && persistedMessageId;

          if (wasPersisted) {
            // Backend already saved - only patch clarificationAck if needed
            if (clarificationAckRef.current) {
              try {
                await updateDoc(doc(messageRef, persistedMessageId), {
                  "meta.clarificationAck": clarificationAckRef.current,
                });
              } catch {
                // Fallback: full save if patch fails
                if (runId) {
                  await setDoc(doc(messageRef, runId), aiMessage);
                } else {
                  await addDoc(messageRef, aiMessage);
                }
              }
              clarificationAckRef.current = null;
            }
            // Message already persisted, no action needed
          } else if (runId) {
            await setDoc(doc(messageRef, runId), aiMessage);
            if (clarificationAckRef.current) {
              clarificationAckRef.current = null;
            }
          } else {
            await addDoc(messageRef, aiMessage);
            if (clarificationAckRef.current) {
              clarificationAckRef.current = null;
            }
          }

          // Clear clarification queue after a successful completion
          updateClarifyQueueForConversation(conversationId, () => []);

          // Auto-generate conversation title if this is the first meaningful exchange
          try {
            await maybeGenerateConversationTitle(conversationId, content, finalText);
          } catch (titleError) {
            console.error('Error generating conversation title:', titleError);
          }

          // Check for learning opportunity after AI response is added
          try {
            await checkForLearningOpportunity(content);
          } catch (learningError) {
            console.error('Error checking for learning opportunity:', learningError);
          }

          return; // Completed via SSE path
        }
      } catch (sseError) {
        console.warn('LangGraph SSE path failed; legacy fallback is disabled:', sseError);
        setMessages(prev => prev.filter(msg => msg.id !== tempMessageId));
        const aiMessage: any = {
          content: 'Sorry — our agent service is unavailable right now. Please try again shortly.',
          sender: 'ai',
          timestamp: serverTimestamp()
        };
        await addDoc(messageRef, aiMessage);
        return;
      }

      // LangGraph-only cutover: if we reached this point, no graph result was produced.
      setMessages(prev => prev.filter(msg => msg.id !== tempMessageId));
      const aiMessage: any = {
        content: 'I could not process this via the new agents. Please retry in a moment.',
        sender: 'ai',
        timestamp: serverTimestamp()
      };
      await addDoc(messageRef, aiMessage);
      return;
    } catch (error) {
      console.error("Error generating AI response:", error);
      
      // Add error message if AI generation fails
      const errorMessage = {
        content: "I'm sorry, I encountered an error processing your message. Please try again.",
        sender: 'ai',
        timestamp: serverTimestamp()
      };
      
      await addDoc(messageRef, errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function editMessage(messageId: string, newContent: string): Promise<void> {
    // Verify auth state before proceeding
    const isAuthenticated = await verifyAuthState();
    let tempEditMessageId = '';
    
    if (!isAuthenticated || !currentUser || !currentConversation) {
      throw new Error("No authenticated user or conversation");
    }

    try {
      setLoading(true);

      // Get the message to be edited
      const messageRef = doc(
        db,
        'users',
        currentUser.uid,
        'conversations',
        currentConversation.id,
        'messages',
        messageId
      );

      const messageDoc = await getDoc(messageRef);
      if (!messageDoc.exists()) {
        throw new Error("Message not found");
      }

      const messageData = messageDoc.data();
      
      // Only allow editing user messages
      if (messageData.sender !== 'user') {
        throw new Error("Only user messages can be edited");
      }

      // Store original content if this is the first edit
      const updateData: any = {
        content: newContent,
        isEdited: true,
        editedAt: serverTimestamp()
      };

      if (!messageData.isEdited) {
        updateData.originalContent = messageData.content;
      }

      // Update the message in Firestore
      await updateDoc(messageRef, updateData);

      // Find the edited message in the local state
      const messageIndex = messages.findIndex(msg => msg.id === messageId);
      if (messageIndex === -1) return;

      // Update local state immediately for better UX
      const updatedMessages = [...messages];
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        content: newContent,
        isEdited: true,
        originalContent: messageData.isEdited ? messageData.originalContent : messageData.content,
        editedAt: new Date()
      };
      // Remove subsequent messages (they'll be regenerated)
      const messagesToKeep = updatedMessages.slice(0, messageIndex + 1);
      setMessages(messagesToKeep);

      // Add temporary loading message with agent handoff animation for edit regeneration
      tempEditMessageId = `temp-edit-${Date.now()}`;
      const tempEditMessage: Message = {
        id: tempEditMessageId,
        content: '',
        sender: 'ai',
        timestamp: Date.now(),
        isLoading: true,
        agentHandoffData: {
          selectedAgent: null,
          phase: 'selecting',
          processingSteps: []
        }
      };
      
      setMessages(prev => [...prev, tempEditMessage]);

      const conversationLanguage = (currentConversation?.language as 'en' | 'ar') || flags.localeDefault || 'en';
      const runLanguage = resolveRunLanguage(conversationLanguage, newContent);
      const includePreviousContent = isLikelyEditContinuation(newContent);
      const previousContent = (() => {
        try {
          const reversed = [...messagesToKeep].reverse();
          const lastArtifactRef = reversed.find((m) => (m as any).isArtifactReference && !!(m as any).artifactContent);
          if (lastArtifactRef) return (lastArtifactRef as any).artifactContent as string;
          const MIN_PREV_CHARS = 200;
          const lastAiDraft = reversed.find((m) => {
            if ((m as any).sender !== 'ai' || (m as any).isArtifactReference) return false;
            const kind = (m as any).messageKind;
            if (kind === 'clarification' || kind === 'clarification_skip') return false;
            const hasDraftMarker =
              kind === 'ai_response' ||
              !!(m as any).artifact ||
              !!((m as any).meta && ((m as any).meta.provenance || (m as any).meta.memories));
            if (hasDraftMarker) return !!((m as any).content || (m as any).text);
            const text = ((m as any).content || (m as any).text || '') as string;
            return text.trim().length >= MIN_PREV_CHARS;
          });
          if (lastAiDraft) return ((lastAiDraft as any).content || (lastAiDraft as any).text) as string;
        } catch {}
        return undefined;
      })();
      const previousContentForRun = includePreviousContent ? previousContent : undefined;

      const applyHandoff = (update: {
        phase?: 'selecting' | 'handoff' | 'processing' | 'complete';
        step?: string;
        agent?: {
          id: string;
          name: string;
          type: 'crisis' | 'media' | 'social' | 'executive' | 'content' | 'general';
          description: string;
        };
      }) => {
        setMessages(prev => prev.map(msg => {
          if (msg.id !== tempEditMessageId) return msg;
          const current = msg.agentHandoffData || { selectedAgent: null, phase: 'selecting', processingSteps: [] as string[] };
          const steps = [...current.processingSteps];
          if (update.step && !steps.includes(update.step)) steps.push(update.step);
          const selectedAgent = update.agent && shouldRenderHandoffAgent(update.agent.id)
            ? {
                id: update.agent.id,
                name: update.agent.name,
                type: update.agent.type,
                description: update.agent.description,
                expertise: getAgentExpertise(update.agent.type),
              }
            : current.selectedAgent;
          return {
            ...msg,
            agentHandoffData: {
              selectedAgent,
              phase: update.phase || current.phase,
              processingSteps: steps,
              statusText: update.step || current.statusText,
            },
          } as Message;
        }));
      };

      let lastDraft: string | undefined;
      let hadRefine = false;
      let hadFinalReview = false;
      let runMeta: RunMeta | null = null;
      let runMemories: MemoryReference[] = [];
      const editClientId = Object.prototype.hasOwnProperty.call(
        pendingConversationClientRef.current,
        currentConversation.id
      )
        ? pendingConversationClientRef.current[currentConversation.id] || undefined
        : currentConversation.clientId || activeClient?.id;

      const final = await runGraphSSE({
        input: newContent,
        userId: currentUser.uid,
        clientId: editClientId,
        threadId: currentConversation.id,
        language: runLanguage,
        previousContent: previousContentForRun,
        kind: 'message',
        onEvent: ({ event, data }) => {
          if (event === 'error') {
            const msg = (data && (data.message || data.error || 'Processing error')) || 'Processing error';
            setMessages(prev => prev.map(m => (
              m.id === tempEditMessageId ? { ...m, content: `Error: ${msg}`, isLoading: false } : m
            )));
            return;
          }

          if (event === 'update') {
            const liveEventStatus = extractLiveStatusText(data);
            const nodeId = typeof data?.node === 'string' ? data.node : '';
            const clarifyEvent = extractClarifyFromStreamPayload(data, undefined);
            let handledNode = false;

            if (data?.status === 'started') {
              applyHandoff({ phase: 'handoff', step: liveEventStatus || 'Routing request' });
            }

            if (data?.node === 'writer') {
              handledNode = true;
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || 'Drafting',
                agent: { id: 'writer', name: 'Drafter', type: 'content', description: 'Drafting initial content' },
              });
            } else if (data?.node === 'editor') {
              handledNode = true;
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || 'Reviewing',
                agent: { id: 'editor', name: 'Reviewer', type: 'content', description: 'Reviewing and improving draft' },
              });
            } else if (data?.node === 'pitch') {
              handledNode = true;
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || 'Pitching',
                agent: { id: 'media', name: 'Media Agent', type: 'media', description: 'Generating media pitch' },
              });
            } else if (data?.node === 'social') {
              handledNode = true;
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || 'Social drafting',
                agent: { id: 'social', name: 'Social Agent', type: 'social', description: 'Drafting social content' },
              });
            } else if (data?.node === 'qa') {
              handledNode = true;
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || 'Answering',
                agent: { id: 'qa', name: 'Assistant', type: 'general', description: 'Answering your question' },
              });
            } else if (data?.node === 'researcher') {
              handledNode = true;
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || 'Researching',
                agent: { id: 'researcher', name: 'Researcher', type: 'general', description: 'Gathering current sources and angles' },
              });
            } else if (data?.node === 'retrieval') {
              handledNode = true;
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || 'Retrieving client sources',
                agent: { id: 'retrieval', name: 'Retriever', type: 'general', description: 'Gathering client documents' },
              });
            } else if (data?.node === 'writer_refine') {
              handledNode = true;
              hadRefine = true;
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || 'Redrafting',
                agent: { id: 'writer', name: 'Drafter', type: 'content', description: 'Applying reviewer notes' },
              });
            } else if (nodeId === 'router' || nodeId === 'orchestrator') {
              handledNode = true;
              applyHandoff({ phase: 'handoff', step: liveEventStatus || 'Routing request' });
            } else if (clarifyEvent) {
              handledNode = true;
              const requestId = clarifyEvent.requestId || generateClarifyRequestId();
              const nextRequest: ClarifyRequest = {
                id: requestId,
                type: clarifyEvent.type,
                questions: clarifyEvent.questions,
                agentLabel: clarifyEvent.agentLabel,
                originRunId: clarifyEvent.originRunId,
                createdAt: Date.now(),
                locale: conversationLanguage,
              };
              updateClarifyQueueForConversation(currentConversation.id, (prev) => {
                const withoutCurrent = prev.filter((req) => req.id !== nextRequest.id);
                return [nextRequest, ...withoutCurrent];
              });
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || 'Clarifying',
                agent: { id: 'writer', name: 'Drafter', type: 'content', description: 'Collecting details' },
              });
              setMessages(prev => prev.map(msg => (
                msg.id === tempEditMessageId
                  ? {
                      ...msg,
                      content: 'ℹ️ Clarifications requested — please use the form below.',
                      isLoading: false,
                      agentHandoffData: msg.agentHandoffData
                        ? {
                            ...msg.agentHandoffData,
                            phase: 'processing',
                            statusText: 'Clarifying',
                            processingSteps: msg.agentHandoffData.processingSteps.includes('Clarifying')
                              ? msg.agentHandoffData.processingSteps
                              : [...msg.agentHandoffData.processingSteps, 'Clarifying'],
                          }
                        : msg.agentHandoffData,
                      meta: {
                        ...(msg.meta || {}),
                        ...(nextRequest.originRunId ? { runId: nextRequest.originRunId } : {}),
                      },
                    }
                  : msg
              )));
            }

            const inferredNodeAgent = !handledNode
              ? resolveHandoffAgent({
                  nodeId:
                    (typeof data?.agentId === 'string' ? data.agentId : undefined)
                    || (typeof data?.agent?.id === 'string' ? data.agent.id : undefined)
                    || nodeId,
                  fallbackName:
                    (typeof data?.agentName === 'string' ? data.agentName : undefined)
                    || (typeof data?.agent?.name === 'string' ? data.agent.name : undefined),
                  fallbackType:
                    (typeof data?.agentType === 'string' ? data.agentType : undefined)
                    || data?.agent?.type,
                  fallbackDescription:
                    (typeof data?.agentDescription === 'string' ? data.agentDescription : undefined)
                    || data?.agent?.description,
                  step: liveEventStatus,
                })
              : null;
            if (!handledNode && inferredNodeAgent) {
              handledNode = true;
              applyHandoff({
                phase: 'processing',
                step: liveEventStatus || `Running ${inferredNodeAgent.name}`,
                agent: {
                  id: inferredNodeAgent.id,
                  name: inferredNodeAgent.name,
                  type: inferredNodeAgent.type,
                  description: inferredNodeAgent.description || getAgentDescription(inferredNodeAgent.type),
                },
              });
            }

            if (!handledNode && liveEventStatus && data?.status !== 'started') {
              applyHandoff({
                phase: nodeId ? 'processing' : undefined,
                step: liveEventStatus,
              });
            }

            if (data?.draft || data?.review) {
              const partial = data?.review?.revised || data?.draft || '';
              setMessages(prev => prev.map(msg => (
                msg.id === tempEditMessageId ? { ...msg, content: partial } : msg
              )));
              if (data?.draft) lastDraft = data.draft;
            }

            if (hadRefine && data?.node === 'editor') {
              hadFinalReview = true;
              applyHandoff({
                phase: 'processing',
                step: 'Final review',
                agent: { id: 'editor', name: 'Reviewer', type: 'content', description: 'Final review after redraft' },
              });
            }
          }

          if (event === 'done') {
            const doneStatus = extractLiveStatusText(data) || 'Run complete';
            setMessages(prev => prev.map(msg => (
              msg.id === tempEditMessageId && msg.agentHandoffData
                ? {
                    ...msg,
                    agentHandoffData: {
                      ...msg.agentHandoffData,
                      phase: 'complete',
                      processingSteps: msg.agentHandoffData.processingSteps.includes(doneStatus)
                        ? msg.agentHandoffData.processingSteps
                        : [...msg.agentHandoffData.processingSteps, doneStatus],
                      statusText: doneStatus,
                    },
                  }
                : msg
            )));
            const metaFromDone = sanitizeRunMeta(data?.state?.meta);
            if (metaFromDone) {
              runMeta = metaFromDone;
              runMemories = metaFromDone.memoriesUsed || [];
            }
          }
        },
        onDone: (payload) => {
          const meta = sanitizeRunMeta(payload?.state?.meta);
          if (meta) {
            runMeta = meta;
            runMemories = meta.memoriesUsed || [];
          }
        },
      });

      const clarifyFromFinalInterrupt = extractClarifyFromInterruptEntries(
        (final as any)?.state?.__interrupt__,
        typeof final?.state?.runId === 'string' ? final.state.runId : undefined,
      );
      if (clarifyFromFinalInterrupt) {
        const requestId = clarifyFromFinalInterrupt.requestId || generateClarifyRequestId();
        const nextRequest: ClarifyRequest = {
          id: requestId,
          type: clarifyFromFinalInterrupt.type,
          questions: clarifyFromFinalInterrupt.questions,
          agentLabel: clarifyFromFinalInterrupt.agentLabel,
          originRunId: clarifyFromFinalInterrupt.originRunId,
          createdAt: Date.now(),
          locale: conversationLanguage,
        };
        updateClarifyQueueForConversation(currentConversation.id, (prev) => {
          const withoutCurrent = prev.filter((req) => req.id !== nextRequest.id);
          return [nextRequest, ...withoutCurrent];
        });
        return;
      }

      const clarifyFromFinalState = extractClarifyFromState(
        final?.state,
        typeof final?.state?.runId === 'string' ? final.state.runId : undefined,
      );
      if (clarifyFromFinalState) {
        const requestId = clarifyFromFinalState.requestId || generateClarifyRequestId();
        const nextRequest: ClarifyRequest = {
          id: requestId,
          type: clarifyFromFinalState.type,
          questions: clarifyFromFinalState.questions,
          agentLabel: clarifyFromFinalState.agentLabel,
          originRunId: clarifyFromFinalState.originRunId,
          createdAt: Date.now(),
          locale: conversationLanguage,
        };
        updateClarifyQueueForConversation(currentConversation.id, (prev) => {
          const withoutCurrent = prev.filter((req) => req.id !== nextRequest.id);
          return [nextRequest, ...withoutCurrent];
        });
        return;
      }

      if (final?.state?.work?.clarify?.needed) {
        return;
      }

      if (!runMeta) {
        const fallbackMeta = sanitizeRunMeta(final?.state?.meta);
        if (fallbackMeta) {
          runMeta = fallbackMeta;
          runMemories = fallbackMeta.memoriesUsed || [];
        }
      }

      const getFormattedFromWork = () => {
        const work = (final?.state?.work || {}) as any;
        const pitch = work.pitch ? (work.pitch) : null;
        const social = work.social ? (work.social) : null;
        const qa = work.qa ? work.qa : null;
        if (pitch) {
          const subjects = Array.isArray(pitch.subjects) ? pitch.subjects : [];
          const subjBlock = subjects.length
            ? `Subject options:\n${subjects.map((s: string) => `- ${s}`).join('\n')}`
            : '';
          const angleBlock = Array.isArray(pitch.angleReasons) && pitch.angleReasons.length
            ? `\nReasons:\n${pitch.angleReasons.map((r: string) => `- ${r}`).join('\n')}`
            : '';
          const follow = pitch.followUp ? `\n\nFollow-up:\n${pitch.followUp}` : '';
          return [subjBlock, pitch.pitch ? `\nPitch:\n${pitch.pitch}` : '', angleBlock, follow].filter(Boolean).join('\n');
        }
        if (social) {
          const xBlock = Array.isArray(social.xThread) && social.xThread.length
            ? `X Thread:\n${social.xThread.map((t: string, i: number) => `${i + 1}. ${t}`).join('\n')}`
            : '';
          const liBlock = social.linkedin ? `\n\nLinkedIn:\n${social.linkedin}` : '';
          const igBlock = social.instagram ? `\n\nInstagram:\n- Caption: ${social.instagram.caption || ''}\n- Alt Text: ${social.instagram.altText || ''}` : '';
          return [xBlock, liBlock, igBlock].filter(Boolean).join('');
        }
        if (qa) {
          const answer = typeof qa.answer === 'string' ? qa.answer.trim() : '';
          if (answer) return answer;
          const summary = typeof qa.summary === 'string' ? qa.summary.trim() : '';
          if (summary) return summary;
        }
        return '';
      };

      const runtimeToolMeta = buildRuntimeToolMessageMeta(final?.state);
      const runtimeToolFallbackText = runtimeToolMeta
        ? (
            normalizeString(runtimeToolMeta.state.error)
            || normalizeString(runtimeToolMeta.state.summary)
            || 'The requested runtime tool did not return a draft.'
          )
        : '';
      const finalBaseText = getFormattedFromWork() || final?.state?.review?.revised || final?.state?.draft || lastDraft || '';
      const finalText = finalBaseText && finalBaseText.trim().length > 0
        ? finalBaseText
        : (runtimeToolFallbackText || 'I could not regenerate a response from this edit. Please try again.');

      setMessages(prev => prev.filter(msg => msg.id !== tempEditMessageId));

      const messageRef2 = collection(
        db,
        'users',
        currentUser.uid,
        'conversations',
        currentConversation.id,
        'messages'
      );

      const aiMessage: any = {
        content: finalText,
        sender: 'ai',
        timestamp: serverTimestamp(),
        messageKind: 'ai_response'
      };
      try {
        const work: any = final?.state?.work || {};
        const extras: any = {};
        if (work.pitch) {
          const p = work.pitch || {};
          if (Array.isArray(p.targets)) extras.targets = p.targets;
          if (Array.isArray(p.sourcesEnriched)) extras.sourcesEnriched = p.sourcesEnriched;
          if (Array.isArray(p.qaIssues)) extras.qaIssues = p.qaIssues;
          if (Array.isArray(p.validationIssues)) extras.validationIssues = p.validationIssues;
        }
        if (work.social) {
          const s = work.social || {};
          if (Array.isArray(s.qaIssues)) extras.qaIssues = s.qaIssues;
          if (Array.isArray(s.validationIssues)) extras.validationIssues = s.validationIssues;
        }
        if (work.format) extras.format = work.format;
        if (work.researcher) extras.researcher = work.researcher;
        if (work.rag) extras.rag = work.rag;
        if (Object.keys(extras).length > 0) aiMessage.artifact = extras;
      } catch {}

      try {
        const workAny = (final?.state?.work as any) || {};
        const agentType = workAny.pitch
          ? 'media'
          : workAny.social
            ? 'social'
            : workAny.researcher || workAny.qa
              ? 'general'
              : 'content';
        aiMessage.agentMetadata = { agentType, multiPass: { refined: hadRefine, finalReview: hadFinalReview } };
      } catch {}

      const metaPayload: Record<string, any> = {};
      if (runMemories.length > 0) metaPayload.memories = runMemories;
      if (runMeta) {
        metaPayload.provenance = runMeta;
        if (Array.isArray(runMeta.policyIssues) && runMeta.policyIssues.length > 0) {
          metaPayload.policyIssues = runMeta.policyIssues;
        }
      }
      if (runtimeToolMeta) {
        metaPayload.runtimeTool = runtimeToolMeta;
      }
      if (Object.keys(metaPayload).length > 0) aiMessage.meta = metaPayload;

      const runId = typeof final?.state?.runId === 'string' ? final.state.runId : undefined;
      const persistedMessageId = typeof final?.messageId === 'string' ? final.messageId : undefined;
      const wasPersisted = final?.persisted === true && persistedMessageId;

      if (wasPersisted) {
        // Backend already persisted the assistant message.
      } else if (runId) {
        await setDoc(doc(messageRef2, runId), aiMessage);
      } else {
        await addDoc(messageRef2, aiMessage);
      }

    } catch (error) {
      console.error("Error editing message:", error);
      if (tempEditMessageId) {
        setMessages(prev => prev.filter(msg => msg.id !== tempEditMessageId));
      }
      const messageRef2 = collection(
        db,
        'users',
        currentUser.uid,
        'conversations',
        currentConversation.id,
        'messages'
      );
      await addDoc(messageRef2, {
        content: 'Sorry — our agent service is unavailable right now. Please try again shortly.',
        sender: 'ai',
        timestamp: serverTimestamp(),
        messageKind: 'ai_response'
      } as any);
    } finally {
      setLoading(false);
    }
  }

  async function createNewConversation(title?: string, clientId?: string): Promise<string> {
    const requestedTitle = title || 'New Conversation';
    const requestedClientId = clientId ?? activeClient?.id ?? null;
    const inFlightKey = `${currentUser?.uid || 'pending-user'}:${requestedClientId || 'general'}:${requestedTitle}`;

    // FORENSIC LOGGING: Track ALL conversation creation attempts
    console.log('🎯 createNewConversation called', {
      title,
      clientId,
      isCleaningUp,
      loading,
      timestamp: Date.now(),
      stack: new Error().stack?.split('\n').slice(1, 4).join('\n') // First 3 stack frames
    });
    
    if (isCleaningUp) {
      console.error('🚨 ATTEMPTED CONVERSATION CREATION DURING CLEANUP!', {
        title,
        clientId,
        stack: new Error().stack
      });
      throw new Error('Cannot create conversation during cleanup');
    }

    const existingCreate = conversationCreateInFlightRef.current.get(inFlightKey);
    if (existingCreate) {
      console.log('⏳ Reusing in-flight conversation creation', {
        title: requestedTitle,
        clientId: requestedClientId,
        inFlightKey,
      });
      return existingCreate;
    }

    const createPromise = (async () => {

      // Verify auth state before proceeding
      const isAuthenticated = await verifyAuthState();

      if (!isAuthenticated || !currentUser) {
        console.error("Authentication error: No current user found when creating conversation");
        throw new Error("No authenticated user");
      }

      const conversationsRef = collection(db, 'users', currentUser.uid, 'conversations');

      const newConversation = {
        title: requestedTitle,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        clientId: requestedClientId, // Store the client ID if provided or last active
        language: flags.localeDefault || 'en',
        hasUserMessage: false,
        messageCount: 0
      };

      const docRef = await addDoc(conversationsRef, newConversation);

      // Add welcome message, with client context if available
      const messagesRef = collection(
        db,
        'users',
        currentUser.uid,
        'conversations',
        docRef.id,
        'messages'
      );

      let welcomeMessage = "Hello! I'm your PR assistant. How can I help you today with your communications?";

      // If there's a client ID, customize the welcome message without mutating
      // activeClient. The caller/loadConversation owns the visible active context.
      if (requestedClientId) {
        if (activeClient?.id !== requestedClientId) {
          try {
            const clientDocRef = doc(db, 'clients', requestedClientId);
            const clientSnap = await getDoc(clientDocRef);

            if (clientSnap.exists()) {
              const clientData = { id: clientSnap.id, ...clientSnap.data() } as Client;
              welcomeMessage = `Hello! I'm your PR assistant for ${clientData.name}. How can I help you with your communications today?`;
            }
          } catch (error) {
            console.error("Error fetching client data:", error);
          }
        } else if (activeClient) {
          welcomeMessage = `Hello! I'm your PR assistant for ${activeClient.name}. How can I help you with your communications today?`;
        }
      }

      await addDoc(messagesRef, {
        content: welcomeMessage,
        sender: 'ai',
        timestamp: serverTimestamp()
      });

      return docRef.id;
    })();

    conversationCreateInFlightRef.current.set(inFlightKey, createPromise);
    try {
      return await createPromise;
    } finally {
      if (conversationCreateInFlightRef.current.get(inFlightKey) === createPromise) {
        conversationCreateInFlightRef.current.delete(inFlightKey);
      }
    }
  }

  async function loadConversation(conversationId: string): Promise<void> {
    // Verify auth state before proceeding
    const isAuthenticated = await verifyAuthState();
    
    if (!isAuthenticated || !currentUser) {
      setLoading(false);
      throw new Error("No authenticated user");
    }
    
    setLoading(true);
    
    try {
      const conversationRef = doc(
        db, 
        'users', 
        currentUser.uid, 
        'conversations', 
        conversationId
      );
      
      const conversationSnapshot = await getDoc(conversationRef);
      
      if (conversationSnapshot.exists()) {
        const rawData = conversationSnapshot.data();
        const conversationData: Conversation = {
          id: conversationSnapshot.id,
          ...rawData,
          clarifications: normalizeClarificationsMap((rawData as any).clarifications),
        };
        
        setCurrentConversation(conversationData);
        
        // If there's a client associated with this conversation, load it
        if (conversationData.clientId) {
          try {
            const clientDocRef = doc(db, 'clients', conversationData.clientId);
            const clientSnap = await getDoc(clientDocRef);
            
            if (clientSnap.exists()) {
              const clientData = { id: clientSnap.id, ...clientSnap.data() } as Client;
              setActiveClient(clientData);
            }
          } catch (clientError) {
            console.error("Error loading client data:", clientError);
          }
        }
      } else {
        throw new Error('Conversation not found');
      }
    } catch (error) {
      console.error("Error loading conversation:", error);
    } finally {
      setLoading(false);
    }
  }

  async function renameConversation(conversationId: string, newTitle: string): Promise<void> {
    if (!currentUser) return;
    
    const conversationRef = doc(
      db, 
      'users', 
      currentUser.uid, 
      'conversations', 
      conversationId
    );
    
    await updateDoc(conversationRef, {
      title: newTitle,
      updatedAt: serverTimestamp()
    });
    
    if (currentConversation && currentConversation.id === conversationId) {
      setCurrentConversation({
        ...currentConversation,
        title: newTitle,
        updatedAt: serverTimestamp()
      });
    }
  }

  async function setClientForConversation(conversationId: string, clientId: string): Promise<void> {
    if (!currentUser) return;
    const desiredClientId = clientId || null;
    desiredConversationClientRef.current[conversationId] = desiredClientId;
    pendingConversationClientRef.current[conversationId] = desiredClientId;
    
    const conversationRef = doc(
      db, 
      'users', 
      currentUser.uid, 
      'conversations', 
      conversationId
    );
    
    // If clientId is empty string, we're switching to general chat
    const updateData = clientId ? { clientId, updatedAt: serverTimestamp() } : { clientId: null, updatedAt: serverTimestamp() };
    
    try {
      await updateDoc(conversationRef, updateData);

      if (desiredConversationClientRef.current[conversationId] !== desiredClientId) {
        const latestDesiredClientId = desiredConversationClientRef.current[conversationId] || null;
        await updateDoc(
          conversationRef,
          latestDesiredClientId
            ? { clientId: latestDesiredClientId, updatedAt: serverTimestamp() }
            : { clientId: null, updatedAt: serverTimestamp() }
        );
        return;
      }
    } catch (error) {
      if (desiredConversationClientRef.current[conversationId] === desiredClientId) {
        delete pendingConversationClientRef.current[conversationId];
      }
      throw error;
    }
    
    if (currentConversation && currentConversation.id === conversationId) {
      setCurrentConversation({
        ...currentConversation,
        clientId: clientId || undefined,
        updatedAt: serverTimestamp()
      });
      
      // If clientId is empty, clear the active client, otherwise fetch and set it
      if (!clientId) {
        setActiveClient(null);
      } else {
        try {
          const clientDocRef = doc(db, 'clients', clientId);
          const clientSnap = await getDoc(clientDocRef);
          
          if (clientSnap.exists()) {
            const clientData = { id: clientSnap.id, ...clientSnap.data() } as Client;
            setActiveClient(clientData);
          }
        } catch (error) {
          console.error("Error fetching client data:", error);
        }
      }
    }

    if (desiredConversationClientRef.current[conversationId] === desiredClientId) {
      delete pendingConversationClientRef.current[conversationId];
    }
  }
  
  // PHASE-03 START
  async function setConversationLanguage(conversationId: string, language: 'en' | 'ar'): Promise<void> {
    if (!currentUser) return;

    try {
      const conversationRef = doc(
        db,
        'users',
        currentUser.uid,
        'conversations',
        conversationId
      );

      await updateDoc(conversationRef, {
        language,
        updatedAt: serverTimestamp()
      });

      setConversations((prev) => prev.map((conv) =>
        conv.id === conversationId
          ? { ...conv, language }
          : conv
      ));

      if (currentConversation && currentConversation.id === conversationId) {
        setCurrentConversation({
          ...currentConversation,
          language,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating conversation language:', error);
      throw error;
    }
  }
  // PHASE-03 END
  
  function getCurrentClientId(): string | undefined {
    return currentConversation?.clientId;
  }

  async function clearCurrentChat(): Promise<void> {
    if (!currentUser || !currentConversation) return;
    
    try {
      setLoading(true);
      // Create a new empty conversation
      const newConversationId = await createNewConversation();
      
      // Load the new conversation
      await loadConversation(newConversationId);
    } catch (error) {
      console.error("Error clearing current chat:", error);
    } finally {
      setLoading(false);
    }
  }
  
  async function handleMessageFeedback(messageId: string, feedback: InteractionFeedback): Promise<void> {
    if (!currentUser || !currentConversation) return;

    try {
      const message = messages.find(m => m.id === messageId);
      if (!message) return;
      const legacyLearningMode = getLegacyRealtimeLearningMode();
      const allowLegacyMutation = canMutateLegacyLearning(legacyLearningMode);
      const allowLegacyLogging = canLogLegacyLearning(legacyLearningMode);

      let learningResult: {
        adaptations: unknown[];
        suggestions: unknown[];
        confidenceBoost: number;
      } | null = null;

      if (allowLegacyMutation && message.agentMetadata) {
        // `full` mode only: legacy runtime is allowed to mutate behavior.
        realTimeLearningService.setCurrentUser(currentUser.uid);
        await realTimeLearningService.loadSession(currentConversation.id, currentUser.uid);

        const result = await realTimeLearningService.processFeedback(
          currentConversation.id,
          messageId,
          feedback,
          message.agentMetadata.agentType as any,
          message.content,
          activeClient || undefined,
          currentUser.uid
        );
        learningResult = {
          adaptations: result.adaptations,
          suggestions: result.suggestions,
          confidenceBoost: result.confidenceBoost,
        };

        const updatedMessages = messages.map(m => {
          if (m.id === messageId && m.agentMetadata) {
            return {
              ...m,
              agentMetadata: {
                ...m.agentMetadata,
                realTimeLearning: {
                  adaptedFromFeedback: true,
                  sessionCorrections: result.suggestions.length,
                  confidenceBoost: result.confidenceBoost,
                  lastAdaptation: new Date().toISOString()
                }
              }
            };
          }
          return m;
        });

        setMessages(updatedMessages);
      } else if (!allowLegacyMutation) {
        console.info('[legacy-learning] behavior_write_blocked', {
          mode: legacyLearningMode,
          conversationId: currentConversation.id,
          messageId,
        });
      }

      if (allowLegacyLogging && currentUser && messageId) {
        const feedbackRef = collection(
          db, 
          'users', 
          currentUser.uid, 
          'conversations', 
          currentConversation.id, 
          'feedback'
        );
        
        await addDoc(feedbackRef, {
          messageId,
          feedback,
          agentType: message.agentMetadata?.agentType || null,
          timestamp: serverTimestamp(),
          legacyLearningMode,
          legacyBehaviorChangeApplied: !!learningResult,
          learningResult: learningResult
            ? {
                adaptations: learningResult.adaptations,
                suggestions: learningResult.suggestions,
                confidenceBoost: learningResult.confidenceBoost
              }
            : null,
        });
      }

      if (learningResult) {
        console.log('Real-time learning processed:', learningResult);
      }
    } catch (error) {
      console.error('Error processing message feedback:', error);
    }
  }

  function getSessionLearningStats() {
    if (!currentConversation) return null;
    
    return realTimeLearningService.getSessionStats(currentConversation.id);
  }

  async function deleteConversation(conversationId: string): Promise<void> {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setIsCleaningUp(true); // CRITICAL FIX: Prevent auto-creation during individual deletion
      console.log('🗑️ Starting individual conversation deletion:', conversationId);
      
      // Clear real-time learning session when deleting conversation
      if (currentUser) {
        await realTimeLearningService.deactivateSession(conversationId, currentUser.uid);
      }
      realTimeLearningService.clearSession(conversationId);
      
      const conversationRef = doc(
        db, 
        'users', 
        currentUser.uid, 
        'conversations', 
        conversationId
      );
      
      // Delete all messages in the conversation
      const messagesRef = collection(
        db, 
        'users', 
        currentUser.uid, 
        'conversations', 
        conversationId, 
        'messages'
      );
      
      const messagesSnapshot = await getDocs(messagesRef);
      
      // Create a batch to delete all messages
      const batch = writeBatch(db); // Changed to writeBatch(db)
      messagesSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      // Add the conversation document deletion to the batch
      batch.delete(conversationRef);
      
      // Execute the batch
      await batch.commit();
      
      // If the deleted conversation was the current one, do not auto-create a new one.
      // Let the UI show the empty state or user select another conversation.
      if (currentConversation && currentConversation.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
      
      // Update the conversations list - this should happen automatically via the onSnapshot listener
    } catch (error) {
      console.error("Error deleting conversation:", error);
      throw new Error(`Failed to delete conversation: ${error}`);
    } finally {
      setLoading(false);
      setIsCleaningUp(false); // CRITICAL FIX: Reset cleanup flag after individual deletion
      console.log('🗑️ Individual conversation deletion completed');
    }
  }

  // Create artifact reference message
  const createArtifactReferenceMessage = useCallback((artifactData: any): Message => {
    const rawMeta = artifactData?.meta && typeof artifactData.meta === 'object' ? artifactData.meta : null;
    const meta = {
      ...(rawMeta || {}),
      ...(normalizeString(artifactData?.runId) ? { runId: normalizeString(artifactData.runId) } : {}),
      ...(normalizeString(artifactData?.checkpointId) ? { checkpointId: normalizeString(artifactData.checkpointId) } : {}),
      ...(normalizeString(artifactData?.artifactKey) ? { artifactKey: normalizeString(artifactData.artifactKey) } : {}),
      ...(normalizeString(artifactData?.lineageId) ? { lineageId: normalizeString(artifactData.lineageId) } : {}),
    };
    return {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: '', // Empty - handled by UI
      sender: 'ai' as const,
      timestamp: new Date(),
      isArtifactReference: true,
      artifactReference: {
        artifactId: artifactData.id,
        artifactType: artifactData.type,
        artifactTitle: sanitizeArtifactTitle(artifactData.title, 'Artifact'),
        createdAt: artifactData.timestamp
      },
      // Store the full artifact content to guarantee follow-up edits have context
      // Truncate to a safe maximum to keep message size reasonable
      ...(artifactData.content
        ? { artifactContent: String(artifactData.content).slice(0, 10000) }
        : {}),
      agentMetadata: artifactData.agentMetadata || {
        agentId: 'artifact-creator',
        agentType: artifactData.agentType || 'ai',
        confidence: 1.0,
        requiresApproval: false,
        tokensUsed: 0,
        responseTime: 0
      },
      // PHASE-02 START
      ...(Object.keys(meta).length ? { meta } : {}),
      // PHASE-02 END
    };
  }, []);

  // Replace message with new message
  const replaceMessage = useCallback(async (messageId: string, newMessage: Message): Promise<void> => {
    if (!currentUser || !currentConversation) {
      throw new Error("No authenticated user or conversation");
    }

    try {
      // Update local state immediately for better UX
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? newMessage : msg
      ));

      // Persist in Firestore so reloads don't reprocess the same AI message into new artifacts
      if (messageId) {
        const messageRef = doc(
          db,
          'users',
          currentUser.uid,
          'conversations',
          currentConversation.id,
          'messages',
          messageId
        );

        try {
          await setDoc(messageRef, {
            ...newMessage,
            timestamp: serverTimestamp()
          }, { merge: true });
        } catch (error) {
          // If message doesn't exist in Firestore, create it
          const messagesRef = collection(
            db,
            'users',
            currentUser.uid,
            'conversations',
            currentConversation.id,
            'messages'
          );
          
          await addDoc(messagesRef, {
            ...newMessage,
            timestamp: serverTimestamp()
          });
        }
      }
    } catch (error) {
      console.error("Error replacing message:", error);
      // Revert local state on error
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg } : msg
      ));
      throw error;
    }
  }, [currentUser, currentConversation]);

  const appendAssistantMessage = useCallback(async (payload: {
    content: string;
    messageKind?: Message['messageKind'];
    meta?: Message['meta'];
  }): Promise<string | null> => {
    if (!currentUser || !currentConversation?.id) return null;
    try {
      const messagesRef = collection(
        db,
        'users',
        currentUser.uid,
        'conversations',
        currentConversation.id,
        'messages'
      );
      const message: Message = {
        content: payload.content,
        sender: 'ai',
        timestamp: serverTimestamp(),
        ...(payload.messageKind ? { messageKind: payload.messageKind } : {}),
        ...(payload.meta ? { meta: payload.meta } : {}),
      };
      const docRef = await addDoc(messagesRef, message as any);
      return docRef.id;
    } catch (error) {
      console.error('[ChatContext] append_assistant_message_failed', error);
      return null;
    }
  }, [currentConversation?.id, currentUser]);

  const messagesWithPending = useMemo(() => {
    const pending = Object.values(pendingRunMessages);
    if (!pending.length) return messages;
    const merged = [...messages, ...pending];
    merged.sort((a, b) => toMillis((a as any)?.timestamp) - toMillis((b as any)?.timestamp));
    return merged;
  }, [messages, pendingRunMessages]);

  const value = {
    conversations,
    currentConversation,
    messages: messagesWithPending,
    loading,
    isCleaningUp,
    setIsCleaningUp,
    sendMessage,
    editMessage,
    createNewConversation,
    loadConversation,
    renameConversation,
    setClientForConversation,
    setConversationLanguage,
    getCurrentClientId,
    clearCurrentChat,
    deleteConversation,
    handleMessageFeedback,
    getSessionLearningStats,
    createArtifactReferenceMessage,
    replaceMessage,
    appendAssistantMessage,
    // In-Chat Learning
    learningSuggestion,
    handleConfirmLearning,
    handleDismissLearning,
    clarifyPending,
    clarifyQueue: clarifyRequests,
    storedClarifications,
    persistClarificationEntry,
    clearClarifications,
    reviewerFeedback,
    clientPreferences,
    preferencesLoading,
    updateClientPreferences
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
